//Job Queue UI/

import React, { useState, useEffect, useRef, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import config from '../config.js';
import pinPrompt from '../utils/pinPrompt';
import useFacePin from '../face/useFacePin';
import FaceSignInStrip from '../face/FaceSignInStrip';
import { getStationKey } from '../face/faceStation';
import openPdfInPrintDialog from '../features/queue/printPdf';
// The task actions (Start ... Complete, part defect) are shared with the phone queue: ONE copy
// in features/queue/taskActions.js; this page passes the desktop's own dialogs (desktopUi).
import { parseServerError, errorTextToMessage, startTask, pauseTask, finishOp, enterMaintenance,
  exitMaintenance, completeTask, autoCompleteTask, printTaskReport, recordPartDefect } from '../features/queue/taskActions';
import { desktopUi } from '../features/queue/desktopUi';
// The reads (queue, job detail, parts, cut sheet, activity, defects) are shared the same way.
import { QUEUE_STATIONS, fetchQueueRows, stationBlocker, uniqueTasks, findInQueue, loadJobDetail, loadJobParts,
  loadCutSheet, loadTaskActivity, loadJobDefects, loadLaserConsumables } from '../features/queue/queueData';
// ...and the rules for what a station may do next (order lock, 2 PM maintenance block, multi-op).
import { isAwaitingNextOp, isMaintenanceJob, compareStationTasks, nextAllowedId, pendingMaintenanceFor,
  taskButtons, taskGates } from '../features/queue/queueRules';
import { remakeHeadsUp, remakeAttachPicker } from '../components/RemakeDialogs.jsx';
import { CoatingSwatch } from '../components/CoatingColor';
import { PartDetailModal, ProductIdLink } from '../components/PartDetailModal.jsx';
import { assemblyCountFromPrimarySpace, queueRowAssemblyQty, isAtOrAfterJoin, isAssemblyRow, isAssemblySetRow, assembliesOnTask, taskPiecesForComponent } from '../utils/assemblyMath';
import { apiErrorMessage, detailToString } from '../utils/apiError';
import { parseDueDate, formatDueDate, isDueDateLate } from '../utils/dueDate';
import { isConsumableDefect, recentConsumableDefects, describeRecentDefects, consumableJobQty, parseDefectQty, buildConsumableDefectPayload, describeConsumableDefectResult } from '../utils/consumableDefects';
import { parseQueueSearchNumber, rowMatchesNumber, mergeLookupIntoQueue, taskQueueKey, laserQueueKey } from '../utils/queueSearch';
import { parseScanCode, scanPath, queueDeepLink } from '../utils/scanCodes';
import { defaultRemnantTrackingNumber, remnantLotLabel, remnantOfText, describeTagContext, isConfirmableSheetRefusal, noTrackingNeedsManager, noTrackingOverride, noTrackingRefusal } from '../utils/remnantTracking';
import { dockedBeacons, dockedLots, dockedPrefill } from '../utils/beacons';
import { DOCK_POLL_MS, DOCK_SLOW_POLL_MS, DOCK_STALE_MS, DOCK_IDLE_MS, dockEnforced, dockAdvice, dockAllowsSave, dockLotToSend, dockRefusal, dockOverrideFields, dockBlockedLabel } from '../features/queue/laserDock';
import { trkText } from '../utils/beacons';
import useLaserDockCheck from '../features/queue/useLaserDockCheck';
import LaserDockCard from '../components/LaserDockCard';
import { cameraRefusal, cameraOverrideFields, cameraOverrideFailure, cameraRecordedSheet, CAMERA_POLL_MS, CAMERA_OVERRIDE_REASON_MIN } from '../features/queue/laserCamera';
import useLaserCamera from '../features/queue/useLaserCamera';
import LaserCameraLine from '../components/LaserCameraLine';
import { useFeatures } from '../utils/features';
import RemnantLabelConfirmModal, { loadPendingLabelConfirmations } from '../components/RemnantLabelConfirmModal.jsx';
import FindMaterialButton from '../components/FindMaterialButton.jsx';
import TaskPalletsButton from '../components/TaskPalletsButton.jsx';
import TaskPalletsPanel from '../components/TaskPalletsPanel.jsx';
import { confirmationTitle } from '../utils/labelConfirm';
// T&M (Commit C): a defect on a T&M job asks the neutral cause (required, decision 15).
import causePrompt from '../utils/causePrompt';
import { queueRowSaysTm, withCause } from '../utils/tmDefects';
import { isTmLinked } from '../utils/tmPricing';

// Adjust to your environment:
const API_BASE = config.API_BASE_URL

/**
 * promptForUserPin - globally accessible so both DetailView and LaserDetailSection can use it.
 */
async function promptForUserPin() {
  return await pinPrompt("Enter your 4-digit PIN:");
}

/**
 * formatInterval - utility for formatting "HH:MM:SS" if needed.
 * We mostly rely on raw seconds from the server.
 */
function formatInterval(intervalStr) {
  if (!intervalStr) return "--:--"
  const parts = intervalStr.split(":")
  const h = parseInt(parts[0], 10) || 0
  const m = parseInt(parts[1], 10) || 0
  const s = parseInt(parts[2], 10) || 0
  return `${h}h ${m}m ${s}s`
}

function toLocalInputValue(utcDate) {
  const year = utcDate.getFullYear()
  const month = String(utcDate.getMonth() + 1).padStart(2, "0")
  const day = String(utcDate.getDate()).padStart(2, "0")
  const hours = String(utcDate.getHours()).padStart(2, "0")
  const mins = String(utcDate.getMinutes()).padStart(2, "0")
  return `${year}-${month}-${day}T${hours}:${mins}`
}

function toCentralTime12h(utcDate) {
  return utcDate.toLocaleString("en-US", {
    timeZone: "America/Chicago",
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}

function parseCentralTime12hToUtc(str) {
  const match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!match) return null
  let [, month, day, year, hour, minute, ampm] = match
  hour = parseInt(hour, 10)
  if (ampm.toUpperCase() === "PM" && hour !== 12) hour += 12
  if (ampm.toUpperCase() === "AM" && hour === 12) hour = 0
  const pad = (n) => String(n).padStart(2, "0")
  const centralStr = `${year}-${pad(parseInt(month, 10))}-${pad(parseInt(day, 10))}T${pad(hour)}:${minute}:00`
  const utcOffset = getChicagoUtcOffset(new Date(`${centralStr}Z`))
  const utcMs = new Date(`${centralStr}Z`).getTime() - utcOffset * 60000
  return new Date(utcMs)
}

function getChicagoUtcOffset(approxDate) {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    timeZoneName: "shortOffset",
  })
  const parts = fmt.formatToParts(approxDate)
  const tzPart = parts.find((p) => p.type === "timeZoneName")
  if (!tzPart) return -360
  const m = tzPart.value.match(/GMT([+-]?\d+)/)
  return m ? parseInt(m[1], 10) * 60 : -360
}

/**
 * formatSecondsToHhMm - utility for formatting seconds into "Hh Mm"
 */
function formatSecondsToHhMm(seconds) {
  if (seconds === null || isNaN(seconds)) {
    return "0h 0m";
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

/**
 * Replace ISO timestamps found in text with a formatted local version.
 */
function formatNoteTimestamps(noteText) {
  if (!noteText) return ""
  const isoRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|\+\d{2}:?\d{2})?/g
  return noteText.replace(isoRegex, (match) => {
    const dateObj = new Date(match)
    if (isNaN(dateObj.getTime())) {
      return match
    }
    return dateObj.toLocaleString("en-US", {
      timeZone: "America/Chicago",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
  })
}

/**
 * A custom React hook for setting up a declarative interval.
 * @param {Function} callback - The function to execute on each interval.
 * @param {number | null} delay - The interval delay in milliseconds. Null to pause.
 */
function useInterval(callback, delay) {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

function formatTimestampToCST(utcString) {
  if (!utcString) return ""
  const dateObj = new Date(utcString)
  return dateObj.toLocaleString("en-US", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
}

/**
 * PunchElapsedTimer: Displays a running HHh MMm SSs timer.
 * Expects an initial number of seconds (dbElapsedSeconds) plus the status.
 * If status is "In Progress," it increments locally by 1 second each second.
 */
function PunchElapsedTimer({ dbElapsedSeconds, status }) {
  const [localSeconds, setLocalSeconds] = useState(dbElapsedSeconds)

  useEffect(() => {
    setLocalSeconds(dbElapsedSeconds)
  }, [dbElapsedSeconds])

  useEffect(() => {
      // UPDATED: Also run the timer during Maintenance
      if (status !== "In Progress" && status !== "Maintenance") return
      const intervalId = setInterval(() => {
        setLocalSeconds((prev) => prev + 1)
      }, 1000)
      return () => clearInterval(intervalId)
    }, [status, dbElapsedSeconds])

  const h = Math.floor(localSeconds / 3600)
  const m = Math.floor((localSeconds % 3600) / 60)
  const s = Math.floor(localSeconds % 60)

  return <span>{`${h}h ${m}m ${s}s`}</span>
}

// isAwaitingNextOp (a multi-op task parked between ops is READY): features/queue/queueRules.js.

function statusBadge(status) {
    let bg = "#999"
    if (status === "Pending") bg = "#7a7a7a"
    else if (status === "In Progress") bg = "#0275d8"
    else if (status === "Paused") bg = "#f0ad4e"
    else if (status === "Completed") bg = "#5cb85c"
    else if (status === "Maintenance") bg = "#6c757d"

    return (
      <span
        style={{
          display: "inline-block",
          marginLeft: "0.35rem",
          padding: "0.15rem 0.4rem",
          backgroundColor: bg,
          borderRadius: "4px",
          color: "#fff",
          fontSize: "0.75rem",
          fontWeight: "bold",
        }}
        title={status}
      >
        {status}
      </span>
    )
  }

// Define a station rank for sorting logic.
const stationOrder = {
  Laser: 1,
  Tap: 2,
  Brake: 3,
  Fabrication: 4,
  Blast: 5,
  Coating: 5.5,
  Finish: 6,
}

const fabricationOrder = {
  Cutting: 1,
  Drilling: 2,
  Milling: 3,
  Tapping: 4,
  Lathe: 5,
  Welding: 6,
  Assembly: 7,
};

// ==============================================================================
// PPAP Measurement Components and Utilities
// ==============================================================================

/**
 * calculateLimits - utility to calculate USL/LSL. Copied from PartEntryPage.jsx.
 * @param {number} nominal 
 * @param {string} tolerance - e.g., "±0.005" or "+0.005/-0.002"
 * @returns {object} {lsl: number, usl: number}
 */
const calculateLimits = (nominal, tolerance) => {
    const value = parseFloat(nominal);
    if (nominal == null || isNaN(value) || !tolerance) {
        return { lsl: null, usl: null };
    }

    const cleanedTolerance = tolerance.replace(/ /g, '');
    let plusTol = 0;
    let minusTol = 0;

    if (cleanedTolerance.includes('±')) {
        const tol = cleanedTolerance.replace(/[^0-9.]/g, '');
        plusTol = minusTol = parseFloat(tol);
    } else if (cleanedTolerance.includes('/')) {
        const parts = cleanedTolerance.split('/');
        const plusStr = parts[0].replace(/[^0-9.+-]/g, '');
        const minusStr = parts[1].replace(/[^0-9.+-]/g, '');
        plusTol = Math.abs(parseFloat(plusStr));
        minusTol = Math.abs(parseFloat(minusStr));
    } else if (!isNaN(parseFloat(cleanedTolerance)) && cleanedTolerance.length > 0) {
        const tol = parseFloat(cleanedTolerance);
        plusTol = minusTol = Math.abs(tol);
    }

    if (isNaN(plusTol) || plusTol < 0) plusTol = 0;
    if (isNaN(minusTol) || minusTol < 0) minusTol = 0;
    
    return {
        usl: parseFloat((value + plusTol).toFixed(4)),
        lsl: parseFloat((value - minusTol).toFixed(4))
    };
};

const formatLimit = (value, nominal) => {
    if (value == null) return '';
    const nominalStr = String(nominal ?? '').trim();
    const dotIdx = nominalStr.indexOf('.');
    const nomDecimals = dotIdx === -1 ? 0 : nominalStr.length - dotIdx - 1;
    const trimmed = value.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
    const tDot = trimmed.indexOf('.');
    const trimmedDecimals = tDot === -1 ? 0 : trimmed.length - tDot - 1;
    return value.toFixed(Math.max(nomDecimals, trimmedDecimals));
};

// A face token lives 10 minutes on the API; the Add Sheet and PPAP popups can sit open while a
// pallet is found or the parts are measured.
const FACE_TOKEN_USE_WITHIN_MS = 9 * 60 * 1000;

/**
 * PpapMeasurementModal: Modal for recording ongoing sample measurements.
 * UPDATED: Allows saving ONE sample at a time.
 */
export function PpapMeasurementModal({
  isOpen,
  onClose,
  jobId,
  taskId,
  partData,
  ongoingSampleSize,
  initialSampleSize = 1,
  samplePhase = 'ongoing',  // 'initial' for FAI, 'ongoing' for in-process sampling
  ppapImageUrl,
  reloadJob
}) {
    const isInitialPhase = samplePhase === 'initial';
    const sampleSize = isInitialPhase ? initialSampleSize : ongoingSampleSize;

    // Measurements state: { sample_number: { characteristic_name: measured_value } }
    const [measurements, setMeasurements] = useState({});
    const [pin, setPin] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [existingSamples, setExistingSamples] = useState([]);

    // Gauge State
    const [gauges, setGauges] = useState([]);
    const [selectedGaugeId, setSelectedGaugeId] = useState("");

    // Face sign-in (src/face, as the laser Add Sheet popup): on a live station the camera names the
    // operator while the parts are measured, and the face token stands in for the PIN on each Save
    // (POST /ppap/samples only names who measured: the API takes a token like a typed PIN). Typing a
    // PIN, or "Not you?", ends it for this popup. Hooks stay above the early return below.
    const pinRef = useRef(null);
    const face = useFacePin(Boolean(isOpen) && Boolean(getStationKey()));
    const faceMatch = face.phase === "matched" ? face.match : null;
    const faceMatchedAtRef = useRef(0);
    useEffect(() => { if (faceMatch) faceMatchedAtRef.current = Date.now(); }, [faceMatch]);

    const characteristicList = partData.ppap_critical_characteristics || [];
    // Sample numbers start from 1 up to the active phase's sample size
    const sampleNumbers = Array.from({ length: sampleSize }, (_, i) => i + 1);

    // Fetch existing samples AND Gauges on mount
    useEffect(() => {
      async function fetchData() {
        try {
          // 1. Fetch Samples — for FAI, only show the latest attempt; for ongoing, show all
          const samplesRes = await fetch(`${API_BASE}/ppap/samples/${jobId}/${partData.part_id}`);
          if (samplesRes.ok) {
            const allData = await samplesRes.json();
            const phaseData = allData.filter(s => (s.sample_phase || 'ongoing') === samplePhase);
            // For initial phase, scope to the latest attempt only.
            // If the latest attempt is COMPLETE and FAILED, clear existingSamples
            // so the modal renders editable inputs for a fresh retry attempt
            // (the next save will land in attempt N+1 server-side).
            let data = phaseData;
            if (isInitialPhase && phaseData.length > 0) {
              const latestAttempt = Math.max(...phaseData.map(s => s.attempt_number || 1));
              data = phaseData.filter(s => (s.attempt_number || 1) === latestAttempt);
              const latestFailed = data.length >= sampleSize && data.some(s => !s.all_passed);
              if (latestFailed) {
                data = []; // operator is starting attempt N+1 — show fresh inputs
              }
            }
            setExistingSamples(data);

            // PRE-FILL MEASUREMENTS STATE WITH EXISTING DATA
            const prefill = {};
            data.forEach(sample => {
                if (!prefill[sample.sample_number]) prefill[sample.sample_number] = {};
                sample.measurements.forEach(m => {
                    prefill[sample.sample_number][m.characteristic_name] = m.measured_value;
                });
            });
            setMeasurements(prefill);
          }
          
          // 2. Fetch Active Gauges
          const gaugesRes = await fetch(`${API_BASE}/gauges?active_only=true`);
          if (gaugesRes.ok) {
            const gaugesData = await gaugesRes.json();
            setGauges(gaugesData);
          }
        } catch (err) {
          console.error("Failed to fetch PPAP data:", err);
        }
      }
      if (isOpen) fetchData();
    }, [isOpen, jobId, partData.part_id, samplePhase, isInitialPhase]);

    // --- Calibration Check Logic ---
    const selectedGauge = gauges.find(g => g.gauge_id === parseInt(selectedGaugeId, 10));
    const isCalibrationOverdue = useMemo(() => {
        if (!selectedGauge || !selectedGauge.next_calibration_date) return false;
        const todayStr = new Date().toISOString().split('T')[0];
        return selectedGauge.next_calibration_date < todayStr;
    }, [selectedGauge]);

    // Check if a sample is already recorded in the DB
    const isSampleSaved = (sampleNum) => {
        return existingSamples.some(s => s.sample_number === sampleNum);
    };

    const handleMeasurementChange = (sampleNum, charName, value) => {
        // Utility to safely parse float
        function safeParseFloat(val, defaultValue = null) {
          if (val === "" || val === null || val === undefined) return defaultValue;
          const parsed = parseFloat(val);
          return isNaN(parsed) ? defaultValue : parsed;
        }

        const measured = safeParseFloat(value, null);

        setMeasurements(prev => ({
            ...prev,
            [sampleNum]: {
                ...(prev[sampleNum] || {}),
                [charName]: measured
            }
        }));
    };
    
    function safeParseFloatSubmit(value, defaultValue = 0) {
      if (value === "" || value === null || value === undefined) return defaultValue;
      const parsed = parseFloat(value);
      return isNaN(parsed) ? defaultValue : parsed;
    }

    // --- SAVE SINGLE SAMPLE LOGIC ---
    const handleSaveSingleSample = async (sampleNum) => {
        setError(null);
        // A face match stands in for the PIN; a typed PIN always wins.
        let operatorPin = pin;
        if (!operatorPin && faceMatch) {
            if (Date.now() - faceMatchedAtRef.current > FACE_TOKEN_USE_WITHIN_MS) {
                face.retry();
                alert("Face sign-in timed out. Look at the camera again, or type your PIN.");
                return;
            }
            operatorPin = faceMatch.token;
        }
        if (!operatorPin) { alert("Operator PIN is required."); return; }
        if (!selectedGaugeId) { alert("Please select the Measurement Tool used."); return; }
        
        // Check calibration
        if (isCalibrationOverdue) {
            if (!window.confirm("⚠️ WARNING: The selected tool is past its calibration date. Continue anyway?")) {
                return;
            }
        }

        setIsSubmitting(true);

        try {
            // Validate measurements for THIS sample only
            const sampleMeasurements = [];
            let isComplete = true;

            for (let char of characteristicList) {
                const measuredValue = measurements[sampleNum]?.[char.name];
                
                if (measuredValue == null) { isComplete = false; break; }
                
                const limits = calculateLimits(char.nominal, char.tolerance);
                
                sampleMeasurements.push({
                    characteristic_name: char.name,
                    nominal_value: safeParseFloatSubmit(char.nominal),
                    usl: limits.usl,
                    lsl: limits.lsl,
                    measured_value: measuredValue
                });
            }

            if (!isComplete) {
                alert(`Sample #${sampleNum} is incomplete. Please enter all measurements for this column.`);
                setIsSubmitting(false);
                return;
            }

            // Prepare Payload
            const payload = {
                job_id: jobId,
                task_id: taskId,
                part_id: partData.part_id,
                sample_number: sampleNum,
                measurements: sampleMeasurements,
                pin: operatorPin,
                gauge_id: parseInt(selectedGaugeId, 10),
                sample_phase: samplePhase,
            };

            // Submit
            const res = await fetch(`${API_BASE}/ppap/samples`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                // The API refused the face token (expired, revoked, PIN changed): bring the PIN box
                // back so the operator can type it, without "Not you?" (that reports a wrong match).
                if (res.status === 401 && operatorPin !== pin) face.cancel();
                throw new Error(await apiErrorMessage(res, `Failed to record sample #${sampleNum}`));
            }

            const data = await res.json();

            if (!data.all_passed) {
                if (isInitialPhase) {
                    // FAI failure: task is still Pending, no auto-pause.
                    // Show the server-provided message (which includes attempt # and retry/lock guidance).
                    alert(`❌ ${data.message || 'FAI MEASUREMENT FAILED'}`);
                    reloadJob();
                    onClose();
                    return;
                } else {
                    // Ongoing failure: task auto-paused, QC notified (legacy behavior).
                    alert("❌ MEASUREMENT FAILED \n\nJob has been automatically PAUSED and QC notified.\nAdmin PIN required to resume.");
                    reloadJob();
                    onClose();
                    return;
                }
            }

            // Success - Refresh local data to lock the column.
            // Filter the refetch to the active phase + (initial only) latest attempt.
            const samplesRes = await fetch(`${API_BASE}/ppap/samples/${jobId}/${partData.part_id}`);
            if (samplesRes.ok) {
                const allUpdated = await samplesRes.json();
                const phaseUpdated = allUpdated.filter(s => (s.sample_phase || 'ongoing') === samplePhase);
                let scoped = phaseUpdated;
                if (isInitialPhase && phaseUpdated.length > 0) {
                    const latestAttempt = Math.max(...phaseUpdated.map(s => s.attempt_number || 1));
                    scoped = phaseUpdated.filter(s => (s.attempt_number || 1) === latestAttempt);
                }
                setExistingSamples(scoped);
            }

            // Trigger background refresh of main job board
            reloadJob();
            alert(`✅ Sample #${sampleNum} Saved!`);
            // For FAI: if this was the last required sample and all passed, auto-close so the FAI gate refresh can fire.
            if (isInitialPhase && sampleNum >= sampleSize) {
                setTimeout(() => onClose(), 300);
            }

        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 10000 }}>
            <div style={{ backgroundColor: "var(--jq-bg-card, #fff)", padding: "20px", borderRadius: "8px", width: "95%", maxWidth: "1100px", maxHeight: "90%", overflowY: "auto", boxShadow: "0 4px 8px rgba(0,0,0,0.2)" }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">
                        {isInitialPhase ? '📐 First Article Inspection' : 'PPAP Ongoing Sample'} — {partData.product_id}
                    </h4>
                    <div className="d-flex align-items-center gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={async () => {
                                try {
                                    const res = await fetch(`${API_BASE}/tasks/${taskId}/ppap_measurement_report`);
                                    if (!res.ok) {
                                        throw new Error(await apiErrorMessage(res));
                                    }
                                    const blob = await res.blob();
                                    const url = URL.createObjectURL(blob);
                                    window.open(url, '_blank');
                                    // Don't revoke immediately — new tab needs the URL alive
                                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                                } catch (err) {
                                    alert(`Could not generate PPAP report: ${err.message}`);
                                }
                            }}
                            title="Open PDF report with balloon print, characteristics, and recorded measurements"
                        >
                            <i className="bi bi-printer me-1"></i>Print Report
                        </button>
                        <button onClick={onClose} className="btn-close"></button>
                    </div>
                </div>
                {isInitialPhase && (
                    <div className="alert alert-info py-2 px-3 mb-3 small">
                        <i className="bi bi-info-circle me-1"></i>
                        Cut <strong>{sampleSize}</strong> part(s) on the laser (timer off), measure each
                        critical characteristic, then save below. All samples must pass before Start
                        is enabled.
                    </div>
                )}

                {error && <div className="alert alert-danger">{error}</div>}
                
                {ppapImageUrl && (
                    <div className="mb-3 text-center border p-2">
                        <img 
                          src={ppapImageUrl} 
                          alt="Balloon Print" 
                          style={{ maxHeight: '150px', maxWidth: '100%', objectFit: 'contain', cursor: 'pointer' }} 
                          onClick={() => window.open(ppapImageUrl, '_blank')}
                        />
                        <div className="small text-muted mt-1">Balloon Print Image (Click to Enlarge)</div>
                    </div>
                )}

                {/* Face sign-in standing in for the Operator PIN below */}
                <FaceSignInStrip
                    state={face}
                    onNotMe={() => { face.notMe(); setTimeout(() => pinRef.current && pinRef.current.focus(), 0); }}
                    onTryAgain={face.retry}
                />

                {/* Gauge Selection & PIN (Moved to Top for visibility) */}
                <div className="d-flex justify-content-between align-items-end mb-3 p-3 bg-light rounded border">
                    <div className="d-flex gap-3">
                        <div>
                            <label className="form-label mb-1 small fw-bold">Measurement Tool:</label>
                            <select 
                                className={`form-select form-select-sm ${isCalibrationOverdue ? 'border-danger text-danger' : ''}`}
                                style={{ minWidth: '200px' }}
                                value={selectedGaugeId}
                                onChange={(e) => setSelectedGaugeId(e.target.value)}
                            >
                                <option value="">-- Select Gauge --</option>
                                {gauges.map(g => (
                                    <option key={g.gauge_id} value={g.gauge_id}>
                                        {g.gauge_name} ({g.gauge_type})
                                    </option>
                                ))}
                            </select>
                            {isCalibrationOverdue && (
                                <small className="text-danger fw-bold d-block mt-1">
                                    <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                    Cal. Expired: {selectedGauge.next_calibration_date}
                                </small>
                            )}
                        </div>
                        {!faceMatch && (
                        <div>
                            <label className="form-label mb-1 small fw-bold">Operator PIN:</label>
                            <input
                                ref={pinRef}
                                type="password"
                                value={pin}
                                onChange={(e) => {
                                    setPin(e.target.value);
                                    // Typing a PIN ends the face attempt (an answer still on its way is ignored).
                                    if (e.target.value && (face.phase === "checking" || face.phase === "looking")) face.cancel();
                                }}
                                className="form-control form-control-sm"
                                style={{ width: '120px' }} 
                                autoComplete="off" 
                                placeholder="Enter PIN"
                            />
                        </div>
                        )}
                    </div>
                    <div className="text-muted small text-end">
                        Enter measurements below. Click the 💾 icon to save a specific sample.
                    </div>
                </div>

                <div style={{ overflowX: "auto" }}>
                    <table className="table table-sm table-bordered" style={{ minWidth: "800px", fontSize: '0.9rem' }}>
                        <thead>
                            <tr className="table-secondary align-middle text-center">
                                <th style={{ position: "sticky", left: 0, zIndex: 2, textAlign: 'left' }}>Char. Name</th>
                                <th>Nominal</th>
                                <th>Tol.</th>
                                <th className="text-success">USL</th>
                                <th className="text-danger">LSL</th>
                                {sampleNumbers.map(n => {
                                    const saved = isSampleSaved(n);
                                    return (
                                        <th key={n} style={{ minWidth: '100px', backgroundColor: saved ? 'var(--jq-bg-subtle, #e2e3e5)' : 'var(--jq-bg-card, #fff)' }}>
                                            <div>Sample #{n}</div>
                                            {!saved ? (
                                                <button 
                                                    className="btn btn-sm btn-success py-0 px-2 mt-1"
                                                    style={{fontSize: '0.8rem'}}
                                                    onClick={() => handleSaveSingleSample(n)}
                                                    disabled={isSubmitting}
                                                    title="Save this sample"
                                                >
                                                    💾 Save
                                                </button>
                                            ) : (
                                                <span className="badge bg-secondary mt-1">Saved</span>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {characteristicList.map((char, index) => {
                                const limits = calculateLimits(char.nominal, char.tolerance);
                                return (
                                    <tr key={char.name || index}>
                                        <td style={{ position: "sticky", left: 0, backgroundColor: "var(--jq-bg-card, #fff)", zIndex: 1, fontWeight: 'bold' }}>
                                            {char.name || `Callout #${char.balloon}`}
                                        </td>
                                        <td className="text-center">{char.nominal}</td>
                                        <td className="text-center">{char.tolerance}</td>
                                        <td className="text-center text-success fw-bold">{formatLimit(limits.usl, char.nominal)}</td>
                                        <td className="text-center text-danger fw-bold">{formatLimit(limits.lsl, char.nominal)}</td>
                                        
                                        {sampleNumbers.map(n => {
                                            const saved = isSampleSaved(n);
                                            const measured = measurements[n]?.[char.name];
                                            
                                            // Determine cell color based on Pass/Fail
                                            let bgColor = 'transparent';
                                            if (measured != null) {
                                                const isPass = measured >= limits.lsl && measured <= limits.usl;
                                                bgColor = isPass ? '#d4edda' : '#f8d7da'; // Green or Red tint
                                            }
                                            if (saved) bgColor = '#e9ecef'; // Gray out if saved (override colors slightly)

                                            return (
                                                <td key={n} style={{ backgroundColor: bgColor, padding: '2px' }}>
                                                    <input 
                                                        type="number" 
                                                        step="0.0001"
                                                        className="form-control form-control-sm text-center" 
                                                        value={measured != null ? measured : ''}
                                                        onChange={(e) => handleMeasurementChange(n, char.name, e.target.value)}
                                                        disabled={saved} // Lock input if saved
                                                        style={{ 
                                                            width: '100%', 
                                                            border: 'none', 
                                                            background: 'transparent',
                                                            fontWeight: saved ? 'bold' : 'normal'
                                                        }}
                                                        placeholder={saved ? '-' : '0.0000'}
                                                    />
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                
                <div className="mt-4 d-flex justify-content-end">
                    <button onClick={onClose} className="btn btn-secondary">
                        Close / Finish Later
                    </button>
                </div>
            </div>
        </div>
    );
}
/**
 * FaiAdminOverrideModal: Unlocks the FAI gate after a second-attempt failure.
 * Requires Admin PIN + a reason note (audit trail).
 */
function FaiAdminOverrideModal({ isOpen, taskId, onClose, onSuccess }) {
    const [adminPin, setAdminPin] = useState("");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        setError(null);
        if (!adminPin) { setError("Admin PIN required."); return; }
        if (reason.trim().length < 3) { setError("Reason must be at least 3 characters."); return; }
        setSubmitting(true);
        try {
            const res = await fetch(`${API_BASE}/tasks/${taskId}/fai_override`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ admin_pin: adminPin, reason: reason.trim() }),
            });
            if (!res.ok) {
                throw new Error(await apiErrorMessage(res, "Override failed"));
            }
            const data = await res.json();
            alert(`✅ ${data.message || 'FAI gate unlocked.'}`);
            onSuccess && onSuccess();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 10001 }}>
            <div style={{ backgroundColor: "var(--jq-bg-card, #fff)", padding: "20px", borderRadius: "8px", width: "95%", maxWidth: "500px", boxShadow: "0 4px 8px rgba(0,0,0,0.2)" }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">🛡️ FAI Admin Override</h5>
                    <button onClick={onClose} className="btn-close"></button>
                </div>
                <div className="alert alert-warning small">
                    First Article Inspection failed twice. Admin PIN required to unlock Start.
                    Override will be recorded with operator name, timestamp, and reason.
                </div>
                {error && <div className="alert alert-danger small">{error}</div>}
                <div className="mb-3">
                    <label className="form-label small fw-bold">Admin PIN</label>
                    <input
                        type="password"
                        className="form-control"
                        value={adminPin}
                        onChange={(e) => setAdminPin(e.target.value)}
                        autoFocus
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label small fw-bold">Reason for Override</label>
                    <textarea
                        className="form-control"
                        rows={3}
                        placeholder="e.g. Setup verified manually with calibrated micrometer; FAI gauge inaccurate."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </div>
                <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
                    <button className="btn btn-warning fw-bold" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? 'Unlocking...' : 'Unlock Start'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ==============================================================================
// END PPAP Measurement Components
// ==============================================================================


/**
 * JobQueuePage - Main queue component.
 */
export default function JobQueuePage() {
  const location = useLocation()
  const navigate = useNavigate()
  const detailRef = useRef(null)

  // Workstation & data
  const [workstation, setWorkstation] = useState(() => {
    const saved = sessionStorage.getItem("selectedWorkstation");
    // Use the saved value, or default to "Laser" if nothing is in storage.
    return saved ? JSON.parse(saved) : "Laser";
  });
  const [jobs, setJobs] = useState([])
  const [filteredJobs, setFilteredJobs] = useState([])
  const [userDailyTime, setUserDailyTime] = useState([]);
  const [stationDailyState, setStationDailyState] = useState({
    in_work_seconds: 0,
    waiting_seconds: 0,
    idle_seconds: 0,
  })

  // Filters
  const [filterStatus, setFilterStatus] = useState("all")
  const [filterPriority, setFilterPriority] = useState("all")
  const [sortMode, setSortMode] = useState("priority")
  const [searchTerm, setSearchTerm] = useState("")
  const [allScheduledDates, setAllScheduledDates] = useState([])
  const [filterScheduledDate, setFilterScheduledDate] = useState("")
  const [showCompleted, setShowCompleted] = useState(false)
  const [showReadyOnly, setShowReadyOnly] = useState(() => {
    const saved = sessionStorage.getItem("selectedWorkstation");
    const ws = saved ? JSON.parse(saved) : "Laser";
    return ws !== "Laser";
  });
  // When on, show only rows that have had qty carved into pre-ship slices
  // (pre_shipped_qty > 0). Composes (AND) with the other filters.
  const [showPreShippedOnly, setShowPreShippedOnly] = useState(false);
  const [filterSubProcess, setFilterSubProcess] = useState("all");
  const [availableSubProcesses, setAvailableSubProcesses] = useState([]);
  const [filterCustomer, setFilterCustomer] = useState("all");
  const [availableCustomers, setAvailableCustomers] = useState([]);
  // Active sales orders (any SO not Completed/Cancelled) for searchable filter
  const [filterSalesOrder, setFilterSalesOrder] = useState("all");
  const [activeSalesOrders, setActiveSalesOrders] = useState([]);
  const [salesOrderSearch, setSalesOrderSearch] = useState("");

  // Beyond-window lookup. /queue only carries Pending work due or scheduled
  // within 30 days; an exact job or task number in the search box also asks
  // the API for that one job/task at this station with no date horizon
  // (`?find=`). The rows live HERE, never in `jobs`, and applyFilterSort merges
  // them in — so the default queue is byte-for-byte what it was.
  const [searchLookupJobs, setSearchLookupJobs] = useState([]);
  // idle | loading | done | failed | unsupported ("unsupported" = the API
  // ignored `find` and answered with the plain 30-day queue: it predates
  // the lookup, so an empty answer proves nothing about the job).
  const [searchLookupStatus, setSearchLookupStatus] = useState("idle");
  const [queueLoadCount, setQueueLoadCount] = useState(0); // bumped when loadQueue lands → lookup re-runs
  const lookupSeqRef = useRef(0); // stale-response guard: only the newest lookup may write state
  const searchNumber = parseQueueSearchNumber(searchTerm);
  // Rows that ARE the searched job/task (in the queue or found beyond the
  // window) but that the other filters then hid — set by applyFilterSort so
  // it is always in step with filteredJobs. Drives the "hidden by your
  // filters" note instead of a silent blank list.
  const [searchHiddenMatches, setSearchHiddenMatches] = useState([]);

  // Selected job & detail - MODIFIED to load from sessionStorage
  const [selectedJobId, setSelectedJobId] = useState(() => {
    const saved = sessionStorage.getItem("selectedJobId");
    return saved && saved !== "null" ? JSON.parse(saved) : null;
  });
  const [selectedTaskId, setSelectedTaskId] = useState(() => {
    const saved = sessionStorage.getItem("selectedTaskId");
    return saved && saved !== "null" ? JSON.parse(saved) : null;
  });

  const [selectedJobDetail, setSelectedJobDetail] = useState(null)

  // Laser detail
  const [cutSheetDetail, setCutSheetDetail] = useState(null)
  const [materialInfo, setMaterialInfo] = useState(null)
  const [dropMaterialInfo, setDropMaterialInfo] = useState(null)
  const [customers, setCustomers] = useState([])

  // Non-laser detail
  const [jobParts, setJobParts] = useState([])
  const isFabrication = workstation === "Fabrication";

  // Activity log
  const [activityLog, setActivityLog] = useState([])

  // UI states
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [enlargedImageUrl, setEnlargedImageUrl] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track which cut sheet IDs have shown notes
  const [cutSheetNotesShown, setCutSheetNotesShown] = useState({})

  // Track which job IDs have shown job notes
  const [notesShown, setNotesShown] = useState({})

  // State for PPAP Modal
  const [showPpapModal, setShowPpapModal] = useState(false);
  const [ppapPartData, setPpapPartData] = useState(null); // Data for the part being measured
  const [ppapTaskId, setPpapTaskId] = useState(null); // The task ID for the part
  const [ppapPhase, setPpapPhase] = useState('ongoing'); // 'initial' (FAI) or 'ongoing'

  // State for First Article Inspection (FAI) gate on Laser tasks
  const [faiStatus, setFaiStatus] = useState(null);
  const [showFaiOverrideModal, setShowFaiOverrideModal] = useState(false);

  const [stationLocked, setStationLocked] = useState(false);
  const [lockReason, setLockReason] = useState("");
  const [pendingMaintenanceToday, setPendingMaintenanceToday] = useState([]);

  // State for Drag and Drop & Admin Check
  const [draggedJob, setDraggedJob] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // NEW: Check for Admin Role (Polling to catch logout events instantly)
  useEffect(() => {
    const checkAdminStatus = () => {
      const role = sessionStorage.getItem("userRole") || "";
      // Check for 'admin' or 'manager'
      const hasAccess = role.toLowerCase() === 'admin' || role.toLowerCase() === 'manager';
      
      // Only update state if it actually changed to prevent unnecessary re-renders
      setIsAdmin(prevIsAdmin => {
        if (prevIsAdmin !== hasAccess) {
          return hasAccess;
        }
        return prevIsAdmin;
      });
    };

    // 1. Check immediately on mount
    checkAdminStatus();

    // 2. Poll every 500ms to catch "Clear Credentials" clicks from the NavBar
    const intervalId = setInterval(checkAdminStatus, 500);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  // --- Calculate next allowed job/task (all workstations) ---
  // Finds the first 'Pending' item in the current filtered list.
  // Laser uses job_id (grouped jobs), other stations use task_id (individual tasks).
  // (queueRules.nextAllowedId: non-blocking maintenance is skipped; a task awaiting its next op
  // counts as ready, so the lock doesn't make the junior skip the run Jesse just handed over.)
  const nextPendingId = useMemo(() => nextAllowedId(filteredJobs, workstation), [workstation, filteredJobs]);
  
  useEffect(() => {
    sessionStorage.setItem("selectedWorkstation", JSON.stringify(workstation));
  }, [workstation]);

  // Load stored cutSheetNotesShown from sessionStorage
  useEffect(() => {
    const storedCuts = sessionStorage.getItem("cutSheetNotesShown")
    if (storedCuts) {
      setCutSheetNotesShown(JSON.parse(storedCuts))
    }
  }, [])

  // Load the "notesShown" from sessionStorage if needed
  useEffect(() => {
    const storedNotes = sessionStorage.getItem("notesShown")
    if (storedNotes) {
      setNotesShown(JSON.parse(storedNotes))
    }
  }, [])

  // Fetch FAI status for the currently selected Laser task.
  // Re-runs when the task changes or after the FAI/override modal closes
  // so the UI reflects the latest gate state without manual refresh.
  useEffect(() => {
    if (workstation !== 'Laser' || !selectedTaskId) {
      setFaiStatus(null);
      return;
    }
    let cancelled = false;
    async function loadFaiStatus() {
      try {
        const res = await fetch(`${API_BASE}/tasks/${selectedTaskId}/fai_status`);
        if (!res.ok) {
          if (!cancelled) setFaiStatus(null);
          return;
        }
        const data = await res.json();
        if (!cancelled) setFaiStatus(data);
      } catch (err) {
        console.error('Failed to load FAI status:', err);
        if (!cancelled) setFaiStatus(null);
      }
    }
    loadFaiStatus();
    return () => { cancelled = true; };
  }, [selectedTaskId, workstation, showPpapModal, showFaiOverrideModal]);

  // Fetch customers once on mount (for material owner display)
  useEffect(() => {
    async function loadCustomers() {
      try {
        const res = await fetch(`${API_BASE}/customers`);
        if (res.ok) {
          const data = await res.json();
          setCustomers(data);
        }
      } catch (err) {
        console.error('Failed to load customers:', err);
      }
    }
    loadCustomers();
  }, []);

  // On mount or location change, parse params
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const st = params.get("station")
    if (st && st !== workstation) {
      setWorkstation(st)
    }
    // Initial selection from URL is now handled by the other useEffects
  }, [location, workstation])

  // MODIFIED: useEffect to handle workstation changes
  useEffect(() => {
    // 1. CLEAR DATA IMMEDIATELY
    // This prevents the UI from trying to render Fabrication data 
    // using Laser logic while waiting for the fetch.
    setJobs([]);
    setFilteredJobs([]);
    
    // 2. CLEAR FILTERS
    // Search terms for Fab (e.g., "Hy-Flo") likely don't apply to Laser.
    setSearchTerm("");
    setFilterSubProcess("all");
    setFilterCustomer("all");
    setFilterSalesOrder("all");
    setSalesOrderSearch("");
    // A found-beyond-window row belongs to the OLD station; drop it now rather
    // than after the lookup effect notices the empty term. (An in-flight
    // response is discarded by the sequence guard in that effect.)
    setSearchLookupJobs([]);
    setSearchLookupStatus("idle");

    // Clear selection
    handleDeselectJob();

    // Load new data
    loadQueue();
    loadStationUserTime();
    loadStationStateToday(workstation);

    // Refresh both user times and station state every 60s:
    const intervalId = setInterval(() => {
      loadStationUserTime();
      loadStationStateToday(workstation);
    }, 60_000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workstation, showCompleted]);

  // Default the "ready only" filter based on workstation: non-laser stations
  // open with it ON so operators see only jobs they can actually start.
  useEffect(() => {
    setShowReadyOnly(workstation !== "Laser");
  }, [workstation]);

  // A scanned traveler (/s/task-N -> ScanResolvePage -> /queue?station=&task=&job=):
  // open that task. The station param is applied by the location effect above; this
  // waits until the page is ON that station, so the workstation effect's reset (search
  // cleared, selection dropped) has already run in the same commit, and then selects the
  // task and searches its number (the beyond-window lookup keeps it in the list even when
  // it is months out). Declared after the workstation effect on purpose.
  const scanTargetRef = useRef(null)
  useEffect(() => {
    const target = queueDeepLink(location.search)
    if (target) scanTargetRef.current = target
  }, [location.search])
  useEffect(() => {
    const t = scanTargetRef.current
    if (!t || t.station !== workstation) return
    scanTargetRef.current = null
    setSearchTerm(String(t.taskId))
    handleSelectJob({ job_id: t.jobId, task_id: t.taskId })
    // Drop the params: a reload must not re-select, and a lingering ?station= would
    // pin the station dropdown (the location effect re-applies it on every change).
    navigate("/queue", { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workstation, location.search])

  // Apply filter+sort whenever jobs or filter states change
  useEffect(() => {
    applyFilterSort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, searchLookupJobs, filterStatus, filterPriority, sortMode, searchTerm, filterScheduledDate, showCompleted, showReadyOnly, showPreShippedOnly, filterSubProcess, filterCustomer, filterSalesOrder, workstation])

  // Beyond-window lookup: an exact job/task number in the search box.
  // Debounced so "3", "39", "390", "3904", "39044" is one request, not five.
  // Re-runs when loadQueue lands (queueLoadCount) so a merged row's status is
  // as fresh as its neighbours'. Every run takes a new sequence number and only
  // the newest may write state, so a slow answer for "390" can never overwrite
  // "39044" and the previous station's answer can never land after a switch.
  useEffect(() => {
    const seq = ++lookupSeqRef.current;
    if (searchNumber == null) {
      setSearchLookupJobs([]);
      setSearchLookupStatus("idle");
      return;
    }
    setSearchLookupStatus("loading");
    const n = searchNumber;
    const timer = setTimeout(async () => {
      try {
        // The contract: every row that comes back IS job/task n. An API
        // without `find` ignores it and answers with the ordinary 30-day
        // queue instead — rows, none of them n. findInQueue keeps only the
        // job/task asked for and says when such an answer says nothing about
        // whether n exists (the note must not claim it doesn't).
        const { rows: matched, unsupported } = await findInQueue(workstation, n, { includeCompleted: showCompleted });
        if (seq !== lookupSeqRef.current) return; // superseded
        setSearchLookupJobs(shapeQueueRows(matched));
        setSearchLookupStatus(unsupported ? "unsupported" : "done");
      } catch (err) {
        if (seq !== lookupSeqRef.current) return;
        // Never the page-level error and never a blank queue: the local list
        // is still right, the operator just doesn't get the extra row.
        console.warn(`Beyond-window lookup for #${n} at ${workstation} failed:`, err);
        setSearchLookupJobs([]);
        setSearchLookupStatus("failed");
      }
    }, 350);
    return () => {
      clearTimeout(timer);
      lookupSeqRef.current += 1; // also retires an in-flight fetch on unmount
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchNumber, workstation, showCompleted, queueLoadCount]);

  // Load active sales orders once (any SO not Completed/Cancelled) for the SO filter
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/sales_orders?status=Open`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data)) {
          setActiveSalesOrders(
            data
              .map((so) => ({
                sales_order_id: so.sales_order_id,
                order_number: so.order_number,
                customer_name: so.customer_name,
              }))
              .sort((a, b) => String(a.order_number).localeCompare(String(b.order_number)))
          );
        }
      } catch (e) {
        // Non-fatal — SO filter just won't populate
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Is this list row the selected job (Laser) / the selected task (every
  // other station)? Shared by the refresh re-select below and the
  // beyond-window deselect after it.
  function isSelection(job) {
    // For non-Laser stations, match the task_id as well
    if (workstation !== "Laser" && selectedTaskId) {
      return job.job_id === selectedJobId && job.task_id === selectedTaskId;
    }
    // For Laser, just the job_id is sufficient
    return job.job_id === selectedJobId;
  }

  // NEW: useEffect to restore OR UPDATE the selected job after data refresh
  useEffect(() => {
    // If a job is selected and the main job list has been updated...
    if (selectedJobId && jobs.length > 0) {
      // A row found beyond the 30-day window is never in `jobs`; without the
      // second look every queue refresh would close its detail panel.
      const jobToReselect = jobs.find(isSelection) || searchLookupJobs.find(isSelection);

      if (jobToReselect) {
        // The job still exists in the list, so re-run handleSelectJob
        // to fetch its latest details — silent so flagged-part alerts
        // don't re-fire on every auto-refresh.
        handleSelectJob(jobToReselect, { silent: true });
      } else if (searchLookupStatus === "loading") {
        // A beyond-window row (a scanned traveler months out) may still be on its
        // way; the lookup effect below deselects once it lands without it.
      } else {
        // The previously selected job is no longer in the queue (e.g., it was completed),
        // so clear the detail view.
        handleDeselectJob();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]); // This hook will now correctly re-sync the details whenever the jobs list updates.

  // A selected beyond-window card lives only in searchLookupJobs, so when a
  // lookup comes back WITHOUT it (completed with Show Completed off, or the
  // lookup failed and its rows were cleared) the card leaves the list — but
  // `jobs` did not change, so the effect above never runs. Deselect here,
  // the moment the answer lands, instead of one refresh cycle later. Only a
  // settled lookup counts: clearing the search term (idle) keeps today's
  // behaviour, the panel stays until the next refresh. Not folded into the
  // [jobs] effect on purpose: that one re-fetches the whole detail panel.
  useEffect(() => {
    if (!selectedJobId) return;
    if (queueLoadCount === 0) return; // `jobs` is not authoritative until the first load lands
    if (searchLookupStatus === "idle" || searchLookupStatus === "loading") return;
    if (jobs.find(isSelection) || searchLookupJobs.find(isSelection)) return;
    handleDeselectJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchLookupJobs, searchLookupStatus]);

  // Extract all unique subprocesses from the current queue and populate the dropdown options.
  useEffect(() => {
    if (workstation === "Fabrication") {
      const subProcesses = new Set(
        jobs.map(job => job.sub_process).filter(Boolean) // .filter(Boolean) removes any null/undefined
      );
      setAvailableSubProcesses(Array.from(subProcesses).sort());
    } else {
      setAvailableSubProcesses([]); // Clear when not on Fabrication station
    }
  }, [jobs, workstation]);


  useInterval(() => {
    // This check prevents refreshing in the background if the user isn't looking at the page
    if (document.visibilityState === 'visible') {
      console.log("Refreshing job queue data...");
      loadQueue();
    }
  }, 300000); // Refresh every 5 minutes (300000 ms)

  // **NEW**: This interval sends a lightweight "keep-alive" ping every 2 minutes
  // to prevent the server's database connection from becoming stale.
  useInterval(() => {
    if (document.visibilityState === 'visible') {
      fetch(`${API_BASE}/time/now`)
        .then(res => {
          if (res.ok) {
            console.log("Keep-alive ping successful at", new Date().toLocaleTimeString());
          } else {
            console.error("Keep-alive ping failed with status:", res.status);
          }
        })
        .catch(err => {
          console.error("Keep-alive ping network error:", err);
        });
    }
  }, 120000); // Ping every 2 minutes (120,000 ms)


  /**
   * FINAL CORRECTED: groupLaserJobs - This function now groups laser tasks by a
   * composite key of both the job ID and the cut sheet ID. This correctly handles all scenarios:
   * 1. A single job with multiple parts on one cut sheet (is grouped into one item).
   * 2. A single assembly job with multiple, different cut sheets (each sheet is a separate item).
   * 3. Multiple, different jobs that happen to use the same cut sheet (are kept as separate items).
   * 4. Shape stock parts (are treated as individual items).
   */
  function groupLaserJobs(rows) {
    const groupedJobs = new Map();
    const finalJobs = [];

    // Helper to structure the nested task data
    const mapTaskData = (t) => ({
      task_id: t.task_id,
      workstation: t.workstation,
      status: t.task_status || t.status,
      scheduled_date: t.scheduled_date,
      estimated_time: t.estimated_time,
      sheet_required: t.sheet_required,
      sheet_count: t.sheet_count,
      cut_sheet_id: t.cut_sheet_id,
      part_id: t.part_id,
      qty_completed: t.qty_completed,
      elapsed_time_seconds: t.elapsed_time_seconds,
      laserMaterialDesc: t.material_description || "(No Material)",
      maintenance_reason: t.maintenance_reason,
      maintenance_definition_id: t.maintenance_definition_id,
      ppap_required_for_task: t.ppap_required_for_task,
      ppap_samples_required: t.ppap_samples_required,
      ppap_samples_collected: t.ppap_samples_collected,
      authorized_user_id: t.authorized_user_id,
    });

    for (const row of rows) {
      // 1. Shape Stock tasks (Individual)
      if (row.workstation === 'Laser' && row.part_id != null) {
        finalJobs.push({
          ...row,
          tasks: [mapTaskData(row)],
          laserEstimatedTime: row.estimated_time,
          laserMaterialDesc: row.material_description || "(No Material)",
        });
      }
      // 2. Sheet Stock tasks (Grouped by Job + Cut Sheet)
      else if (row.workstation === 'Laser' && row.cut_sheet_id != null) {
        const groupKey = `${row.job_id}-${row.cut_sheet_id}`;
        if (!groupedJobs.has(groupKey)) {
          groupedJobs.set(groupKey, []);
        }
        groupedJobs.get(groupKey).push(row);
      }
      // 3. Maintenance / Generic tasks (Individual - NEW)
      else if (row.workstation === 'Laser') {
        finalJobs.push({
          ...row,
          tasks: [mapTaskData(row)],
          laserEstimatedTime: row.estimated_time,
          laserMaterialDesc: row.material_description || "(No Material)",
        });
      }
    }

    // Process the grouped Sheet Stock tasks
    for (const [groupKey, tasks] of groupedJobs.entries()) {
      const primaryRow = tasks[0];
      if (primaryRow) {
        const jobObject = {
          ...primaryRow,
          ppap_required_for_task: primaryRow.ppap_required_for_task, 
          ppap_samples_required: primaryRow.ppap_samples_required,
          ppap_samples_collected: primaryRow.ppap_samples_collected,
          tasks: tasks.map(mapTaskData),
          laserEstimatedTime: primaryRow.estimated_time,
          laserMaterialDesc: primaryRow.material_description || "(No Material)",
        };
        finalJobs.push(jobObject);
      }
    }

    return finalJobs;
  }

  async function loadQueue() {
    setLoading(true)
    setError(null)
    setStationLocked(false); // Reset lock state on load
    setLockReason("");

    try {
      let data = await fetchQueueRows(workstation, { includeCompleted: showCompleted })

      const blocker = stationBlocker(data);

      if (blocker) {
          setStationLocked(true);
          setLockReason(blocker.maintenance_title || "Critical Maintenance");
      }

      // Pending maintenance scheduled for today (all workstations)
      setPendingMaintenanceToday(pendingMaintenanceFor(data));

      data = shapeQueueRows(data);

      setJobs(data)
      gatherScheduledDates(data)
      gatherCustomers(data)
      setQueueLoadCount((c) => c + 1); // let an active beyond-window lookup refresh too
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  /**
   * shapeQueueRows - raw /queue rows into list items. Laser groups by job + cut
   * sheet (groupLaserJobs); every other station is one item per task, deduped
   * by task_id. The ONE path for both loadQueue and the beyond-window lookup,
   * so a found row is indistinguishable from its in-window neighbours.
   */
  function shapeQueueRows(rows) {
    if (workstation === "Laser") {
      return groupLaserJobs(rows);
    }
    return uniqueTasks(rows);
  }

  // Handle forcing a job to the top (Priority 1 + Scheduled Yesterday)
  async function promoteJobToTop(job) {
    if (!window.confirm(`Force Job #${job.master_job_num} to start immediately?\n\nThis will set Priority to 1 and Schedule Date to Yesterday (to ensure it ranks first).`)) {
      return;
    }

    setLoading(true);
    try {
      // 1. Update Job Priority to 1
      await fetch(`${API_BASE}/jobs/${job.job_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ priority_level: 1 })
      });

      // 2. Update Task Schedule to Yesterday (Manual Override)
      // Find the specific task ID for this list item
      const laserTask = job.tasks?.find(t => t.workstation === "Laser");
      const taskId = workstation === "Laser" ? (laserTask?.task_id) : job.task_id;

      if (taskId) {
        // Calculate Yesterday's date to force this job to win the Date Sort logic
        const date = new Date();
        date.setDate(date.getDate() - 1);
        const yesterdayStr = date.toISOString().split('T')[0];

        await fetch(`${API_BASE}/tasks/${taskId}/manual_schedule`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ scheduled_date: yesterdayStr })
        });
      }

      // 3. Refresh to reflect changes
      await loadQueue();
      alert("Job promoted to top successfully."); 
    } catch (err) {
      console.error(err);
      alert("Failed to promote job.");
    } finally {
      setLoading(false);
      setDraggedJob(null);
    }
  }

  // Drag Event Handlers
  const handleDragStart = (e, job) => {
    setDraggedJob(job);
    e.dataTransfer.effectAllowed = "move";
    // Optional: Set a custom drag image or data if needed
  };

  const handleDragOver = (e, index) => {
    e.preventDefault(); // Necessary to allow dropping
    e.dataTransfer.dropEffect = "move";
    
    // Optional visual cue: Add class to target (simplified here)
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (!draggedJob) return;

    // We only trigger the "Force Start" logic if dropped at the very top (Index 0)
    // or if the list was empty (unlikely here).
    if (targetIndex === 0) {
      // Don't do anything if we dropped it on itself at the top
      if (filteredJobs[0].job_id !== draggedJob.job_id) {
        promoteJobToTop(draggedJob);
      }
    }
    // Logic for reordering arbitrarily could go here, but requires backend "sort_order" support.
    
    setDraggedJob(null);
  };

  async function loadStationUserTime() {
    try {
      // 1) Fetch from the all-stations endpoint:
      const res = await fetch(`${API_BASE}/stations/user_time_today`)
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res, "Failed to fetch user time"));
      }
      const data = await res.json()
      // 2) Filter to only the current workstation:
      const stationData = data.filter((item) => item.station === workstation)
      // 3) Update state with the filtered data:
      setUserDailyTime(stationData)
    } catch (err) {
      console.error("Error in loadStationUserTime:", err)
    }
  }

  async function loadStationStateToday(stn) {
        try {
          const res = await fetch(`${API_BASE}/stations/${stn}/today_state_summary`)
          if (!res.ok) {
            throw new Error(await apiErrorMessage(res, "Failed to fetch station state"));
          }
          const data = await res.json()
          setStationDailyState({
            in_work_seconds: data.in_work_seconds || 0,
            waiting_seconds: data.waiting_seconds || 0,
            idle_seconds: data.idle_seconds || 0,
            maintenance_seconds: data.maintenance_seconds || 0,
          })
        } catch (err) {
          console.error("Error in loadStationStateToday:", err)
        }
      }

  /**
   * gatherScheduledDates - collects unique scheduled dates from the queue,
   * for the "Scheduled Date" filter dropdown.
   */
  function gatherScheduledDates(list) {
    const dateSet = new Set()
    for (const item of list) {
      const sDate = item.scheduled_date
      const status = item.task_status || item.status
      if (sDate && ["Pending", "In Progress", "Paused"].includes(status)) {
        const dt = sDate.split("T")[0]
        dateSet.add(dt)
      }
    }
    setAllScheduledDates([...dateSet].sort())
  }

  /**
   * gatherCustomers - collects unique customer names from active tasks in the queue,
   * for the "Customer" filter dropdown.
   */
  function gatherCustomers(list) {
    const customerSet = new Set()
    for (const item of list) {
      const name = item.customer_name
      const status = item.task_status || item.status
      if (name && ["Pending", "In Progress", "Paused"].includes(status)) {
        customerSet.add(name)
      }
    }
    setAvailableCustomers([...customerSet].sort())
  }

  function applyFilterSort() {
    // 2. SAFETY CHECK: If jobs is null/undefined, stop.
    if (!jobs) {
        setFilteredJobs([]);
        return;
    }

    // The queue plus any row the beyond-window lookup found that the queue
    // does not already hold (marked found_beyond_window). From here on a found
    // row is just another row: same filters, same search, same sort.
    let temp = mergeLookupIntoQueue(jobs, searchLookupJobs, workstation === "Laser" ? laserQueueKey : taskQueueKey);
    const merged = temp; // everything the search could show, before any filter
    // "#9880" means job 9880 (the placeholder invites the #); the substring
    // match below compares against the bare number, so drop a leading "#".
    const st = searchTerm.replace(/^\s*#\s*/, "").toLowerCase().trim();

    try {
      if (workstation === "Laser") {
        // ---------------------------
        // LASER logic
        // ---------------------------
        if (filterStatus === "all") {
          if (!showCompleted) {
            temp = temp.filter((job) => {
              const wsTask = job.tasks?.find((t) => t.workstation === "Laser");
              return wsTask && wsTask.status !== "Completed";
            });
          }
        } else {
          temp = temp.filter((job) => {
            const wsTask = job.tasks?.find((t) => t.workstation === "Laser");
            return wsTask && wsTask.status === filterStatus;
          });
        }

        // Pre-shipped filter (Laser)
        if (showPreShippedOnly) {
          temp = temp.filter((j) => j && (j.pre_shipped_qty || 0) > 0);
        }

        // Priority filter
        if (filterPriority !== "all") {
          const pVal = parseInt(filterPriority, 10);
          temp = temp.filter((j) => j.priority_level === pVal);
        }

        // Scheduled date filter
        if (filterScheduledDate === "nodate") {
          temp = temp.filter((j) => !j.scheduled_date);
        } else if (filterScheduledDate) {
          temp = temp.filter((j) => {
            if (!j.scheduled_date) return false;
            return j.scheduled_date.split("T")[0] === filterScheduledDate;
          });
        }

        // Customer filter
        if (filterCustomer !== "all") {
          temp = temp.filter((j) => j.customer_name === filterCustomer);
        }

        // Sales Order filter (Laser) — match SO on any grouped task
        if (filterSalesOrder !== "all") {
          const soId = Number(filterSalesOrder);
          temp = temp.filter((j) => {
            if (j.sales_order_id === soId) return true;
            return (j.tasks || []).some((t) => t && t.sales_order_id === soId);
          });
        }

        // Search term filter (Laser)
        if (st) {
          temp = temp.filter((j) => {
            if (!j) return false;
            const masterStr = j.master_job_num ? String(j.master_job_num).toLowerCase() : "";
            const desc = j.job_description ? j.job_description.toLowerCase() : "";
            const pri = j.priority_level != null ? String(j.priority_level) : "";
            
            const taskIdMatch = j.tasks?.some((t) =>
               t && t.task_id ? String(t.task_id).toLowerCase().includes(st) : false
            );

            return (
              masterStr.includes(st) ||
              desc.includes(st) ||
              pri.includes(st) ||
              taskIdMatch
            );
          });
        }

        // Sort (Laser)
        temp.sort((a, b) => {
          // Completed always sorts to the bottom regardless of pin.
          const aDone = (a.task_status === 'Completed' || a.status === 'Completed');
          const bDone = (b.task_status === 'Completed' || b.status === 'Completed');
          if (aDone !== bDone) return aDone ? 1 : -1;

          // Operator-pinned position wins (drag-to-reorder on Scheduling Page).
          // Laser groups have a single task per cut sheet, so manual_sort_position
          // is carried straight through from the underlying row.
          const aPin = a.manual_sort_position ?? Number.POSITIVE_INFINITY;
          const bPin = b.manual_sort_position ?? Number.POSITIVE_INFINITY;
          if (aPin !== bPin) return aPin - bPin;

          if (a.priority_level !== b.priority_level) {
            return (a.priority_level ?? 999) - (b.priority_level ?? 999);
          }
          const ad = a.due_date ? (parseDueDate(a.due_date)?.getTime() ?? Infinity) : Infinity;
          const bd = b.due_date ? (parseDueDate(b.due_date)?.getTime() ?? Infinity) : Infinity;
          if (ad !== bd) return ad - bd;

          if (a.multi_station !== b.multi_station) {
            return a.multi_station ? -1 : 1;
          }
          const aMat = a.laserMaterialDesc || "";
          const bMat = b.laserMaterialDesc || "";
          return aMat.localeCompare(bMat);
        });

      } else {
        // ---------------------------
        // NON-LASER logic (Fabrication, etc.)
        // ---------------------------

        // 1. Ready Filter
        // "Ready" means "an operator can pick this up right now". A multi-op task
        // parked between ops is Paused, not Pending — but it IS ready, and it is
        // precisely the card the NEXT operator needs to find. Without this arm the
        // junior cannot see the task Jesse just handed them. Scoped by op_count so
        // ordinary Paused tasks (someone else's half-done work) stay hidden exactly
        // as before.
        if (showReadyOnly) {
          temp = temp.filter(
            (task) =>
              task &&
              !task.blocked &&
              (task.task_status === "Pending" || isAwaitingNextOp(task))
          );
        }

        // 2. Status Filter
        if (filterStatus === "all") {
          if (!showCompleted) {
            temp = temp.filter((task) => task && task.task_status !== "Completed");
          }
        } else {
          temp = temp.filter((task) => task && task.task_status === filterStatus);
        }

        // 2b. Pre-shipped Filter
        if (showPreShippedOnly) {
          temp = temp.filter((task) => task && (task.pre_shipped_qty || 0) > 0);
        }

        // 3. Priority Filter
        // Prefer task_priority (per-part, Bad Idea consumption-model output)
        // and fall back to job-level priority_level (non-Bad Idea operator-set).
        if (filterPriority !== "all") {
          const pVal = parseInt(filterPriority, 10);
          temp = temp.filter((t) => t && (t.task_priority ?? t.priority_level) === pVal);
        }

        // 4. Scheduled Date Filter (Safe)
        if (filterScheduledDate === "nodate") {
          temp = temp.filter((t) => t && !t.scheduled_date);
        } else if (filterScheduledDate) {
          temp = temp.filter((t) => {
             if (!t || !t.scheduled_date) return false;
             return String(t.scheduled_date).startsWith(filterScheduledDate);
          });
        }

        // 5. Customer Filter
        if (filterCustomer !== "all") {
          temp = temp.filter((t) => t && t.customer_name === filterCustomer);
        }

        // 5b. Sales Order Filter
        if (filterSalesOrder !== "all") {
          const soId = Number(filterSalesOrder);
          temp = temp.filter((t) => t && t.sales_order_id === soId);
        }

        // 6. Search Filter (Safe & Debugged)
        if (st) {
          console.log("Debug: Entering Search Filter...");
          temp = temp.filter((t) => {
            if (!t) return false;
            // Force string conversion on ALL fields
            const masterStr = t.master_job_num != null ? String(t.master_job_num).toLowerCase() : "";
            const desc = t.job_description ? String(t.job_description).toLowerCase() : "";
            const taskIdStr = t.task_id != null ? String(t.task_id).toLowerCase() : "";
            const prodDesc = t.product_description ? String(t.product_description).toLowerCase() : "";
            const subProc = t.sub_process ? String(t.sub_process).toLowerCase() : "";

            // Simple include check
            return (
              masterStr.includes(st) ||
              desc.includes(st) ||
              taskIdStr.includes(st) ||
              prodDesc.includes(st) ||
              subProc.includes(st)
            );
          });
        }

        // 7. Sub-Process Filter
        if (workstation === "Fabrication" && filterSubProcess !== "all") {
          temp = temp.filter((task) => task && task.sub_process === filterSubProcess);
        }

        // 8. Sorting (Crash Proofed)
        // For non-laser stations, each task has a well-defined single part,
        // so we sort by the task's own per-part priority/due date when set
        // (Bad Idea consumption model), falling back to job-level values
        // (operator-set on non-Bad Idea jobs). This keeps tasks ordered by
        // their actual per-part urgency rather than the job-level aggregate.
        temp.sort(compareStationTasks(workstation));
      }

      console.log(`Debug: Filter Complete. Returning ${temp.length} jobs.`);
      setFilteredJobs(temp);
      // The searched job/task is in the list but none of its rows survived
      // the filters (blocked, not ready, completed, wrong priority/date/…):
      // keep those rows so the search box can say so instead of going blank.
      if (searchNumber != null && !temp.some((r) => rowMatchesNumber(r, searchNumber))) {
        setSearchHiddenMatches(merged.filter((r) => rowMatchesNumber(r, searchNumber)));
      } else {
        setSearchHiddenMatches([]);
      }

    } catch (err) {
      // DEBUG: Explicitly ALERT the user to the crash
      console.error("Critical error in applyFilterSort:", err);
      alert(`⚠️ Filter Logic Crashed: ${err.message}\nCheck console for details.`);

      // DEBUG: Set to EMPTY so we know it crashed (instead of showing all jobs)
      setFilteredJobs([]);
      setSearchHiddenMatches([]);
    }
  }

  // MODIFIED: This function now clears all selection state AND sessionStorage
  function handleDeselectJob() {
    setSelectedJobId(null);
    setSelectedTaskId(null);
    setSelectedJobDetail(null);
    setCutSheetDetail(null);
    setMaterialInfo(null);
    setDropMaterialInfo(null);
    setJobParts([]);
    setActivityLog([]);
    sessionStorage.removeItem("selectedJobId");
    sessionStorage.removeItem("selectedTaskId");
  }
 
  // This function now only clears the detail view, used before fetching new details
  function clearSelectedJobDetail() {
    setSelectedJobDetail(null)
  }

  /**
   * handleSelectJob - fetch and display detail for the chosen row/job.
   */
  async function handleSelectJob(job, options = {}) {
    const { silent = false } = options;
    // Determine the correct task ID to use, whether it's an initial selection or a reload.
    const taskIdForSelection = job.task_id || selectedTaskId;

    // Persist the selection to sessionStorage
    sessionStorage.setItem("selectedJobId", JSON.stringify(job.job_id));
    if (taskIdForSelection) {
      sessionStorage.setItem("selectedTaskId", JSON.stringify(taskIdForSelection));
    } else {
      sessionStorage.removeItem("selectedTaskId");
    }

    setSelectedJobId(job.job_id);
    setSelectedTaskId(taskIdForSelection); // Use the new robust variable

    // Clear previous details before fetching new ones
    clearSelectedJobDetail();
    setCutSheetDetail(null);
    setMaterialInfo(null);
    setDropMaterialInfo(null);
    setJobParts([]);
    setActivityLog([]);

    try {
      // 1-2) Job detail + progress (features/queue/queueData.js loadJobDetail: the Laser sees
      // every task of the job, any other station only the selected one).
      // Carry the per-part pre-ship qty from the queue row (the /queue payload
      // includes pre_shipped_qty; GET /jobs/{id} does not) so the detail card
      // can surface it. pre_shipped_qty is per-task (scoped to the task's part),
      // so resolve it for the SELECTED task: prefer the clicked queue row, then
      // a task_id lookup in the loaded queue (reload paths pass a minimal job
      // object). Must be task-scoped, NOT job-scoped — a job_id lookup would grab
      // another part's value and re-surface the cross-part badge bug.
      const _preShipRow = jobs.find((j) => j.task_id === taskIdForSelection)
        || searchLookupJobs.find((j) => j.task_id === taskIdForSelection);
      const { detail: combined, job: detailData, allTasks } = await loadJobDetail({
        jobId: job.job_id,
        taskId: taskIdForSelection,
        workstation,
        preShippedQty: job?.pre_shipped_qty ?? _preShipRow?.pre_shipped_qty ?? 0,
      });
      setSelectedJobDetail(combined);

      // Show job note once
      if (detailData.notes && !notesShown[job.job_id]) {
        alert(`JOB NOTE:\n\n${detailData.notes}`);
        const updated = { ...notesShown, [job.job_id]: true };
        setNotesShown(updated);
        sessionStorage.setItem("notesShown", JSON.stringify(updated));
      }

      // 3) Job parts, each filled in from /parts/{id} (queueData.loadJobParts: images, flags, PPAP,
      // blast times, coating colour; its allowlist is the ONLY path part fields take to a station).
      const partArr = await loadJobParts(job.job_id);
      if (partArr) {
        setJobParts(partArr);

        // Flagged-parts acknowledgment alert. Only fires for user-initiated
        // selections (silent=false); auto-refresh and reload-after-action
        // call sites pass silent=true so the operator isn't re-prompted
        // every 5 minutes for flags they already dismissed.
        const anyFlagged = partArr.some((pr) => pr.flagged);
        if (anyFlagged && !silent) {
          alert("WARNING: This job contains flagged part(s). Please review carefully.");
        }
      }

      // 4) If Laser, fetch the cut sheet data if applicable
      if (workstation === "Laser") {
        const specificTask = allTasks.find(t => t.task_id === taskIdForSelection);

        if (specificTask && specificTask.cut_sheet_id) {
          const cutSheetId = specificTask.cut_sheet_id;
          // queueData.loadCutSheet: the sheet, each part filled in (image, PPAP).
          const csData = await loadCutSheet(cutSheetId);

          if (csData) {
            setCutSheetDetail(csData);

            // Show cut sheet note once
            if (csData.notes && !cutSheetNotesShown[cutSheetId]) {
              alert(`CUT SHEET NOTE:\n\n${csData.notes}`);
              const updated = { ...cutSheetNotesShown, [cutSheetId]: true };
              setCutSheetNotesShown(updated);
              sessionStorage.setItem("cutSheetNotesShown", JSON.stringify(updated));
            }

            // Material & drop material
            if (csData.material_id) {
              const matRes = await fetch(`${API_BASE}/materials/${csData.material_id}`);
              if (matRes.ok) {
                const matData = await matRes.json();
                setMaterialInfo(matData);
              }
            }
            if (csData.drop_material_id) {
              const dmRes = await fetch(`${API_BASE}/materials/${csData.drop_material_id}`);
              if (dmRes.ok) {
                const dmData = await dmRes.json();
                setDropMaterialInfo(dmData);
              }
            }
          }
        }
      }

      // 5) Activity log (only the selected task's entries)
      const filteredLogData = await loadTaskActivity(job.job_id, taskIdForSelection);
      if (filteredLogData) {
        setActivityLog(filteredLogData);
      }

      // Scroll detail into view
      if (detailRef.current) {
        const rect = detailRef.current.getBoundingClientRect();
        const offset = 0;
        const scrollY = window.scrollY + rect.top - offset;

        window.scrollTo({
          top: scrollY,
          behavior: "smooth",
        });
      }
    } catch (err) {
      setError(err.message);
    }
  }

  function isLate(job) {
    return isDueDateLate(job.due_date)
  }

function renderJobItem(job, index) {
      const isLaser = workstation === "Laser";

      const laserTask = isLaser ? job.tasks?.find((t) => t.workstation === "Laser") : null;
      const isShapeStockLaser = isLaser && laserTask && laserTask.part_id;

      // Cut-list-level task: the list IS the work item, so the card leads with
      // Material + Stock Pieces instead of Part + Qty. Keyed on "no part_id but
      // a cut_list_id", NOT on sub_process === "Cutting" — a cut list's
      // fab_subprocess is 'Lathe'/'Milling' when parts are machined straight
      // off the stick (Blackstone standoffs), and those tasks look identical.
      const isCutListLevelTask = workstation === "Fabrication" && !job.part_id && !!job.cut_list_id;

      const displayStatus = isLaser ? (laserTask?.status || job.status) : job.task_status;
      const maintenanceReason = isLaser ? laserTask?.maintenance_reason : job.maintenance_reason;

      const authorizedUserId = isLaser ? laserTask?.authorized_user_id : job.authorized_user_id;
      const isProtected = authorizedUserId != null;

      // For non-laser, prefer task-level due date; laser uses job-level.
      const effectiveDue = isLaser ? job.due_date : (job.task_due_date ?? job.due_date);
      // For non-laser, prefer task-level priority; laser uses job-level.
      const effectivePriority = isLaser ? job.priority_level : (job.task_priority ?? job.priority_level);
      const late = isDueDateLate(effectiveDue);
      const selected = selectedJobId === job.job_id && (isLaser || selectedTaskId === job.task_id);

      // FIX: Use specific fields from the job list item, not the global selected jobParts state
      const hasPpapRequiredPart = !!job.ppap_required_for_task;
      // NEW: Check if the part is obsolete (from the backend flag)
      const isObsolete = job.contains_obsolete_parts === true;
      // NEW: Check for Maintenance Priority (Level 0)
      const isMaintenance = job.priority_level === 0;
      
      const setIdentifier = isLaser ? null : job.set_identifier;

      // At or after the join the operator is holding an assembled product, not a loose
      // primary piece — so the card names the assembly. Ten weld sets of one rack all
      // read "Part: Coil Rack - Skid 4x3 rect tube…" before this, which is the component
      // that goes INTO the rack, not the thing being built.
      const showAsAssembly = isAssemblyRow(job, workstation);

      const samplesCollected = job.ppap_samples_collected || 0;
      const samplesRequired = job.ppap_samples_required || 3;

      return (
          <div
              key={`${job.job_id}-${job.task_id || "NA"}`}
              onClick={() => handleSelectJob(job)}
              
              // NEW: Drag and Drop Attributes
              draggable={isAdmin}
              onDragStart={(e) => handleDragStart(e, job)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}

              style={{
                  marginBottom: "0.5rem",
                  padding: "0.3rem",
                  
                  // NEW: Cursor and Border logic for Dragging
                  cursor: isAdmin ? "grab" : "pointer",
                  // Highlight the top position visually when dragging something else over it
                  border: (index === 0 && draggedJob && draggedJob.job_id !== job.job_id) 
                      ? "2px dashed #007bff" 
                      : "1px solid #ddd",
                  
                  borderRadius: "6px",
                  backgroundColor: selected
                      ? "var(--jq-job-selected, #cce3ff)"
                      : isMaintenance
                      ? "var(--jq-alert-warning-bg, #fff3cd)"
                      : late
                      ? "var(--jq-job-late, #ffc6c6)"
                      : "var(--jq-bg-card, #ffffff)",
                  transition: "box-shadow 0.2s, background-color 0.2s",
                  
                  // NEW: Dim the item currently being dragged
                  opacity: draggedJob && draggedJob.job_id === job.job_id ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.1)"
              }}
              onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none"
              }}
          >
              {/* NEW: Admin Grip Handle Visual */}
              {isAdmin && (
                  <div style={{ float: 'right', color: '#ccc', marginTop: '-2px', marginRight: '2px' }}>
                      <i className="bi bi-grip-vertical"></i>
                  </div>
              )}

              {/* Sales Order badge (top right) — shown when this task/job is part of an SO */}
              {job.sales_order_number && (
                  <div
                      style={{ float: 'right', marginRight: isAdmin ? '6px' : '2px' }}
                      title={`Sales Order ${job.sales_order_number}`}
                  >
                      <span style={{
                          padding: '2px 6px',
                          backgroundColor: '#0d6efd',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                      }}>
                          <i className="bi bi-receipt me-1"></i>{job.sales_order_number}
                      </span>
                  </div>
              )}

              {/* Non-laser cards lead with the Part/Material + Qty so operators
                  see what they're making at a glance; Job # moves to the
                  smaller secondary row below. Laser cards keep the Job #
                  header because the Material/Est line is more useful there. */}
              <div style={{ fontWeight: "bold" }}>
                  {isMaintenance && (
                      <span style={{ marginRight: "6px" }} title="Maintenance Task">🔧</span>
                  )}
                  {isLaser ? (
                      <>Job #{job.master_job_num}: {isShapeStockLaser ? job.product_description : job.job_description}</>
                  ) : isCutListLevelTask ? (
                      <>Material: {job.material_description || "N/A"} <span style={{ fontWeight: "normal" }}>|</span> Stock Pieces: {job.stock_pieces_needed ? `${job.stock_pieces_needed} sticks` : "N/A"}</>
                  ) : showAsAssembly ? (
                      <>Assembly: {job.assembly_name || job.product_description || "N/A"} <span style={{ fontWeight: "normal" }}>|</span> Task Qty: {queueRowAssemblyQty(job.remaining || 0, job, workstation)}</>
                  ) : (
                      <>Part: {job.product_description || "N/A"} <span style={{ fontWeight: "normal" }}>|</span> Task Qty: {queueRowAssemblyQty(job.remaining || 0, job, workstation)}</>
                  )}

                  {/* --- NEW PROTECTED BADGE --- */}
                  {isProtected && (
                      <span style={{ 
                          marginLeft: '8px', 
                          padding: '2px 6px',
                          backgroundColor: '#6610f2', // Indigo/Purple
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                      }}>
                          <i className="bi bi-person-lock me-1"></i>Protected
                      </span>
                  )}
                  
                  {/* 1. Fabrication Sub-Process Badge */}
                  {workstation === "Fabrication" && job.sub_process && (
                      <span style={{ 
                          marginLeft: '8px', 
                          padding: '2px 6px',
                          backgroundColor: '#17a2b8',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'normal'
                      }}>
                          {job.sub_process}
                      </span>
                  )}

                  {/* 2. NEW: Assembly Set Badge */}
                  {setIdentifier && (
                      <span style={{ 
                          marginLeft: '8px', 
                          padding: '2px 6px',
                          backgroundColor: '#fd7e14', // Orange/Orange-Red
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                      }}>
                          SET {setIdentifier}
                      </span>
                  )}

                  {/* 3. Obsolete Part Warning Badge */}
                  {isObsolete && (
                       <span style={{ 
                          marginLeft: '8px', 
                          padding: '2px 6px',
                          backgroundColor: '#dc3545', // Red
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                      }}>
                          ⚠️ Obsolete Part/Rev
                      </span>
                  )}

                  {/* 4. Pre-shipped Badge — this part has had qty carved into pre-ship slices */}
                  {job.pre_shipped_qty > 0 && (
                      <span style={{
                          marginLeft: '8px',
                          padding: '2px 6px',
                          backgroundColor: '#198754', // Green/success
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                      }}
                      title={`${job.pre_shipped_qty} pc(s) of this part pre-shipped`}>
                          <i className="bi bi-box-seam me-1"></i>Pre-shipped: {job.pre_shipped_qty}
                      </span>
                  )}

                  {/* 5. REMAKE Badge — this job cuts replacement parts for another
                      job's open assembly sets; pieces hand-deliver to that join
                      station and never pack (violet, matches the REMAKE slip). */}
                  {(() => {
                      let rmk = null;
                      try { rmk = job.remake_target_json ? JSON.parse(job.remake_target_json) : null; } catch (e) { rmk = null; }
                      if (!rmk) return null;
                      const station = rmk.station === 'Welding' || rmk.station === 'Fabrication'
                          ? 'Welding' : (rmk.station || 'Join');
                      return (
                          <span style={{
                              marginLeft: '8px',
                              padding: '2px 6px',
                              backgroundColor: '#5a5adb', // Violet — remake identity
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                          }}
                          title={`Replacement parts: deliver ${rmk.qty} pc(s) to Job #${rmk.job_num} at ${station} — do NOT pack`}>
                              <i className="bi bi-arrow-repeat me-1"></i>REMAKE → Job #{rmk.job_num} {station}
                          </span>
                      );
                  })()}

                  {/* 5b. REMAKE HOLD — this join set is waiting on replacement parts (jobq-api
                      _remake_hold_gate). Red: no replacement job exists, a manager must release it.
                      Amber: replacements are on the way; the welder confirms they are here. */}
                  {job.remake_hold && job.remake_hold.level !== 'info' && (
                      <span style={{
                          marginLeft: '8px',
                          padding: '2px 6px',
                          backgroundColor: job.remake_hold.level === 'block' ? '#dc3545' : '#ffc107',
                          color: job.remake_hold.level === 'block' ? 'white' : '#212529',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                      }}
                      title={job.remake_hold.message}>
                          {job.remake_hold.level === 'block' ? '⛔ WAITING ON REPLACEMENTS' : '⏳ REPLACEMENTS ON THE WAY'}
                      </span>
                  )}

                  {/* 6. Found-by-number chip — this row came from the exact job/task
                      lookup, not the 30-day queue. Plain words (a kiosk has no
                      hover for the tooltip). Not on Completed rows: the lookup
                      has no date cap at all, so with Show Completed on it also
                      returns work finished months AGO, which is not "future".
                      Dates go through formatDueDate: new Date("YYYY-MM-DD")
                      renders a day early in US time. */}
                  {job.found_beyond_window && job.task_status !== "Completed" && job.status !== "Completed" && (
                      <span style={{
                          marginLeft: '8px',
                          padding: '2px 6px',
                          backgroundColor: '#6c757d', // Grey — informational
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 'normal'
                      }}
                      title={`Found by job/task number — this work is outside the 30-day queue. Scheduled: ${formatDueDate(job.scheduled_date) || "no date"}. Due: ${formatDueDate(effectiveDue) || "no date"}.`}>
                          <i className="bi bi-calendar-range me-1"></i>Not in 30-day queue
                      </span>
                  )}
              </div>
          
              <div style={{ fontSize: "0.85rem", marginTop: "0.1rem" }}>
                  {isShapeStockLaser ? (
                      <span>
                          <strong>Quantity:</strong> {job.quantity || 0} &nbsp;|&nbsp; <strong>Task Qty:</strong> {job.remaining || 0}
                      </span>
                  ) : isLaser ? (
                      <span>
                          <strong>Material:</strong> {job.laserMaterialDesc || "(No Material)"} &nbsp;|&nbsp; <strong>Est:</strong> {job.laserEstimatedTime ? formatInterval(job.laserEstimatedTime) : "--:--"}
                      </span>
                  ) : (
                      <span>
                          Job #{job.master_job_num}: {job.job_description}
                      </span>
                  )}
              </div>

              <div style={{ marginTop: "0.2rem", fontSize: "0.85rem" }}>
                  <strong>Priority:</strong> {isMaintenance ? "Maint" : effectivePriority}
                  {effectivePriority === 1 && (
                      <span style={{ color: "red", fontSize: "1.2rem", marginLeft: "0.5rem" }}>
                          ‼️
                      </span>
                  )}
                  {isLaser ? (
                      <>
                          &nbsp;|&nbsp;
                          <strong>Workstation Status:</strong> {statusBadge(displayStatus)}
                          {displayStatus === 'Maintenance' && maintenanceReason && (
                              <span style={{ color: '#dc3545', marginLeft: '0.5rem', fontStyle: 'italic' }}>
                                  ⚙️ {maintenanceReason}
                              </span>
                          )}
                      </>
                  ) : (
                      effectiveDue && (
                          <>
                              &nbsp;|&nbsp;
                              <strong>Due:</strong> {formatDueDate(effectiveDue)}
                          </>
                      )
                  )}
              </div>

              {!isLaser && (
                  <div style={{ marginTop: "0.1rem", fontSize: "0.85rem" }}>
                      <strong>Workstation Status:</strong> {statusBadge(displayStatus)}
                      {/* Multi-op progress. This is where "the task is not finished
                          yet" is actually communicated to the floor: the stored
                          status between ops is Paused (so the station LED, the
                          overdue detector and the 5PM bumped-jobs report all behave),
                          and this chip says what that Paused actually means. */}
                      {job.op_count && (
                          <span
                              style={{
                                  display: "inline-block",
                                  marginLeft: "0.35rem",
                                  padding: "0.15rem 0.4rem",
                                  backgroundColor: "#5b2d90",
                                  borderRadius: "4px",
                                  color: "#fff",
                                  fontSize: "0.75rem",
                                  fontWeight: "bold",
                              }}
                              title={
                                  displayStatus === "Completed"
                                      ? `All ${job.op_count} operations complete.`
                                      : `Operation ${job.current_op || 1} of ${job.op_count}. The task is not finished until every operation is done.`
                              }
                          >
                              {displayStatus === "Completed"
                                  ? `All ${job.op_count} ops done`
                                  : `Op ${job.current_op || 1} of ${job.op_count}`}
                          </span>
                      )}
                      {isAwaitingNextOp(job) && (
                          <span style={{ color: '#5b2d90', marginLeft: '0.5rem', fontStyle: 'italic', fontWeight: 'bold' }}>
                              ⏭ Op {(job.current_op || 1) - 1} finished — awaiting {job.current_op_label || `Op ${job.current_op}`}
                          </span>
                      )}
                      {displayStatus === 'Maintenance' && maintenanceReason && (
                          <span style={{ color: '#dc3545', marginLeft: '0.5rem', fontStyle: 'italic' }}>
                              ⚙️ {maintenanceReason}
                          </span>
                      )}
                  </div>
              )}

              {/* PPAP PROGRESS BAR */}
              {hasPpapRequiredPart && (
                  <div style={{ marginTop: "5px" }}>
                      <div className="d-flex justify-content-between align-items-center" style={{fontSize: '0.75rem'}}>
                          <strong>Samples Collected: {samplesCollected}/{samplesRequired}</strong>
                          {samplesCollected >= samplesRequired ? (
                              <span className="text-success">✔ Complete</span>
                          ) : (
                              <span className="text-primary">In Progress</span>
                          )}
                      </div>
                      <div className="progress" style={{ height: "6px" }}>
                          <div
                              className={`progress-bar ${displayStatus === 'Paused' && maintenanceReason?.includes('QUALITY LOCK') ? 'bg-danger' : 'bg-success'}`}
                              role="progressbar"
                              style={{ width: `${Math.min((samplesCollected / samplesRequired) * 100, 100)}%` }}
                          ></div>
                      </div>
                      {/* Quality Stop Indicator */}
                      {displayStatus === 'Paused' && maintenanceReason?.includes('QUALITY LOCK') && (
                          <div className="text-danger fw-bold small mt-1">
                              🛑 QUALITY STOP: INSPECTION FAILED
                          </div>
                      )}
                  </div>
              )}
          </div>
      );
  }

  return (
    <div style={{ padding: "1rem", position: "relative" }}>
      {/* --- STATION LOCKED BANNER --- */}
      {stationLocked && (
        <div className="alert alert-danger d-flex align-items-center mb-3 shadow-sm" role="alert">
            <i className="bi bi-lock-fill fs-4 me-3"></i>
            <div>
                <h5 className="alert-heading m-0 fw-bold">STATION LOCKED</h5>
                <p className="mb-0">
                    Production is blocked. Required Maintenance: <strong>{lockReason}</strong>.
                    Complete the maintenance task to resume production.
                </p>
            </div>
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start", // Changed to flex-start to align tops
          marginBottom: "1rem",
        }}
      >
        {/* Left Side: Title */}
        <h2 style={{ margin: 0 }}>Job Queue - {workstation}</h2>

        {/* Right Side: Stacked Panels */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'stretch' }}>
            <StationStatusPanel userTime={userDailyTime} stationDailyState={stationDailyState} />
            
            {/* NEW: Inject Laser Consumables Panel here (below station stats) */}
            {workstation === "Laser" && <LaserConsumablesPanel />}
        </div>
      </div>

      {/* Quick controls for station, status, priority, search, scheduled date */}
      <div style={{ marginBottom: "1rem", display: "flex", gap: "1rem" }}>
        <div>
          <label>Station: </label>
          <select value={workstation} onChange={(e) => setWorkstation(e.target.value)}>
            {QUEUE_STATIONS.map((st) => <option key={st}>{st}</option>)}
          </select>
        </div>
        <div>
          <label>Status: </label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All</option>
            <option value="Pending">Pending</option>
            <option value="In Progress">In Progress</option>
            <option value="Paused">Paused</option>
            <option value="Maintenance">Maintenance</option>
          </select>
        </div>
        <div>
          <label>Priority: </label>
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
            <option value="all">All</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
          </select>
        </div>
        <div>
          <label>Scheduled Date: </label>
          <select value={filterScheduledDate} onChange={(e) => setFilterScheduledDate(e.target.value)}>
            <option value="">All</option>
            <option value="nodate">No Date</option>
            {allScheduledDates.map((dt) => (
              <option key={dt} value={dt}>
                {dt}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Customer: </label>
          <select value={filterCustomer} onChange={(e) => setFilterCustomer(e.target.value)}>
            <option value="all">All</option>
            {availableCustomers.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Sales Order: </label>
          {(() => {
            const s = salesOrderSearch.trim().toLowerCase();
            const matches = s
              ? activeSalesOrders.filter((so) => {
                  const on = String(so.order_number || "").toLowerCase();
                  const cn = String(so.customer_name || "").toLowerCase();
                  return on.includes(s) || cn.includes(s);
                })
              : activeSalesOrders;
            return (
              <>
                <input
                  type="text"
                  list="active-sales-orders"
                  placeholder="All"
                  value={salesOrderSearch}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSalesOrderSearch(v);
                    // Auto-select on exact match to "SO# — Customer" or "SO#".
                    // Also clear the filter if the input is empty OR if the
                    // partially-edited input no longer matches any known SO —
                    // otherwise the queue would keep filtering by the
                    // previously-selected id while the visible text reads
                    // like a search-in-progress.
                    const typed = v.trim().toLowerCase();
                    const exact = activeSalesOrders.find((so) => {
                      const label = `${so.order_number} — ${so.customer_name}`.toLowerCase();
                      return typed === label || typed === String(so.order_number).toLowerCase();
                    });
                    if (exact) setFilterSalesOrder(String(exact.sales_order_id));
                    else setFilterSalesOrder("all");
                  }}
                  style={{ width: "220px" }}
                />
                <datalist id="active-sales-orders">
                  {matches.slice(0, 200).map((so) => (
                    <option
                      key={so.sales_order_id}
                      value={`${so.order_number} — ${so.customer_name}`}
                    />
                  ))}
                </datalist>
                {filterSalesOrder !== "all" && (
                  <button
                    type="button"
                    onClick={() => {
                      setFilterSalesOrder("all");
                      setSalesOrderSearch("");
                    }}
                    title="Clear sales order filter"
                    style={{ marginLeft: "0.25rem" }}
                  >
                    ×
                  </button>
                )}
              </>
            );
          })()}
        </div>
        <div>
          <label>Search: </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              // A scanner types a traveler's whole link and presses Enter.
              if (e.key !== "Enter") return;
              const scanned = parseScanCode(searchTerm);
              if (scanned) {
                e.preventDefault();
                setSearchTerm("");
                navigate(scanPath(scanned));
              }
            }}
            placeholder="Job #, task #, part…"
          />
          {/* Lookup status. Only while the term is an exact job/task number.
              In order: still asking; the job/task IS here but the filters hid
              every row of it (otherwise the list just goes blank); the API is
              too old to look beyond 30 days (an empty answer proves nothing);
              or "nowhere at this station" (the lookup came back empty AND the
              local list has no such job/task either). A failed lookup says
              nothing — see the effect. Ticking Show Completed clears the
              search (the workstation effect), hence "search again". */}
          {searchNumber != null && (() => {
            let note = null;
            if (searchLookupStatus === "loading") {
              note = "Searching all dates…";
            } else if (searchHiddenMatches.length > 0) {
              const isDone = (r) => r.task_status === "Completed" || r.status === "Completed";
              let hint;
              if (searchHiddenMatches.every(isDone) && !showCompleted) {
                hint = "tick Show Completed, then search again";
              } else if (showReadyOnly && workstation !== "Laser") {
                hint = "untick Show Only Jobs Ready for Work";
              } else {
                hint = "check the Status, Priority, Date, Customer and Sales Order filters";
              }
              note = `Job/task #${searchNumber} is at ${workstation} but hidden by your filters (blocked, not ready, or completed) — ${hint}`;
            } else if (searchLookupStatus === "unsupported") {
              note = "Search beyond the 30-day queue isn't available yet";
            } else if (
              searchLookupStatus === "done" &&
              searchLookupJobs.length === 0 &&
              !jobs.some((j) => rowMatchesNumber(j, searchNumber)) &&
              // A digits-only term can still be part of a part number
              // (FAB308616): if the text search shows cards, don't say "no".
              filteredJobs.length === 0
            ) {
              note = showCompleted
                ? `No job or task #${searchNumber} at ${workstation}`
                : `No open job or task #${searchNumber} at ${workstation} — tick Show Completed to include finished work, then search again`;
            }
            return note ? (
              <span style={{ marginLeft: "0.5rem", fontSize: "0.8rem", color: "#6c757d", fontStyle: "italic" }}>
                {note}
              </span>
            ) : null;
          })()}
        </div>
      </div>
        {workstation === "Fabrication" && (
          <div>
            <label>Sub-Process: </label>
            <select value={filterSubProcess} onChange={(e) => setFilterSubProcess(e.target.value)}>
              <option value="all">All</option>
              {availableSubProcesses.map((sp) => (
                <option key={sp} value={sp}>
                  {sp}
                </option>
              ))}
            </select>
          </div>
        )}
      <div>
        <label>
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={() => setShowCompleted((prev) => !prev)}
            style={{ marginRight: "4px" }}
          />
          Show Completed
        </label>
      </div>
      <div style={{ marginTop: '0.5rem' }}>
        <label>
            <input
                type="checkbox"
                checked={showReadyOnly}
                onChange={() => setShowReadyOnly(prev => !prev)}
                style={{ marginRight: '4px' }}
            />
            Show Only Jobs Ready for Work
        </label>
      </div>
      <div style={{ marginTop: '0.5rem' }}>
        <label>
            <input
                type="checkbox"
                checked={showPreShippedOnly}
                onChange={() => setShowPreShippedOnly(prev => !prev)}
                style={{ marginRight: '4px' }}
            />
            <i className="bi bi-box-seam me-1" style={{ color: '#198754' }}></i>
            Show Only Pre-Shipped
        </label>
      </div>

      {error && (
        <div
          style={{
            backgroundColor: "var(--jq-alert-danger-bg, #f8d7da)",
            color: "var(--jq-alert-danger-text, #721c24)",
            padding: "0.5rem",
            marginBottom: "1rem",
            borderRadius: "4px",
          }}
        >
          {error}
        </div>
      )}
      {loading && <div>Loading...</div>}

      {/* MODIFIED: Two-column Flexbox layout with reverted styles */}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', alignItems: 'flex-start' }}>

        {/* Column 1: Job List (Reverted Appearance) */}
        <div style={{ flex: '1 1 30%' }}>
          {!loading && (
            <div
              style={{
                maxHeight: "810px",
                overflowY: "auto",
                marginBottom: "1rem",
                border: "1px solid var(--jq-border-medium, #ccc)",
                borderRadius: "6px",
                padding: "0.5rem",
                backgroundColor: "var(--jq-bg-card, #fefefe)",
              }}
            >
              {filteredJobs.map((job, index) => renderJobItem(job, index))}
            </div>
          )}
        </div>

        {/* Column 2: Job Detail View */}
        <div style={{ flex: '1 1 70%' }}>
          {selectedJobDetail && (
            <div ref={detailRef}>
              <DetailView
                jobDetail={selectedJobDetail}
                workstation={workstation}
                nextAllowedJobId={nextPendingId}
                selectedTaskId={selectedTaskId}
                cutSheetDetail={cutSheetDetail}
                materialInfo={materialInfo}
                dropMaterialInfo={dropMaterialInfo}
                customers={customers}
                jobParts={jobParts}
                reloadJob={() => handleSelectJob(selectedJobDetail, { silent: true })}
                onEnlarge={setEnlargedImageUrl}
                activityLog={activityLog}
                clearSelectedJobDetail={handleDeselectJob}
                isSubmitting={isSubmitting}
                setIsSubmitting={setIsSubmitting}
                // NEW PPAP PROPS for JobQueuePage state management
                setShowPpapModal={setShowPpapModal}
                setPpapPartData={setPpapPartData}
                setPpapTaskId={setPpapTaskId}
                setPpapPhase={setPpapPhase}
                faiStatus={faiStatus}
                setShowFaiOverrideModal={setShowFaiOverrideModal}
                isStationLocked={stationLocked}
                lockReason={lockReason}
                pendingMaintenanceToday={pendingMaintenanceToday}
                queueRowSaysTm={queueRowSaysTm(
                  jobs.find((j) => j.job_id === selectedJobDetail.job_id)
                  || searchLookupJobs.find((j) => j.job_id === selectedJobDetail.job_id))}
              />
            </div>
          )}
        </div>
      </div>

      {/* Consumables drawer */}
      <div style={{ marginTop: "2rem" }}>
        <ConsumablesDrawer workstation={workstation} />
      </div>

      {/* Enlarged image modal if any */}
      {enlargedImageUrl && (
        <EnlargeModal url={enlargedImageUrl} onClose={() => setEnlargedImageUrl(null)} />
      )}
      
      {/* NEW: PPAP Modal Integration */}
      {showPpapModal && ppapPartData && ppapTaskId && (
          <PpapMeasurementModal
              isOpen={showPpapModal}
              onClose={() => setShowPpapModal(false)}
              jobId={selectedJobId}
              taskId={ppapTaskId}
              partData={ppapPartData}
              samplePhase={ppapPhase}
              ongoingSampleSize={ppapPartData.ppap_sample_size_ongoing || 3}
              initialSampleSize={ppapPartData.ppap_sample_size_initial || 1}
              ppapImageUrl={ppapPartData.ppap_image_path}
              reloadJob={() => handleSelectJob({ job_id: selectedJobId, task_id: ppapTaskId }, { silent: true })} // Force reload the job/task details
          />
      )}
      {/* END NEW: PPAP Modal Integration */}

      {/* NEW: FAI Admin Override Modal */}
      {showFaiOverrideModal && selectedTaskId && (
          <FaiAdminOverrideModal
              isOpen={showFaiOverrideModal}
              taskId={selectedTaskId}
              onClose={() => setShowFaiOverrideModal(false)}
              onSuccess={() => {
                  setShowFaiOverrideModal(false);
                  // FAI status effect will refetch via showFaiOverrideModal dep
              }}
          />
      )}
    </div>
  );
}


/**
 * DefectsList - new component to show and allow editing/deleting defects.
 */
function DefectsList({ jobId, onEdit, onDelete, isSubmitting }) {
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    setLoading(true);
    loadJobDefects(jobId).then((data) => {
      if (cancelled) return;
      setDefects(data);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [jobId]);

  if (loading) return <div>Loading defects...</div>;

  return (
    <div style={{ marginTop: "2rem" }}>
      <h5>Defects</h5>
      {defects.length === 0 ? (
        <p>No defects recorded for this job.</p>
      ) : (
        <table className="table table-sm table-bordered">
          <thead>
            <tr>
              <th>Product ID</th>
              <th>User</th>
              <th>Reason</th>
              <th>Quantity</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {defects.map(defect => (
              <tr key={defect.defect_id}>
                <td>{defect.product_id || 'N/A'}{isConsumableDefect(defect) && (<span className="badge bg-secondary ms-2" title="Purchased component — this defect moved consumable stock">component</span>)}</td>
                <td>{defect.recorded_by_name || 'N/A'}</td>
                <td>{defect.defect_reason}</td>
                <td>{defect.defective_quantity}</td>
                <td>
                  <button onClick={() => onEdit(defect)} disabled={isSubmitting} style={{ marginRight: '5px' }}>Edit</button>
                  <button onClick={() => onDelete(defect)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * EditDefectModal - new component for the defect editing form.
 */
function StagedDefectsList({ jobId, onEdit, onDelete, isSubmitting, reloadKey }) {
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE}/pending_defects?job_id=${jobId}`)
      .then(async res => {
        if (cancelled) return;
        if (!res.ok) {
          console.error(`Error fetching staged defects: HTTP ${res.status}`);
          setDefects([]);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        setDefects(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        if (cancelled) return;
        console.error("Error fetching staged defects:", err);
        setDefects([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [jobId, reloadKey]);

  if (loading) return <div>Loading staged defects...</div>;
  if (defects.length === 0) return null;

  return (
    <div style={{ marginTop: "2rem", border: "1px solid #ffc107", borderRadius: "4px", padding: "0.75rem", backgroundColor: "rgba(255, 193, 7, 0.08)" }}>
      <h5 style={{ marginTop: 0 }}>
        Staged at Laser
        <span style={{ fontSize: "0.8rem", fontWeight: "normal", marginLeft: "0.5rem", color: "#856404" }}>
          — commits when laser task completes
        </span>
      </h5>
      <table className="table table-sm table-bordered" style={{ marginBottom: 0 }}>
        <thead>
          <tr>
            <th>Product ID</th>
            <th>User</th>
            <th>Reason</th>
            <th>Quantity</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {defects.map(defect => (
            <tr key={defect.pending_defect_id}>
              <td>{defect.product_id || 'N/A'}</td>
              <td>{defect.recorded_by_name || 'N/A'}</td>
              <td>{defect.defect_reason}</td>
              <td>{defect.defective_quantity}</td>
              <td>
                <button onClick={() => onEdit(defect)} disabled={isSubmitting} style={{ marginRight: '5px' }}>Edit</button>
                <button onClick={() => onDelete(defect)} disabled={isSubmitting}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EditDefectModal({ defect, onSave, onClose, isSubmitting, title = "Edit Defect" }) {
  const [quantity, setQuantity] = useState(defect.defective_quantity);
  const [reason, setReason] = useState(defect.defect_reason);

  const handleSave = () => {
    const numQuantity = parseInt(quantity, 10);
    if (isNaN(numQuantity) || numQuantity < 1) {
      alert("Defective quantity must be a positive number.");
      return;
    }

    onSave({
      ...defect,
      defective_quantity: numQuantity,
      defect_reason: reason,
    });
  };

  return (
    <div className="modal" style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      justifyContent: 'center', alignItems: 'center'
    }}>
      <div className="modal-content" style={{
        backgroundColor: 'var(--jq-bg-card, white)', padding: '20px', borderRadius: '5px'
      }}>
        <h4>{title}</h4>
        <label>Defective Quantity</label>
        <input
          type="number"
          value={quantity}
          onChange={e => setQuantity(e.target.value)}
          style={{ width: '100%', padding: '8px', marginBottom: '10px' }}
        />
        <label>Defect Reason</label>
        <input
          type="text"
          value={reason}
          onChange={e => setReason(e.target.value)}
          style={{ width: '100%', padding: '8px', marginBottom: '10px' }}
        />
        <button onClick={handleSave} disabled={isSubmitting} style={{ marginRight: '10px' }}>Save</button>
        <button onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/**
 * SheetTrackingModal - For capturing PO/Shipment data per sheet
 */
export function SheetTrackingModal({ isOpen, onClose, onSave, initialPo, initialShipment, isSubmitting, taskId,
                                    dockPollMs = DOCK_POLL_MS, dockSlowPollMs = DOCK_SLOW_POLL_MS,
                                    dockStaleMs = DOCK_STALE_MS, dockIdleMs = DOCK_IDLE_MS,
                                    // The laser camera's last answer (LaserDetailSection polls it): its line at the top.
                                    camera = null }) {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [poNumber, setPoNumber] = useState(initialPo || "");
  const [shipmentId, setShipmentId] = useState(initialShipment || "");
  const [pin, setPin] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [isExempt, setIsExempt] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  // "No Tracking" when every sheet / piece in stock is on a lot: a manager's PIN + reason (2026-10-20).
  const [managerPin, setManagerPin] = useState("");
  const [managerReason, setManagerReason] = useState("");
  // The API refused a sheet with no lot (No Tracking, or a PO + Shipment naming no single lot): its message.
  const [managerNeeded, setManagerNeeded] = useState(null);
  // Local in-flight guard: parent's handleSheetModalSave doesn't toggle
  // the isSubmitting prop, so a fast double-click hits the API twice
  // before the modal closes. Ref blocks synchronously (refs update before
  // re-render); state drives the disabled/"Saving..." UI.
  const submittingRef = useRef(false);
  const [localBusy, setLocalBusy] = useState(false);
  // MTN sheet context + notes/warnings for the entered tracking number.
  // NOTE: these hooks MUST stay above the `if (!isOpen) return null` early
  // return below — an early return before a hook fails the Amplify CRA build
  // (react-hooks/rules-of-hooks).
  const [mtnContext, setMtnContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [noteAck, setNoteAck] = useState(false);
  const [diverting, setDiverting] = useState(false);
  // What tracking applies to this task. For a REMNANT the API names the lot(s) its tag
  // can show (the sheet it was cut from); the one lot on record is pre-filled.
  const [trackInfo, setTrackInfo] = useState(null);
  // Pallet beacons plugged into the laser Pi (checked in at the laser). One pallet fills the
  // tracking number in; several are offered as chips. The API's own material and depleted-lot
  // checks still judge whatever ends up in the box.
  const beaconsOn = useFeatures().palletBeacons.enabled;
  const [dockedAtLaser, setDockedAtLaser] = useState([]);
  const [filledFromBeacon, setFilledFromBeacon] = useState(null);
  // Docked mode (Mike, 2026-10-13): a pallet plugged in at the laser IS the answer, so the
  // popup asks for the PIN only. "Cutting from a different pallet?" (manualEntry) brings the
  // tracking number box back; nothing docked = the popup exactly as before.
  const [manualEntry, setManualEntry] = useState(false);
  const [unassignedDocked, setUnassignedDocked] = useState([]);   // plugged in, on no live lot
  // The laser dock decides the lot (jobq-api laser dock rule, 2026-10-21): GET /laser/{id}/dock_check
  // on open and every 2 s. Enforced (mode enforce + required): the docked pallet IS the sheet's lot,
  // anything else is refused, and only a manager can override (dead dock / dead beacon). Mode off:
  // the card is advice. No endpoint / no `required`: the dialog exactly as before.
  const dock = useLaserDockCheck(taskId, Boolean(isOpen), {
    pollMs: dockPollMs, slowMs: dockSlowPollMs, staleMs: dockStaleMs, idleMs: dockIdleMs });
  const enforced = dockEnforced(dock.check);
  const [dockOverrideOpen, setDockOverrideOpen] = useState(false);
  const [dockManagerPin, setDockManagerPin] = useState("");
  const [dockReason, setDockReason] = useState("");
  const [dockNoLot, setDockNoLot] = useState(false);       // override: "This piece has no tracking number"
  const [dockSaveMsg, setDockSaveMsg] = useState(null);   // the save's 409 dock_required message
  // The laser camera records sheet 2+ (jobq-api "LASER CAMERA SHEETS", 2026-11-03): the save's 409
  // camera_records_sheets is information (`cameraInfo`), with a manager override when the API offers one. The
  // override re-sends the request that was refused (`lastSaveRef`) with camera_override_pin / _reason; while its
  // box is open, Confirm & Add carries them too (independent of the dock override: a request can carry both).
  // The modal unmounts on close, so a manager PIN never carries to the next sheet.
  const [cameraInfo, setCameraInfo] = useState(null);
  const [cameraOverrideOpen, setCameraOverrideOpen] = useState(false);
  const [cameraPin, setCameraPin] = useState("");
  const [cameraReason, setCameraReason] = useState("");
  const [cameraError, setCameraError] = useState(null);
  const lastSaveRef = useRef(null);
  // The number the operator typed / scanned into the box themselves (not a fill-in): under the dock rule
  // the dialog says when it is not the lot used.
  const [typedTrk, setTypedTrk] = useState("");
  // The lot whose notes are loaded (mtnContext); under the dock rule Confirm waits for the lot it sends.
  const [ctxLot, setCtxLot] = useState(null);
  const pinRef = useRef(null);
  // Face sign-in (src/face, 2026-10-15): on a live station the camera names the operator while
  // they fill the sheet in, and the face token stands in for the PIN when they press Add (the API
  // takes it like a typed PIN). Typing a PIN, or "Not you?", ends it for this popup.
  const face = useFacePin(Boolean(isOpen) && Boolean(getStationKey()));
  const faceMatch = face.phase === "matched" ? face.match : null;
  const faceMatchedAtRef = useRef(0);
  useEffect(() => { if (faceMatch) faceMatchedAtRef.current = Date.now(); }, [faceMatch]);
  // The fill-in lands after two round trips; by then the operator may have chosen
  // "PO + Shipment" or "no tracking", or typed a number. Read the CURRENT choice, never the one at start.
  const entryModeRef = useRef({ isExempt: false, showLegacy: false, typed: false, overrideOpen: false });
  entryModeRef.current = { isExempt, showLegacy, typed: trackingNumber !== "", overrideOpen: dockOverrideOpen };
  const seedDock = dock.seed;

  useEffect(() => {
    if (!taskId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/laser/${taskId}/tracking_required`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        setTrackInfo(data);
        // Its `dock` block (the dock check's answer) seeds the dialog until the first dock_check answers.
        if (data && data.dock && typeof data.dock === "object") seedDock(data.dock);
        const prefill = defaultRemnantTrackingNumber(data);
        // Never into the manager override's box (L8: the manager types that one).
        if (prefill && !entryModeRef.current.overrideOpen) setTrackingNumber((cur) => cur || prefill);
      } catch (_) {
        // Advisory only: the popup works exactly as before without it.
      }
    })();
    return () => { cancelled = true; };
  }, [taskId, seedDock]);

  useEffect(() => {
    // A remnant's lot is on its tag (pre-filled above). Since 2026-10-14 a remnant has lots of its
    // own and one can carry a beacon: a docked pallet is the answer only when it is one of THIS
    // remnant's own lots (never its parent sheet's stack). Nor when the material has no lot to track.
    const remnantOwnIds = trackInfo && trackInfo.source === "remnant"
      ? new Set((trackInfo.candidate_mtns || []).filter((m) => m && m.own_lot).map((m) => String(m.mtn_id)))
      : null;
    if (!beaconsOn || !trackInfo || trackInfo.tracking_required === false
        || (remnantOwnIds && remnantOwnIds.size === 0)) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const r = await dockedBeacons("Laser");
      if (cancelled || !r.ok || !r.body) return;
      const lots = dockedLots(r.body.beacons)
        .filter((b) => !remnantOwnIds || remnantOwnIds.has(String(b.lot.mtn_id)));
      setDockedAtLaser(lots);
      const onLots = new Set(lots.map((b) => b.beacon_id));
      setUnassignedDocked((Array.isArray(r.body.beacons) ? r.body.beacons : [])
        .filter((b) => b && b.beacon_id && !onLots.has(b.beacon_id) && (!b.lot || b.lot_done) && b.in_range !== false)
        .map((b) => b.beacon_id));
      const prefill = remnantOwnIds
        ? (lots.length === 1 ? String(lots[0].lot.mtn_id) : null)
        : dockedPrefill(r.body.beacons);
      const mode = entryModeRef.current;
      if (prefill && !mode.isExempt && !mode.showLegacy && !mode.typed && !mode.overrideOpen) {
        setTrackingNumber(prefill);
        // Docked mode lasts only while the box still holds this pallet's number.
        setFilledFromBeacon(lots[0].beacon_id);
      }
    })();
    return () => { cancelled = true; };
  }, [beaconsOn, trackInfo]);

  // The lot this sheet goes on: the box's number, or (dock enforced, no override) the docked pallet's,
  // or (the override with "no tracking number") none.
  const dockLotId = enforced && !dockOverrideOpen ? dockLotToSend(dock.check) : null;
  const lotText = enforced
    ? (dockOverrideOpen ? (dockNoLot ? "" : trackingNumber) : (dockLotId ? String(dockLotId) : ""))
    : trackingNumber;

  // Debounced lookup of the tracking number → lot identity + active warnings.
  useEffect(() => {
    if (isExempt || showLegacy) { setMtnContext(null); setNoteAck(false); setCtxLot(null); return; }
    const mtnId = parseInt(lotText, 10);
    if (!lotText || isNaN(mtnId) || mtnId <= 0) { setMtnContext(null); setNoteAck(false); setCtxLot(null); return; }
    // Under the dock rule the lot can change under the operator (another pallet docked): the last lot's
    // notes and acknowledgement go at once, never shown (or counted) for the new one (M1).
    if (enforced) { setMtnContext(null); setNoteAck(false); setCtxLot(null); }
    let cancelled = false;
    setContextLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/material_tracking/${mtnId}/sheet_context${taskId ? `?task_id=${taskId}` : ""}`);
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) { setMtnContext(data); setNoteAck(false); setCtxLot(String(mtnId)); }
        } else if (!cancelled) {
          setMtnContext(null);
          setCtxLot(String(mtnId));          // looked up: nothing to show (as today: a failed read blocks nothing)
        }
      } catch (_) {
        if (!cancelled) { setMtnContext(null); setCtxLot(String(mtnId)); }
      } finally {
        if (!cancelled) setContextLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [lotText, isExempt, showLegacy, taskId, enforced]);

  // A refusal is about the entry it answered: a different entry (mode, TRK, PO, Shipment) asks the API again.
  useEffect(() => { setManagerNeeded(null); }, [isExempt, showLegacy, trackingNumber, poNumber, shipmentId]);

  // The docked pallet whose number is in the box, while the number came from the dock.
  const dockedLot = filledFromBeacon && !manualEntry
    ? dockedAtLaser.find((b) => b.beacon_id === filledFromBeacon
        && String(b.lot.mtn_id) === String(parseInt(trackingNumber, 10))) || null
    : null;
  // A remnant only reaches here with its OWN lot docked (the effect above filters to those).
  // While the dock rule is enforced the dock check decides instead (its own card below).
  const dockedMode = !!dockedLot && !isExempt && !showLegacy && !enforced;
  // Enforced: the dock's ok counts only while it is fresh (L2).
  const dockOk = enforced && dockAllowsSave(dock.check) && !dock.stale;

  useEffect(() => {
    // Never while a manager override is open: the manager is typing there (L4).
    if ((dockedMode || dockOk) && !dockOverrideOpen && !cameraOverrideOpen && pinRef.current) pinRef.current.focus();
  }, [dockedMode, dockOk, dockOverrideOpen, cameraOverrideOpen]);

  // Enforced: No Tracking and PO + Shipment are not offered, so a choice made before the check arrived goes.
  useEffect(() => {
    if (enforced) { setIsExempt(false); setShowLegacy(false); }
  }, [enforced]);

  // Mode off (advice): today's docked fill-in is read once, the card every 2 s. When the card's docked lot
  // is no longer the filled-in one (a pallet swapped), the fill-in follows the card, so what is sent
  // matches what the card says (M2). Only a number the dock filled in is touched, never a typed one.
  const adviceOn = dockAdvice(dock.check);
  const adviceLot = adviceOn ? dockLotToSend(dock.check) : null;
  const adviceDockedId = dockedLot ? Number(dockedLot.lot.mtn_id) : null;
  useEffect(() => {
    if (!adviceOn || adviceDockedId === null || adviceLot === adviceDockedId) return;
    const p = adviceLot !== null
      ? ((dock.check && Array.isArray(dock.check.pallets) ? dock.check.pallets : [])
          .find((x) => x && Number(x.mtn_id) === adviceLot) || null)
      : null;
    if (adviceLot !== null) {
      setTrackingNumber(String(adviceLot));
      const known = p && dockedAtLaser.some((b) => b.beacon_id === p.beacon_id && Number(b.lot.mtn_id) === adviceLot);
      setFilledFromBeacon(known ? p.beacon_id : null);
    } else {
      setFilledFromBeacon(null);
      setTrackingNumber("");
    }
  }, [adviceOn, adviceLot, adviceDockedId, dock.check, dockedAtLaser]);

  // The lot that will be sent under the dock rule, and whether its notes are loaded yet (M1).
  const dockLotSent = enforced
    ? (dockOverrideOpen ? (dockNoLot ? null : (parseInt(trackingNumber, 10) > 0 ? parseInt(trackingNumber, 10) : null))
       : dockLotId)
    : null;
  const dockNotesPending = dockLotSent !== null && ctxLot !== String(dockLotSent);

  if (!isOpen) return null;

  const busy = isValidating || isSubmitting || localBusy;
  // Manager PIN + reason for a sheet with no lot: No Tracking on a material whose every sheet is on a lot
  // (tracking_required said so, or the API refused it), or a PO + Shipment the API could not map to a lot.
  const managerBoxShown = (isExempt && noTrackingNeedsManager(trackInfo)) || !!managerNeeded;

  // What a save that did not add the sheet answered (handleSheetModalSave): each refusal in its own place.
  const takeSaveAnswer = (r) => {
    if (!r) return;
    if (r.needsManager) setManagerNeeded(r.needsManager);
    if (r.dockRefused) {
      // The pallet changed between the check and the save: say why and look at the dock again now.
      setDockSaveMsg(r.dockRefused.message);
      dock.recheck();
    }
    if (r.cameraRefused) {
      // The camera records this sheet: information, not an error. No override offered (the camera just
      // recorded it): the override box goes.
      setCameraInfo(r.cameraRefused);
      if (!r.cameraRefused.override) setCameraOverrideOpen(false);
    }
    if (r.cameraOverrideError) setCameraError(r.cameraOverrideError);
  };
  const cameraWhat = cameraInfo && cameraInfo.override ? cameraInfo.override.what : null;
  // The camera override's fields, or null after saying in its box what is missing (nothing is sent).
  const cameraOverrideToSend = () => {
    const ov = cameraOverrideFields(cameraPin, cameraReason);
    if (ov.error) { setCameraError(ov.error); return null; }
    return { cameraOverridePin: ov.cameraOverridePin, cameraOverrideReason: ov.cameraOverrideReason,
             cameraOverrideWhat: cameraWhat };
  };
  // "Record the missed sheet": the request the camera refused, sent again with the manager's PIN + reason.
  const handleCameraOverride = async () => {
    if (submittingRef.current || busy) return;
    setCameraError(null);
    const base = lastSaveRef.current;
    if (!base) return;
    const cameraOv = cameraOverrideToSend();
    if (!cameraOv) return;
    setDockSaveMsg(null);
    submittingRef.current = true;
    setLocalBusy(true);
    try {
      takeSaveAnswer(await onSave({ ...base, ...cameraOv }));
    } finally {
      submittingRef.current = false;
      setLocalBusy(false);
    }
  };
  const openCameraOverride = () => { setCameraOverrideOpen(true); setCameraError(null); };
  const cancelCameraOverride = () => {
    setCameraOverrideOpen(false); setCameraPin(""); setCameraReason(""); setCameraError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submittingRef.current || busy) return;
    // A refused save's message stays until the operator tries again (L3).
    setDockSaveMsg(null);
    setCameraError(null);

    // 1. PIN is always required (a face match stands in for it; a typed PIN always wins)
    let operatorPin = pin;
    if (!operatorPin && faceMatch) {
      if (Date.now() - faceMatchedAtRef.current > FACE_TOKEN_USE_WITHIN_MS) {
        face.retry();
        alert("Face sign-in timed out. Look at the camera again, or type your PIN.");
        return;
      }
      operatorPin = faceMatch.token;
    }
    if (!operatorPin) {
      alert("Operator PIN is required.");
      return;
    }

    // 1a. Under the dock rule, the lot's notes must be loaded for exactly the lot that will be sent (M1).
    if (dockNotesPending) {
      alert("Checking this pallet's notes: try again in a moment.");
      return;
    }

    // 1b. A critical "do not use" warning on this sheet must be acknowledged first
    const criticalNow = !!(mtnContext && (mtnContext.notes || []).some(n => n.applies_now && n.severity === "critical"));
    if (criticalNow && !noteAck) {
      alert("Please acknowledge the critical warning before adding this sheet (or use “Set aside / Divert this sheet”).");
      return;
    }

    // 1c. The camera override's box is open: its manager PIN + reason ride on this save (said before sending).
    const cameraOv = cameraOverrideOpen ? cameraOverrideToSend() : null;
    if (cameraOverrideOpen && !cameraOv) return;

    // 2. Validate inputs before locking the button
    let payload;
    let override = null;
    if (enforced) {
      // The dock decides: the docked pallet's lot, or a manager's override with a typed tracking number.
      if (dockOverrideOpen) {
        const ov = dockOverrideFields(dockManagerPin, dockReason, trackingNumber, { noLot: dockNoLot });
        if (ov.error) { alert(ov.error); return; }
        // No lot = "No Tracking Info Available"'s shape; the API takes the one manager approval for both rules.
        payload = { poNumber: ov.mtnId ? "" : "N/A", shipmentId: 0, pin: operatorPin, mtnId: ov.mtnId,
                    dockOverridePin: ov.dockOverridePin, dockOverrideReason: ov.dockOverrideReason };
      } else {
        const lot = dockOk ? dockLotToSend(dock.check) : null;
        if (!lot) {
          alert((dock.check && dock.check.message) || "Dock the beacon of the pallet you're cutting.");
          return;
        }
        payload = { poNumber: "", shipmentId: 0, pin: operatorPin, mtnId: lot };
      }
    } else if (managerBoxShown) {
      const ov = noTrackingOverride(managerPin, managerReason);
      if (ov.error) { alert(ov.error); return; }
      override = { managerPin: ov.managerPin, managerReason: ov.managerReason };
    }
    if (payload) {
      // set above (the dock rule)
    } else if (isExempt) {
      payload = { poNumber: "N/A", shipmentId: 0, pin: operatorPin, mtnId: null, ...(override || {}) };
    } else if (trackingNumber) {
      const mtnId = parseInt(trackingNumber, 10);
      if (isNaN(mtnId) || mtnId <= 0) {
        alert("Please enter a valid tracking number.");
        return;
      }
      payload = { poNumber: "", shipmentId: 0, pin: operatorPin, mtnId, ...(override || {}) };
    } else {
      if (!poNumber || !shipmentId) {
        alert("Enter a Tracking Number, or PO Number + Shipment ID.");
        return;
      }
      payload = null; // legacy path validates server-side first
    }

    submittingRef.current = true;
    setLocalBusy(true);
    try {
      if (payload) {
        lastSaveRef.current = payload;
        takeSaveAnswer(await onSave({ ...payload, ...(cameraOv || {}) }));
        return;
      }

      // Legacy PO+Shipment path: server-side validation, then save.
      setIsValidating(true);
      try {
        const res = await fetch(`${API_BASE}/purchase_orders/validate_shipment?po_number=${poNumber}&shipment_id=${shipmentId}&task_id=${taskId}`);
        if (!res.ok) throw new Error("Validation check failed.");
        const data = await res.json();
        if (!data.valid) {
          alert(`Validation Error:\n\n${data.message || "Invalid PO/Shipment combination."}`);
          return;
        }
        const legacy = { poNumber, shipmentId, pin: operatorPin, mtnId: null, ...(override || {}) };
        lastSaveRef.current = legacy;
        takeSaveAnswer(await onSave({ ...legacy, ...(cameraOv || {}) }));
      } catch (err) {
        alert("Error validating: " + err.message);
      } finally {
        setIsValidating(false);
      }
    } finally {
      submittingRef.current = false;
      setLocalBusy(false);
    }
  };

  // Set aside / divert the current physical sheet (e.g. an out-of-spec piece).
  // Advances the lot's sheet position and resolves the firing note. Uses the
  // PIN already typed into the modal — no second prompt.
  const handleDivert = async (noteId) => {
    const mtnId = parseInt(lotText, 10);
    if (isNaN(mtnId) || mtnId <= 0) return;
    if (!pin) { alert("Operator PIN is required to set a sheet aside."); return; }
    const reason = window.prompt("Optional note about why this sheet is being set aside:", "") || "";
    setDiverting(true);
    try {
      const res = await fetch(`${API_BASE}/material_tracking/${mtnId}/divert_sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, note_id: noteId || null, reason }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        alert(`Divert failed: ${detailToString(body.detail) || res.statusText}`);
        return;
      }
      // Re-fetch context so the sheet position advances and the warning clears.
      const ctxRes = await fetch(`${API_BASE}/material_tracking/${mtnId}/sheet_context${taskId ? `?task_id=${taskId}` : ""}`);
      if (ctxRes.ok) { setMtnContext(await ctxRes.json()); setNoteAck(false); }
    } catch (err) {
      alert("Error setting sheet aside: " + err.message);
    } finally {
      setDiverting(false);
    }
  };

  const isRemnantTask = !!(trackInfo && trackInfo.source === "remnant");
  const remnantLots = isRemnantTask && Array.isArray(trackInfo.candidate_mtns) ? trackInfo.candidate_mtns : [];
  const tagLine = describeTagContext(mtnContext);
  const tagLineColor = !tagLine ? undefined
    : tagLine.tone === "error" ? "#b02a37"
    : tagLine.tone === "warn" ? "#997404"
    : tagLine.tone === "remnant" ? "#0a58ca"
    : "var(--jq-text-muted, #666)";
  const activeNotes = (mtnContext && Array.isArray(mtnContext.notes)) ? mtnContext.notes : [];
  const nowNotes = activeNotes.filter(n => n.applies_now);
  const upcomingNotes = activeNotes.filter(n => n.applies_upcoming);
  const hasCriticalNow = nowNotes.some(n => n.severity === "critical");
  // Enforced dock: Confirm & Add needs the dock's (fresh) ok, or the manager override, and the notes of the
  // lot it sends. The button says why it is held back (L5).
  const dockBlocked = enforced && !dockOverrideOpen && !dockOk;
  const confirmDisabled = busy || (hasCriticalNow && !noteAck) || dockBlocked || dockNotesPending;
  const dockHeldLabel = dockBlocked
    ? dockBlockedLabel(dock.check, { stale: dock.stale && dockAllowsSave(dock.check) })
    : (dockNotesPending ? "Checking this pallet's notes…" : null);
  const dockCardShown = enforced || adviceOn || !!dockSaveMsg;
  // L6: a number the operator typed before the dock decided is not used; say so (and which lot is).
  const typedLot = parseInt(typedTrk, 10) > 0 ? parseInt(typedTrk, 10) : null;
  const typedNote = enforced && !dockOverrideOpen && typedLot !== null && typedLot !== dockLotId
    ? (dockLotId
        ? `You entered TRK ${trkText(typedLot)}: this sheet goes on the docked pallet, TRK ${trkText(dockLotId)}.`
        : `You entered TRK ${trkText(typedLot)}: dock that pallet's beacon (a typed number isn't used).`)
    : null;
  // L4: with the override open, the card says what the save records.
  const overrideNote = enforced && dockOverrideOpen
    ? (dockNoLot
        ? "The override is on: this sheet is recorded with no tracking number, not against the docked pallet."
        : (dockLotSent
            ? `The override is on: this sheet is recorded against TRK ${trkText(dockLotSent)} (typed below), not the docked pallet.`
            : "The override is on: this sheet is recorded against the tracking number typed below, not the docked pallet."))
    : null;
  const openDockOverride = () => {
    setDockOverrideOpen(true);
    setManualEntry(true); setFilledFromBeacon(null); setTrackingNumber(""); setDockNoLot(false);
  };
  const cancelDockOverride = () => {
    setDockOverrideOpen(false); setDockManagerPin(""); setDockReason(""); setTrackingNumber(""); setDockNoLot(false);
    setTypedTrk("");
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000
    }}>
      <div style={{ backgroundColor: "var(--jq-bg-card, #fff)", padding: "20px", borderRadius: "8px", width: "380px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}
           onInputCapture={dock.activity} onFocusCapture={dock.activity}
           onPointerDownCapture={dock.activity} onKeyDownCapture={dock.activity}>
        <h4>Add Sheet & Track Material</h4>
        <form onSubmit={handleSubmit}>

          {/* The laser camera: what it is doing (the poll), or why this save did not add the sheet (the 409) */}
          {cameraInfo ? (
            <div data-testid="camera-refused" role="status" style={{
              marginBottom: "12px", padding: "8px 10px", borderRadius: "6px", fontSize: "0.9rem",
              backgroundColor: "#e7f1ff", border: "1px solid #9ec5fe", color: "#052c65",
            }}>
              <div><span aria-hidden="true">📷 </span>{cameraInfo.message}</div>
              {cameraInfo.override && !cameraOverrideOpen && (
                <button
                  type="button"
                  onClick={openCameraOverride}
                  disabled={busy}
                  style={{ background: "none", border: "none", padding: 0, marginTop: "6px", color: "#0a58ca", cursor: "pointer", fontSize: "0.85rem", textDecoration: "underline" }}
                >
                  Camera missed a sheet: manager override
                </button>
              )}
            </div>
          ) : (
            <LaserCameraLine camera={camera} style={{ marginBottom: "12px" }} />
          )}

          {cameraOverrideOpen && (
            <div data-testid="camera-override" style={{
              marginBottom: "12px", padding: "8px", borderRadius: "4px",
              background: "var(--jq-warning-bg, #fff3cd)", border: "1px solid var(--jq-warning-border, #ffe69c)",
            }}>
              <div style={{ fontSize: "0.85rem", marginBottom: "6px" }}>
                {cameraWhat || "Recording a sheet the camera missed"}: a manager's PIN and a reason record this sheet anyway.
              </div>
              <label style={{ display: "block", fontWeight: "bold", fontSize: "0.85rem" }}>Manager PIN:</label>
              <input type="password" value={cameraPin} onChange={(e) => { setCameraPin(e.target.value); setCameraError(null); }}
                     aria-label="Manager PIN"
                     autoComplete="off" style={{ width: "100%", padding: "6px", marginBottom: "6px" }} />
              <label style={{ display: "block", fontWeight: "bold", fontSize: "0.85rem" }}>What did the camera miss?</label>
              <input type="text" value={cameraReason} onChange={(e) => { setCameraReason(e.target.value); setCameraError(null); }}
                     aria-label="What did the camera miss?"
                     placeholder="e.g. sheet swapped while the camera was down" style={{ width: "100%", padding: "6px" }} />
              <div style={{ fontSize: "0.75rem", color: "var(--jq-text-muted, #666)", marginTop: "2px" }}>
                At least {CAMERA_OVERRIDE_REASON_MIN} characters.
              </div>
              {cameraError && (
                <div data-testid="camera-override-error" role="alert" style={{ marginTop: "6px", fontSize: "0.85rem", color: "#b02a37", fontWeight: "bold" }}>
                  {cameraError}
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "8px" }}>
                <button
                  type="button"
                  onClick={handleCameraOverride}
                  disabled={busy || !lastSaveRef.current}
                  style={{ padding: "6px 10px", backgroundColor: "#0a58ca", color: "#fff", border: "none", borderRadius: "4px",
                           cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1 }}
                >
                  {localBusy ? "Saving..." : "Record the missed sheet"}
                </button>
                <button
                  type="button"
                  onClick={cancelCameraOverride}
                  style={{ background: "none", border: "none", padding: 0, color: "#0a58ca", cursor: "pointer", fontSize: "0.8rem", textDecoration: "underline" }}
                >
                  Cancel the override
                </button>
              </div>
            </div>
          )}

          {/* The laser dock decides the lot (enforced), or advises (switch off) */}
          {dockCardShown && (
            <LaserDockCard
              check={enforced || adviceOn ? dock.check : null}
              enforced={enforced}
              stale={enforced && dock.stale && dockAllowsSave(dock.check)}
              saveMessage={dockSaveMsg}
              typedNote={typedNote}
              overrideNote={overrideNote}
              noLot={dockNoLot}
              onNoLot={(v) => { setDockNoLot(v); if (v) setTrackingNumber(""); }}
              overrideOpen={dockOverrideOpen}
              onOpenOverride={openDockOverride}
              onCancelOverride={cancelDockOverride}
              managerPin={dockManagerPin}
              onManagerPin={setDockManagerPin}
              reason={dockReason}
              onReason={setDockReason}
            />
          )}

          {/* REMNANT: its tag shows the lot it was cut from; that lot traces the parts. */}
          {isRemnantTask && (!enforced || dockOverrideOpen) && (
            <div style={{
              marginBottom: "12px", padding: "8px 10px", borderRadius: "6px",
              backgroundColor: "#e7f1ff", border: "1px solid #9ec5fe", color: "#052c65", fontSize: "0.85rem"
            }}>
              <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                Remnant{remnantOfText(trackInfo) ? ` of ${remnantOfText(trackInfo)}` : ""}
              </div>
              {remnantLots.length > 0 ? (
                <>
                  <div style={{ marginBottom: "6px" }}>Enter the tracking number on its tag: the lot it was cut from.</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {remnantLots.map((lot) => (
                      <button
                        key={lot.mtn_id}
                        type="button"
                        onClick={() => { setIsExempt(false); setShowLegacy(false); setTrackingNumber(String(lot.mtn_id)); }}
                        style={{
                          padding: "4px 8px", borderRadius: "4px", cursor: "pointer", fontSize: "0.8rem",
                          border: String(lot.mtn_id) === String(parseInt(trackingNumber, 10)) ? "2px solid #0a58ca" : "1px solid #9ec5fe",
                          backgroundColor: "#fff", color: "#052c65"
                        }}
                      >
                        {remnantLotLabel(lot)}
                      </button>
                    ))}
                  </div>
                </>
              ) : (Array.isArray(trackInfo.lineage_mtns) && trackInfo.lineage_mtns.length > 0) ? (
                <div>The pieces on record for this remnant are all used. Enter the tracking number from this piece's tag.</div>
              ) : (
                <div>No lot is on record for this remnant. If its tag shows a tracking number, enter it.</div>
              )}
              {Number(trackInfo.unrecorded_pieces || 0) > 0 && remnantLots.length > 0 && (
                <div style={{ marginTop: "6px", fontSize: "0.8rem" }}>
                  {Number(trackInfo.unrecorded_pieces) === 1
                    ? "1 piece of this remnant has no lot on record"
                    : `${trackInfo.unrecorded_pieces} pieces of this remnant have no lot on record`}: read the number on the tag of the piece you are cutting.
                </div>
              )}
            </div>
          )}

          {/* Docked mode: the pallet plugged in at the laser is the material: PIN only */}
          {dockedMode && (
            <div data-testid="docked-mode" style={{
              marginBottom: "12px", padding: "10px 12px", borderRadius: "6px",
              backgroundColor: "#e8f5e9", border: "2px solid #2e7d32", color: "#1b5e20"
            }}>
              <div style={{ fontWeight: "bold", fontSize: "0.85rem" }}>Pallet docked at the laser</div>
              <div style={{ fontSize: "1.4rem", fontWeight: "bold", margin: "2px 0" }}>TRK {dockedLot.lot.trk}</div>
              <div style={{ fontSize: "0.85rem" }}>
                {`${dockedLot.lot.material || ""}${dockedLot.lot.size ? ` ${dockedLot.lot.size}` : ""} · ${dockedLot.beacon_id}`}
              </div>
              <div style={{ fontSize: "0.8rem", marginTop: "6px" }}>
                No tracking number needed: enter your PIN to record this sheet against this pallet.
              </div>
              <button
                type="button"
                onClick={() => { setManualEntry(true); setFilledFromBeacon(null); setTrackingNumber(""); }}
                style={{ background: "none", border: "none", padding: 0, marginTop: "6px", color: "#0a58ca", cursor: "pointer", fontSize: "0.85rem", textDecoration: "underline" }}
              >
                Cutting from a different pallet? Enter its tracking number
              </button>
            </div>
          )}

          {/* A beacon plugged in at the laser that isn't on a pallet in JobQ */}
          {!enforced && !dockedMode && !isRemnantTask && !isExempt && !showLegacy && dockedAtLaser.length === 0 && unassignedDocked.length > 0 && (
            <div data-testid="docked-unassigned" style={{
              marginBottom: "12px", padding: "8px 10px", borderRadius: "6px",
              backgroundColor: "#fff8e1", border: "1px solid #ffe082", color: "#5d4037", fontSize: "0.85rem"
            }}>
              {unassignedDocked.join(", ")} {unassignedDocked.length === 1 ? "is" : "are"} plugged in at the laser but {unassignedDocked.length === 1 ? "isn't" : "aren't"} on a pallet in JobQ. Enter the tracking number from the label.
            </div>
          )}

          {/* Pallets plugged into the laser Pi (pallet beacons) */}
          {!enforced && !dockedMode && !isRemnantTask && !isExempt && !showLegacy && dockedAtLaser.length > 0 && (
            <div data-testid="docked-pallets" style={{
              marginBottom: "12px", padding: "8px 10px", borderRadius: "6px",
              backgroundColor: "#e8f5e9", border: "1px solid #a5d6a7", color: "#1b5e20", fontSize: "0.85rem"
            }}>
              <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                {dockedAtLaser.length === 1 ? "Pallet plugged in at the laser" : "Pallets plugged in at the laser: pick the one you're cutting"}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {dockedAtLaser.map((b) => (
                  <button
                    key={b.beacon_id}
                    type="button"
                    onClick={() => { setIsExempt(false); setShowLegacy(false); setManualEntry(false); setTrackingNumber(String(b.lot.mtn_id)); setFilledFromBeacon(b.beacon_id); }}
                    style={{
                      padding: "4px 8px", borderRadius: "4px", cursor: "pointer", fontSize: "0.8rem",
                      border: String(b.lot.mtn_id) === String(parseInt(trackingNumber, 10)) ? "2px solid #1b5e20" : "1px solid #a5d6a7",
                      backgroundColor: "#fff", color: "#1b5e20"
                    }}
                  >
                    {`TRK ${b.lot.trk} · ${b.lot.material}${b.lot.size ? ` ${b.lot.size}` : ""} · ${b.beacon_id}`}
                  </button>
                ))}
              </div>
              {filledFromBeacon && String(parseInt(trackingNumber, 10)) === String((dockedAtLaser.find((b) => b.beacon_id === filledFromBeacon) || {}).lot?.mtn_id) && (
                <div style={{ marginTop: "4px", fontSize: "0.8rem" }}>Filled in from {filledFromBeacon}. Check it matches the pallet you pulled from.</div>
              )}
            </div>
          )}

          {/* Tracking Number (Primary; under the dock rule only with the manager override) */}
          {!dockedMode && (!enforced || (dockOverrideOpen && !dockNoLot)) && (
          <div style={{ marginBottom: "12px", opacity: isExempt ? 0.5 : 1 }}>
            <label style={{display:"block", marginBottom:"5px", fontWeight:"bold"}}>Tracking Number:</label>
            <input
              type="number"
              value={trackingNumber}
              onChange={(e) => { setTrackingNumber(e.target.value); setFilledFromBeacon(null); setTypedTrk(e.target.value); }}
              style={{ width: "100%", padding: "10px", fontSize: "1.2rem", fontWeight: "bold" }}
              placeholder="Enter number from label"
              disabled={isExempt || showLegacy}
              autoFocus={!isExempt}
            />
            <small style={{color:"var(--jq-text-muted, #666)"}}>6-digit number printed on material label</small>
          </div>
          )}

          {/* MTN lot context + notes/warnings */}
          {!isExempt && !showLegacy && lotText && (
            <div style={{ marginBottom: "12px" }}>
              {contextLoading && !mtnContext && (
                <div style={{ fontSize: "0.8rem", color: "var(--jq-text-muted, #666)" }}>Looking up tracking number…</div>
              )}
              {mtnContext && (
                <>
                  <div style={{ fontSize: "0.85rem", marginBottom: "6px" }}>
                    <strong>{mtnContext.material_code || "—"}</strong>
                    <span style={{ color: tagLineColor, fontWeight: tagLine && tagLine.tone === "error" ? "bold" : undefined }}>
                      {"  ·  "}{tagLine ? tagLine.text : ""}
                    </span>
                  </div>

                  {/* Warnings that apply to THIS sheet */}
                  {nowNotes.map(n => {
                    const palette = n.severity === "critical"
                      ? { bg: "#f8d7da", border: "#dc3545", text: "#842029", icon: "⛔ DO NOT USE" }
                      : n.severity === "info"
                      ? { bg: "#cff4fc", border: "#0dcaf0", text: "#055160", icon: "ℹ️ Note" }
                      : { bg: "#fff3cd", border: "#ffc107", text: "#664d03", icon: "⚠️ Warning" };
                    const trig = n.trigger_type === "at_sheet" ? ` — Piece ${n.trigger_sheet_number}`
                      : n.trigger_type === "after_sheet" ? ` — Sheet ${n.trigger_sheet_number}+` : "";
                    return (
                      <div key={n.note_id} style={{
                        backgroundColor: palette.bg, border: `2px solid ${palette.border}`, color: palette.text,
                        borderRadius: "6px", padding: "10px", marginBottom: "8px"
                      }}>
                        <div style={{ fontWeight: "bold", marginBottom: "4px" }}>{palette.icon}{trig}</div>
                        <div style={{ whiteSpace: "pre-wrap" }}>{n.note_text}</div>
                        {n.severity === "critical" && (
                          <div style={{ marginTop: "8px" }}>
                            <button
                              type="button"
                              onClick={() => handleDivert(n.note_id)}
                              disabled={diverting}
                              style={{
                                backgroundColor: "#dc3545", color: "#fff", border: "none", borderRadius: "4px",
                                padding: "6px 10px", cursor: diverting ? "not-allowed" : "pointer", fontWeight: "bold"
                              }}
                            >
                              {diverting ? "Working…" : "Set aside / Divert this sheet"}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Heads-up for the very next sheet */}
                  {upcomingNotes.map(n => (
                    <div key={`up_${n.note_id}`} style={{
                      fontSize: "0.8rem", color: "#664d03", backgroundColor: "#fff9e6",
                      border: "1px dashed #ffc107", borderRadius: "4px", padding: "6px 8px", marginBottom: "8px"
                    }}>
                      ⏭ Heads-up — next sheet (piece {n.trigger_sheet_number}): {n.note_text}
                    </div>
                  ))}

                  {/* Critical acknowledgment gate */}
                  {hasCriticalNow && (
                    <label style={{ display: "flex", alignItems: "center", fontSize: "0.85rem", marginBottom: "4px", cursor: "pointer" }}>
                      <input type="checkbox" checked={noteAck} onChange={e => setNoteAck(e.target.checked)} style={{ marginRight: "8px" }} />
                      <span>I have read the warning above.</span>
                    </label>
                  )}
                </>
              )}
            </div>
          )}

          {/* Legacy toggle */}
          {!enforced && !isExempt && !trackingNumber && (
            <div style={{ marginBottom: "10px", textAlign: "center" }}>
              <button
                type="button"
                onClick={() => setShowLegacy(!showLegacy)}
                style={{ background: "none", border: "none", color: "#007bff", cursor: "pointer", fontSize: "0.85rem", textDecoration: "underline" }}
              >
                {showLegacy ? "Use Tracking Number instead" : "No tracking number? Enter PO + Shipment ID"}
              </button>
            </div>
          )}

          {/* Legacy PO+Shipment Inputs */}
          {!enforced && showLegacy && !isExempt && (
            <>
              <div style={{ marginBottom: "10px" }}>
                <label style={{display:"block", marginBottom:"5px", fontWeight:"bold"}}>PO Number:</label>
                <input
                  type="number"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  style={{ width: "100%", padding: "8px" }}
                  placeholder="e.g. 1050"
                />
              </div>
              <div style={{ marginBottom: "10px" }}>
                <label style={{display:"block", marginBottom:"5px", fontWeight:"bold"}}>Shipment ID:</label>
                <input
                  type="number"
                  value={shipmentId}
                  onChange={(e) => setShipmentId(e.target.value)}
                  style={{ width: "100%", padding: "8px" }}
                  placeholder="e.g. 55"
                />
              </div>
            </>
          )}

          {/* Exempt Checkbox (not offered while a docked pallet is the answer, nor under the dock rule) */}
          {!dockedMode && !enforced && (
          <div style={{ marginBottom: "12px", padding: "8px", backgroundColor: "var(--jq-bg-subtle, #f8f9fa)", borderRadius: "4px", border: "1px solid var(--jq-border-medium, #dee2e6)" }}>
            <label style={{ display: "flex", alignItems: "flex-start", cursor: "pointer", fontSize: "0.85rem" }}>
              <input
                type="checkbox"
                checked={isExempt}
                onChange={(e) => { setIsExempt(e.target.checked); if (e.target.checked) setShowLegacy(false); }}
                style={{ marginTop: "3px", marginRight: "8px" }}
              />
              <span>
                <strong>No Tracking Info Available</strong><br/>
                <span style={{color: "var(--jq-text-muted, #666)", fontSize: "0.8rem"}}>(Customer Supplied / Drop / Remnant with no number on its tag)</span>
              </span>
            </label>
            {managerBoxShown && (
              <div data-testid="no-tracking-manager" style={{ marginTop: "8px", padding: "8px", borderRadius: "4px",
                   background: "var(--jq-warning-bg, #fff3cd)", border: "1px solid var(--jq-warning-border, #ffe69c)" }}>
                <div style={{ fontSize: "0.85rem", marginBottom: "6px" }}>
                  {managerNeeded || (trackInfo && trackInfo.no_tracking_message)
                    || "Every sheet of this material in stock is on a tracking number."}
                </div>
                <label style={{ display: "block", fontWeight: "bold", fontSize: "0.85rem" }}>Manager PIN:</label>
                <input type="password" value={managerPin} onChange={(e) => setManagerPin(e.target.value)}
                       aria-label="Manager PIN"
                       autoComplete="off" style={{ width: "100%", padding: "6px", marginBottom: "6px" }} />
                <label style={{ display: "block", fontWeight: "bold", fontSize: "0.85rem" }}>Why is there no tracking number?</label>
                <input type="text" value={managerReason} onChange={(e) => setManagerReason(e.target.value)}
                       aria-label="Why is there no tracking number?"
                       placeholder="e.g. tag torn off the piece" style={{ width: "100%", padding: "6px" }} />
              </div>
            )}
          </div>
          )}

          {/* PIN (or the face sign-in standing in for it) */}
          <FaceSignInStrip
            state={face}
            onNotMe={() => { face.notMe(); setTimeout(() => pinRef.current && pinRef.current.focus(), 0); }}
            onTryAgain={face.retry}
          />
          {!faceMatch && (
          <div style={{ marginBottom: "15px" }}>
            <label style={{display:"block", marginBottom:"5px", fontWeight:"bold"}}>Operator PIN:</label>
            <input
              ref={pinRef}
              type="password"
              value={pin}
              onChange={(e) => {
                setPin(e.target.value);
                // Typing a PIN ends the face attempt (an answer still on its way is ignored).
                if (e.target.value && (face.phase === "checking" || face.phase === "looking")) face.cancel();
              }}
              style={{ width: "100%", padding: "8px" }}
              placeholder="****"
              autoComplete="off"
            />
          </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <button type="button" onClick={onClose} style={{ padding: "8px 12px" }} disabled={busy}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={confirmDisabled}
              title={dockHeldLabel || undefined}
              style={{
                padding: "8px 12px",
                backgroundColor: isValidating ? "#17a2b8" : "#28a745",
                color: "#fff", border: "none", borderRadius: "4px",
                cursor: confirmDisabled ? "not-allowed" : "pointer",
                opacity: confirmDisabled ? 0.7 : 1
              }}
            >
              {isValidating ? "Validating..." : (isSubmitting || localBusy) ? "Saving..." : dockHeldLabel ? dockHeldLabel : (hasCriticalNow && !noteAck) ? "Acknowledge warning to continue" : "Confirm & Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * DetailView - shows detailed info about the selected job/task.
 */
/**
 * DetailView - shows detailed info about the selected job/task.
 */
function DetailView({
  jobDetail,
  workstation,
  selectedTaskId,
  cutSheetDetail,
  materialInfo,
  dropMaterialInfo,
  customers,
  jobParts,
  reloadJob,
  onEnlarge,
  activityLog,
  clearSelectedJobDetail,
  isSubmitting,
  setIsSubmitting,
  // NEW PPAP PROPS for JobQueuePage state management
  setShowPpapModal,
  setPpapPartData,
  setPpapTaskId,
  setPpapPhase,
  faiStatus,
  setShowFaiOverrideModal,
  nextAllowedJobId,
  isStationLocked,
  lockReason,
  pendingMaintenanceToday,
  // What the job's queue row says about its order (queueRowSaysTm): true / false when the row
  // carries so_pricing_mode (jobq-api since 2026-09-28; it is per JOB, so any of the job's rows
  // answers), undefined when it does not (an older API, or no loaded row): then GET /jobs/{id}.
  queueRowSaysTm: rowSaysTm,
}) {
  // T&M job -> a part / sheet defect asks the neutral cause (decision 15). The queue row decides when
  // it says; only when it does not, the GET /jobs/{id} answer (jobDetail.so_pricing_mode) does.
  const isTmJob = rowSaysTm === undefined ? isTmLinked(jobDetail) : rowSaysTm;

  // Helper used by the FAI banner to open the PPAP modal in 'initial' mode.
  // Mirrors LaserDetailSection.handlePpapMeasureClick but lives at this scope
  // so the banner (rendered in DetailView) has access to it.
  const handleFaiOpenForPart = async (partId, taskId) => {
    try {
      const res = await fetch(`${API_BASE}/parts/${partId}`);
      if (!res.ok) throw new Error("Failed to fetch part details for FAI.");
      const fullPartData = await res.json();
      const csPartData = cutSheetDetail?.parts?.find(p => p.part_id === partId);
      if (csPartData) {
        fullPartData.ppap_critical_characteristics = csPartData.ppap_critical_characteristics;
        fullPartData.ppap_image_path = csPartData.ppap_image_path;
        fullPartData.ppap_sample_size_ongoing = csPartData.ppap_sample_size_ongoing;
        fullPartData.ppap_sample_size_initial = csPartData.ppap_sample_size_initial;
      }
      setPpapPartData(fullPartData);
      setPpapTaskId(taskId);
      setPpapPhase('initial');
      setShowPpapModal(true);
    } catch (err) {
      alert(err.message);
    }
  };

  // PIN-gated unflag from the Flagged Parts card. The PIN is the audit
  // act: /auth/pin validates the operator and the resolved name appears
  // in the confirmation alert + console log. The existing PATCH /parts
  // clears the flag fields atomically once auth passes.
  const handleUnflagFromCard = async (part) => {
    const pin = await pinPrompt(`Enter your PIN to clear the flag on ${part.product_id}:`);
    if (!pin) return;
    let userName = "Unknown";
    try {
      const authRes = await fetch(`${API_BASE}/auth/pin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      if (!authRes.ok) {
        alert("Invalid PIN. Unflag cancelled.");
        return;
      }
      const authData = await authRes.json();
      userName = authData.name || "Unknown";
    } catch (err) {
      alert("PIN verification failed: " + err.message);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/parts/${part.part_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          flagged: false,
          flag_reason: null,
          flagged_by_name: null,
          flagged_at: null,
        }),
      });
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      console.log(`Part ${part.product_id} (id=${part.part_id}) unflagged by ${userName}`);
      alert(`${part.product_id} unflagged by ${userName}.`);
      reloadJob();
    } catch (err) {
      alert("Error unflagging part: " + err.message);
    }
  };

  // Identify maintenance tasks robustly by checking job_type OR Priority 0
  const isMaintenanceTask = isMaintenanceJob(jobDetail);

  // Logic: Disable controls if station is locked UNLESS this IS the maintenance task itself
  // This allows the maintenance task to be started/completed even when the station is locked.
  const controlsDisabled = isStationLocked && !isMaintenanceTask;
  
  const [localNotes, setLocalNotes] = useState("");
  const [editingDefect, setEditingDefect] = useState(null);
  const [editingStagedDefect, setEditingStagedDefect] = useState(null);
  const [stagedDefectsReloadKey, setStagedDefectsReloadKey] = useState(0);

  // Synchronous double-click guard for Start/Pause/Complete and similar
  // action handlers. The existing `if (isSubmitting) return` check reads
  // a stale closure value, so a second click that fires before React
  // re-renders with disabled=true sneaks past it. Refs update
  // synchronously, so this catches that gap.
  const submittingRef = useRef(false);

  // Constants for Gas Swaps (Matched to main.py logic)
  const GAS_ID_N2 = 20;
  const GAS_ID_O2 = 21;

  useEffect(() => {
    if (jobDetail && typeof jobDetail.notes === "string") {
      setLocalNotes(jobDetail.notes);
    } else {
      setLocalNotes("");
    }
  }, [jobDetail]);

  // Colors as they appear on the Outside Processing Order — i.e. what the coater
  // was actually told. Read from the OPO rather than the part because they can
  // legitimately differ: an assembly's Coating row overrides the primary part's
  // color, and the OPO snapshot is deliberately frozen at hand-off so a later
  // edit to the part cannot rewrite history. Showing the part's color here would
  // let the station contradict the paperwork in the operator's hand.
  // Declared before the early return below (rules-of-hooks is fatal on Amplify).
  const [opoColors, setOpoColors] = useState([]);
  useEffect(() => {
    if (workstation !== "Coating" || !jobDetail?.job_id) { setOpoColors([]); return undefined; }
    let alive = true;
    fetch(`${API_BASE}/outside_processing_orders?job_id=${jobDetail.job_id}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((orders) => {
        if (!alive) return;
        const list = Array.isArray(orders) ? orders : [];
        const mine = list.find((o) => o.job_task_id === selectedTaskId) || list[0];
        setOpoColors(mine && Array.isArray(mine.colors) ? mine.colors : []);
      })
      .catch(() => { if (alive) setOpoColors([]); });
    return () => { alive = false; };
  }, [workstation, jobDetail?.job_id, selectedTaskId]);

  // Remnant / stock-drop label questions (2026-09-23). Every label the laser prints for a
  // remnant or drop asks the operator whether it printed (RemnantLabelConfirmModal); a
  // "didn't print" sends the label to the office in Slack. Unanswered ones come back after a
  // reload (banner in LaserDetailSection) and must be answered before Complete. Refreshed on
  // every job reload so Add Sheet / Subtract Sheet show up at once. Declared before the early
  // return below (rules-of-hooks is fatal on Amplify).
  const [pendingLabelConfs, setPendingLabelConfs] = useState([]);
  const [activeLabelConf, setActiveLabelConf] = useState(null);
  const [labelConfIntro, setLabelConfIntro] = useState(null);
  useEffect(() => {
    if (workstation !== "Laser" || !selectedTaskId) { setPendingLabelConfs([]); return undefined; }
    let alive = true;
    loadPendingLabelConfirmations(selectedTaskId).then((list) => { if (alive) setPendingLabelConfs(list); });
    return () => { alive = false; };
  }, [workstation, selectedTaskId, jobDetail]);

  // Find the specific task from the job's task list
  const wsTask = jobDetail.tasks.find((t) => t.task_id === selectedTaskId);

  if (!wsTask) {
    return <div>No {workstation} task found.</div>;
  }

  const openLabelConfirmation = (conf, intro = null) => {
    if (!conf || !conf.print_job_id) return;
    setLabelConfIntro(intro);
    setActiveLabelConf(conf);
  };

  // After an answer, the next unanswered label (if any) is asked right away: every label
  // gets answered before the operator moves on.
  const handleLabelConfClosed = async () => {
    const closedId = activeLabelConf && activeLabelConf.print_job_id;
    setActiveLabelConf(null);
    const list = await loadPendingLabelConfirmations(wsTask.task_id);
    setPendingLabelConfs(list);
    const next = list.find((c) => c.print_job_id !== closedId);
    if (next) openLabelConfirmation(next, labelConfIntro);
  };

  // What the task may do now and the buttons it offers come from the shared rules
  // (features/queue/queueRules.js taskGates + taskButtons) the phone task sheet uses: the order
  // lock, the maintenance nudge / 2 PM block, the station lock, multi-op (wsTask comes from
  // /jobs/{id}/progress, which carries op_count since jobq-api 2026-09-10; an older payload reads
  // as an ordinary task), Auto-Complete one op at a time, and the Laser's First Article gate.
  const gates = taskGates({
    job: jobDetail,
    task: wsTask,
    workstation,
    selectedTaskId,
    stationLocked: isStationLocked,
    nextAllowed: nextAllowedJobId,
    pendingMaintenanceToday,
    faiStatus,
  });
  const { opFlow, showMaintenanceNudge, maintenanceTitles } = gates;
  // The action area, in order (buttons and the desktop-only boxes): drawn by renderActionStep.
  const { steps: actionSteps } = taskButtons({ task: wsTask, workstation, gates });

  // Colors for the Coating-station banner. The OPO is authoritative (it is what
  // the coater was handed); the part's standing color is only a fallback for the
  // window before the OPO exists or if the lookup failed. Plain derivation, not
  // a hook, so it can sit below the early return above.
  const coatingColorsOnTask = (() => {
    if (workstation !== "Coating") return [];
    if (opoColors.length) return opoColors;
    const scoped = wsTask.part_id
      ? (jobParts || []).filter((p) => p.part_id === wsTask.part_id)
      : (jobParts || []);
    const seen = new Set();
    const out = [];
    for (const p of scoped) {
      const code = (p.coating_color_code || "").trim();
      if (!code || seen.has(code.toUpperCase())) continue;
      seen.add(code.toUpperCase());
      out.push({ code, name: p.coating_color_name, hex: p.coating_color_hex });
    }
    return out;
  })();


  const isProtected = wsTask.authorized_user_id != null;

  // --- First Article Inspection (FAI) gate (queueRules.faiGate) ---
  // Closed when FAI is required for this Laser task and the operator has
  // not yet recorded a passing batch of initial samples (or admin override).
  // Only relevant on Pending; Resume from Paused doesn't re-gate.
  const { closed: isFaiGateClosed, passed: isFaiPassed, locked: isFaiLocked, pendingParts: faiPendingParts } = gates.fai;

  // --- Determine if the selected task is a Sheet Stock laser task ---
  // A sheet stock task is on the Laser station AND does NOT have a part_id.
  const isSheetStockLaserTask = workstation === "Laser" && !wsTask.part_id;

  const dbElapsedSeconds = wsTask.elapsed_time_seconds || 0;

  // Helper: parse server error messages

  async function handleUnblock() {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      if (!window.confirm("Are you sure you want to manually unblock this task? An Admin PIN is required.")) {
        return;
      }
      const userPin = await promptForUserPin();
      if (!userPin) return;

      const performUnblock = async (adminOverridePin = null) => {
        const payload = { pin: userPin };
        if (adminOverridePin) {
          payload.admin_override_pin = adminOverridePin;
        }

        const res = await fetch(`${API_BASE}/tasks/${wsTask.task_id}/unblock`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          if (res.status === 403 && !adminOverridePin) {
            const msg = await parseServerError(res);
            // A second person authorises: face would read the operator again and loop on the 403.
            const adminPin = await pinPrompt(`${msg}\n\nAdmin: Enter PIN to AUTHORIZE unblock:`, { face: false });
            if (adminPin) {
              await performUnblock(adminPin);
              return;
            }
            return;
          }
          const msg = await parseServerError(res);
          alert(`Error: ${msg}`);
          return;
        }

        alert("Task successfully unblocked.");
        reloadJob();
      };

      try {
        await performUnblock();
      } catch (e) {
        alert(e.message || "An error occurred while unblocking the task.");
      }
    } finally {
      submittingRef.current = false;
    }
  }

  // Start / Pause / Finish Op / Maintenance / Complete / Auto-Complete: the actions live in
  // features/queue/taskActions.js (ONE copy, shared with the phone queue). This page keeps its
  // double-tap guard (submittingRef), the button lock (setIsSubmitting) and the reload.
  async function runTaskAction(action) {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const result = await action();
      if (result && result.reload) reloadJob();
    } finally {
      submittingRef.current = false;
    }
  }

  function handleStart() {
    return runTaskAction(() => startTask({
      task: wsTask, workstation, isMaintenanceTask,
      cutSheetParts: cutSheetDetail ? cutSheetDetail.parts : null,
      ui: desktopUi, setBusy: setIsSubmitting,
    }));
  }

  function handlePause() {
    return runTaskAction(() => pauseTask({ task: wsTask, ui: desktopUi, setBusy: setIsSubmitting }));
  }

  // Finish the CURRENT op of a multi-op task and hand it to the next one. Only ever offered when
  // current_op < op_count — the LAST op is finished with Complete.
  function handleFinishOp() {
    return runTaskAction(() => finishOp({ task: wsTask, opFlow, ui: desktopUi, setBusy: setIsSubmitting }));
  }

  function handleEnterMaintenance() {
    return runTaskAction(() => enterMaintenance({ task: wsTask, ui: desktopUi, setBusy: setIsSubmitting }));
  }

  function handleExitMaintenance() {
    return runTaskAction(() => exitMaintenance({ task: wsTask, ui: desktopUi, setBusy: setIsSubmitting }));
  }

  // --- NEW LASER CONSUMABLE HANDLERS ---

  async function handleGasSwap(gasType) {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const pin = await promptForUserPin();
      if (!pin) return;

      setIsSubmitting(true);
      try {
        const payload = {
          gas_type: gasType, // 'N2' or 'O2'
          consumable_id: gasType === 'N2' ? GAS_ID_N2 : GAS_ID_O2,
          pin: pin
        };

        const res = await fetch(`${API_BASE}/inventory/gas_swap`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });

        if (!res.ok) {
          const msg = await parseServerError(res);
          alert(`Error recording gas swap: ${msg}`);
          return;
        }

        const data = await res.json();
        alert(`✅ ${data.message}`);
        // No need to reload job, this is an inventory event
      } catch (e) {
        alert(e.message || "Network error during gas swap.");
      } finally {
        setIsSubmitting(false);
      }
    } finally {
      submittingRef.current = false;
    }
  }

  async function handleNozzleSwap() {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {

    // 1. Verify we have material info loaded to check specs
    if (!materialInfo) {
        alert("⚠️ Material information not loaded for this job. Cannot automatically identify nozzle.");
        return;
    }

    setIsSubmitting(true);
    try {
        // 2. Fetch all laser specs
        const specsRes = await fetch(`${API_BASE}/laser_specs`);
        if (!specsRes.ok) throw new Error("Failed to fetch laser tech specs.");
        const specs = await specsRes.json();

        // 3. Find matching spec for current material
        // FIXED: Match by thickness_id if available, otherwise fallback to approximate float match
        const currentType = materialInfo.material_type;
        const currentThickId = materialInfo.thickness_id;
        const currentThickVal = parseFloat(materialInfo.thickness_in);

        const match = specs.find(s => {
            // First check types match
            if (s.material_type !== currentType) return false;

            // Preferred: Match by Database ID
            if (currentThickId && s.thickness_id) {
                return s.thickness_id === currentThickId;
            }

            // Fallback: Match by value (within small tolerance for float errors)
            if (s.thickness_in) {
                return Math.abs(s.thickness_in - currentThickVal) < 0.001;
            }
            
            return false;
        });

        if (!match || !match.nozzle_consumable_id) {
            alert(`⚠️ No tech spec or nozzle defined for ${currentType} @ ${currentThickVal}". Please manually deduct the nozzle from the Consumables list below.`);
            setIsSubmitting(false);
            return;
        }

        const nozzleName = match.nozzle_description || match.nozzle_name || "Unknown Nozzle";
        const nozzleId = match.nozzle_consumable_id;

        // 4. Confirm and Get PIN
        const confirmMsg = `Identify Nozzle: ${nozzleName}\n\nSwap and deduct 1 from inventory?`;
        if (!window.confirm(confirmMsg)) {
            setIsSubmitting(false);
            return;
        }

        const pin = await promptForUserPin();
        if (!pin) {
            setIsSubmitting(false);
            return;
        }

        // 5. Deduct Inventory using standard consumables endpoint.
        //
        // We attach task_id so the backend can attribute premature-swap
        // cost (nozzle destroyed well before expected life) directly to
        // the running laser task instead of smearing the loss into the
        // rolling hourly-wear average. See add_consumable_transaction in
        // jobq-api/main.py for the cost-attribution logic.
        const payload = {
            delta: -1,
            pin: pin,
            reason: `Nozzle Swap at Laser for Job ${jobDetail.master_job_num}`,
            task_id: wsTask.task_id
        };

        const txRes = await fetch(`${API_BASE}/consumables/${nozzleId}/transactions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
        });

        if (!txRes.ok) {
            const msg = await parseServerError(txRes);
            alert(`Error deducting nozzle: ${msg}`);
            return;
        }

        // Surface premature-swap cost to the operator so they know the
        // loss was captured against this task on the SO cost breakdown.
        const txData = await txRes.json().catch(() => ({}));
        const prematureCost = Number(txData.premature_cost || 0);
        if (prematureCost > 0) {
            alert(
                `⚠️ Premature failure detected: ${nozzleName}\n\n` +
                `$${prematureCost.toFixed(2)} in unused-life cost has been ` +
                `billed to this task and will show as an "Incidents" line on ` +
                `the sales order cost breakdown.`
            );
        } else {
            alert(`✅ Swapped & Deducted: ${nozzleName}`);
        }

    } catch (e) {
        alert(`Error: ${e.message}`);
    } finally {
        setIsSubmitting(false);
    }
    } finally {
      submittingRef.current = false;
    }
  }

  // -------------------------------------

  function handleComplete() {
    return runTaskAction(async () => {
      // Every remnant / drop label this task printed must be answered (printed / didn't print)
      // before it completes. Fails open: if the list can't be read, Complete proceeds.
      if (workstation === "Laser" && !isMaintenanceTask) {
        const unanswered = await loadPendingLabelConfirmations(wsTask.task_id);
        setPendingLabelConfs(unanswered);
        if (unanswered.length > 0) {
          openLabelConfirmation(
            unanswered[0],
            "Answer this before completing the task: every remnant label has to be confirmed."
          );
          return { reload: false };
        }
      }
      return completeTask({
        task: wsTask, workstation, isMaintenanceTask, jobId: jobDetail.job_id,
        ui: desktopUi, setBusy: setIsSubmitting,
      });
    });
  }

  // Coating workstation: "Receive from vendor" completes the outsourced-finishing task
  // (marks the linked OPO Returned on the backend) and unblocks the next station.
  async function handleReceiveFromVendor() {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      if (!window.confirm("Receive this batch back from the coating vendor? This completes the Coating step so the job can continue.")) return;
      const userPin = await promptForUserPin();
      if (!userPin) return;
      setIsSubmitting(true);
      try {
        const res = await fetch(`${API_BASE}/tasks/${wsTask.task_id}/receive_from_vendor`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pin: userPin, punch_time_utc: new Date().toISOString() }),
        });
        if (!res.ok) {
          alert(await parseServerError(res));
          return;
        }
        const received = await res.json().catch(() => ({}));
        alert("Received from vendor. Coating complete — the job continues to the next station.");
        reloadJob();
        // pallets back with a beacon to put on (2026-10-26): the list, nothing when none
        if (received && received.opo_id) {
          try {
            const { offerReattach } = await import("../features/pallets/opoPalletFlows");
            await offerReattach(received.opo_id);
          } catch (reattachErr) {
            // the receive is recorded: a failed popup must never read as a failed receive
            console.error("Reattach list:", reattachErr);
          }
        }
      } finally {
        setIsSubmitting(false);
      }
    } catch (e) {
      alert(e.message || "Error receiving from vendor.");
    } finally {
      submittingRef.current = false;
    }
  }

  // Reprint the Outside Processing Order that accompanies this job's parts to the coater.
  async function handleReprintCoatingPO() {
    try {
      const listRes = await fetch(`${API_BASE}/outside_processing_orders?job_id=${wsTask.job_id}&include_lines=true`);
      if (!listRes.ok) { alert("Could not load the coating PO."); return; }
      const orders = await listRes.json();
      // Match THIS coating task. The old `|| orders[0]` fallback silently printed
      // whichever PO happened to come back first — fine when a job had one, but a
      // job with two coating tasks (build sets, or two parts going out) would
      // hand the operator another part's PO. Now that a PO carries a COLOR, the
      // wrong one is not a paperwork nuisance, it is a wrong-color batch.
      // Multi-line coating MANIFESTS link tasks at the LINE level, so match
      // both the legacy header-level job_task_id and the manifest lines.
      const matches = (orders || []).filter(
        (o) => o.job_task_id === wsTask.task_id
          || (o.lines || []).some((l) => l.job_task_id === wsTask.task_id)
      );
      const opo = matches[0];
      if (!opo) {
        alert((orders || []).length
          ? "No coating PO is linked to this specific Coating task. Open the Sales Order to find the right PO."
          : "No coating PO found for this job yet.");
        return;
      }
      const pdfRes = await fetch(`${API_BASE}/outside_processing_orders/${opo.opo_id}/pdf`);
      if (!pdfRes.ok) { alert("Could not load the coating PO PDF."); return; }
      openPdfInPrintDialog(await pdfRes.blob());
    } catch (e) {
      console.error("Reprint coating PO failed:", e);
      alert("Error reprinting the coating PO.");
    }
  }

  function handleAutoComplete() {
    return runTaskAction(() => autoCompleteTask({
      task: wsTask, jobId: jobDetail.job_id, isMaintenanceTask,
      ui: desktopUi, setBusy: setIsSubmitting,
    }));
  }

  // One step of the action area (queueRules.taskButtons decides which, and in what order), drawn
  // as this page always drew it. A button's label, title and disabled come from the rule; its look
  // and handler from here. The note steps (Unblock, Coating's Receive / Reprint, the LOCKED /
  // MAINTENANCE REQUIRED box, Task Completed) keep this page's own words, not the phone's note.
  function renderActionStep(step) {
    switch (step.kind) {
      case "exitMaintenance":
        return (
          <button
            onClick={handleExitMaintenance}
            disabled={isSubmitting}
            style={{
              backgroundColor: '#ffc107',
              color: '#000',
              border: 'none',
              borderRadius: '4px',
              padding: '0.5rem 1rem',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            {step.label}
          </button>
        );
      case "unblock":
        return (
          <button
            onClick={handleUnblock}
            disabled={isSubmitting}
            style={{
              backgroundColor: "#6f42c1", // A distinct purple color
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "0.5rem 1rem",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Unblock Task
          </button>
        );
      case "stationLocked":
        // The LOCKED banner above the steps already says it.
        return null;
      case "maintenanceRequired":
      case "orderLocked":
        return (
          <div
            style={{
              backgroundColor: "var(--jq-bg-subtle, #e2e3e5)",
              color: "var(--jq-text-muted, #6c757d)",
              padding: "0.5rem",
              borderRadius: "4px",
              fontSize: "0.85rem",
              fontWeight: "bold",
              border: "1px solid #d6d8db",
              textAlign: "center"
            }}
          >
            {step.kind === "maintenanceRequired" ? (
              <>
                🔒 MAINTENANCE REQUIRED<br/>
                <span style={{fontSize: "0.75rem", fontWeight: "normal"}}>
                  Complete: {step.titles}
                </span>
              </>
            ) : (
              <>
                🔒 LOCKED<br/>
                <span style={{fontSize: "0.75rem", fontWeight: "normal"}}>
                  Process top job first
                </span>
              </>
            )}
          </div>
        );
      case "coatingReceive":
        return (
          <>
            {/* The color the coater was told to spray. Shown right above
                the Receive button so the operator can check the returned
                batch against it before punching the receipt. */}
            {coatingColorsOnTask.length > 0 && (
              <div
                style={{
                  display: "flex", alignItems: "center", gap: "0.75rem",
                  flexWrap: "wrap", marginBottom: "0.6rem", padding: "0.5rem 0.75rem",
                  border: "1px solid var(--jq-border-medium, #ccc)", borderRadius: "4px",
                  backgroundColor: "var(--jq-bg-surface, #f9f9f9)",
                }}
              >
                <strong style={{ fontSize: "0.8rem", letterSpacing: "0.03em" }}>COLOR</strong>
                {coatingColorsOnTask.map((cc) => (
                  <CoatingSwatch
                    key={cc.code}
                    code={cc.code}
                    name={cc.name}
                    hex={cc.hex}
                    size={18}
                  />
                ))}
                <span style={{ fontSize: "0.7rem", color: "var(--jq-text-muted, #6c757d)" }}>
                  chip is approximate — match to the code
                </span>
              </div>
            )}
            <button
              onClick={handleReceiveFromVendor}
              disabled={isSubmitting}
              style={{
                backgroundColor: "#0d9488", color: "#fff", border: "none",
                borderRadius: "4px", padding: "0.5rem 1rem", cursor: "pointer", fontWeight: "bold",
              }}
            >
              📦 Receive from Vendor
            </button>
            <button
              onClick={handleReprintCoatingPO}
              disabled={isSubmitting}
              style={{
                backgroundColor: "#6c757d", color: "#fff", border: "none",
                borderRadius: "4px", padding: "0.5rem 1rem", cursor: "pointer",
                fontWeight: "bold", marginLeft: "0.5rem",
              }}
            >
              🖨 Reprint Coating PO
            </button>
            <div style={{ marginTop: "0.5rem", fontSize: "0.8rem", color: "var(--jq-text-muted, #6c757d)" }}>
              Batch sends &amp; receives: <a href="/coating">Coating Manifests</a>
            </div>
          </>
        );
      case "start":
        // Pending. Greyed out while the Laser's First Article gate is closed (step.disabled).
        return (
          <button
            onClick={handleStart}
            disabled={isSubmitting || step.disabled}
            title={step.title || ''}
            style={{
              backgroundColor: step.disabled ? "#6c757d" : "#28a745",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "0.5rem 1rem",
              cursor: step.disabled ? "not-allowed" : "pointer",
              fontWeight: "bold",
              opacity: step.disabled ? 0.7 : 1,
            }}
          >
            {step.label}
          </button>
        );
      case "autoComplete":
        // Multi-op: this books the CURRENT op only (its share of the estimate) and hands the task
        // on; the label names the op.
        return (
          <button
            onClick={handleAutoComplete}
            disabled={isSubmitting}
            title={step.title}
            style={{
              backgroundColor: "#17a2b8",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "0.5rem 1rem",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {step.label}
          </button>
        );
      case "pause":
        return (
          <button
            onClick={handlePause}
            disabled={isSubmitting}
            style={{
              backgroundColor: "#f0ad4e",
              color: "#000",
              border: "none",
              borderRadius: "4px",
              padding: "0.5rem 1rem",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {step.label}
          </button>
        );
      case "finishOp":
        // A multi-op task with ops still to run (In Progress, or Paused after its run).
        return (
          <button
            onClick={handleFinishOp}
            disabled={isSubmitting}
            style={{
              backgroundColor: "#5b2d90",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "0.5rem 1rem",
              cursor: "pointer",
              fontWeight: "bold",
            }}
            title={step.title}
          >
            {step.label}
          </button>
        );
      case "complete":
        return (
          <button
            onClick={handleComplete}
            disabled={isSubmitting}
            style={{
              backgroundColor: "#d9534f",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "0.5rem 1rem",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {step.label}
          </button>
        );
      case "resume":
        // Paused: "Resume", or "Start Op N" for the next operator of a multi-op task.
        return (
          <button
            onClick={handleStart}
            disabled={isSubmitting}
            style={{
              backgroundColor: "#28a745",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "0.5rem 1rem",
              cursor: "pointer",
              fontWeight: "bold",
            }}
            title={step.title}
          >
            {step.label}
          </button>
        );
      case "enterMaintenance":
        return (
          <button
            onClick={handleEnterMaintenance}
            disabled={isSubmitting}
            style={{
              backgroundColor: '#6c757d',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              padding: '0.5rem 1rem',
              cursor: 'pointer',
              fontWeight: 'bold',
              marginTop: '5px'
            }}
          >
            {step.label}
          </button>
        );
      case "completed":
        return <span style={{ fontStyle: "italic" }}>Task Completed</span>;
      default:
        return null;
    }
  }

  // The "print report" button (its click event is not an include mode: the full report prints).
  function handleDownloadPdf() {
    return printTaskReport({ task: wsTask, jobId: jobDetail.job_id, isMaintenanceTask, includeOverride: undefined, ui: desktopUi });
  }

  // Allow editing an activity timestamp
  async function handleEditActivityTimestamp(entry) {
    const userPin = await promptForUserPin()
    if (!userPin) return

    const oldUtc = new Date(entry.action_timestamp)
    const oldCentralStr = toCentralTime12h(oldUtc)

    const userInput = prompt(
      `Enter new Central Time (MM/DD/YYYY, H:MM AM/PM). Current: ${oldCentralStr}`,
      oldCentralStr
    )
    if (!userInput) return

    const parsedUtc = parseCentralTime12hToUtc(userInput)
    if (!parsedUtc) {
      alert("Invalid format. Please use: MM/DD/YYYY, H:MM AM/PM")
      return
    }
    const newIsoUtc = parsedUtc.toISOString()
    const reason = prompt("Reason for timestamp change:")
    if (!reason) return

    try {
      // Update job activity
      const activityBody = {
        pin: userPin,
        new_timestamp_utc: newIsoUtc,
        reason,
      }
      const activityRes = await fetch(
        `${API_BASE}/jobs/${jobDetail.job_id}/activity_log/${entry.job_activity_id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(activityBody),
        }
      )
      if (!activityRes.ok) {
        const msgAct = await parseServerError(activityRes)
        alert(msgAct)
        return
      }

      // If there's a punch_id, also update the time punch
      if (entry.punch_id) {
        const punchBody = {
          pin: userPin,
          new_timestamp_utc: newIsoUtc,
          reason,
        }
        const punchRes = await fetch(`${API_BASE}/time_punches/${entry.punch_id}/edit_timestamp`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(punchBody),
        })
        if (!punchRes.ok) {
          const msgPunch = await parseServerError(punchRes)
          alert(msgPunch)
          return
        }
      }
      alert("Timestamp updated!")
      reloadJob()
    } catch (err) {
      alert("Error updating timestamp: " + err.message)
    }
  }

  async function handleSaveNotes() {
    try {
      const patchData = { notes: localNotes }
      const res = await fetch(`${API_BASE}/jobs/${jobDetail.job_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patchData),
      })
      if (!res.ok) {
        const errorText = await res.text()
        alert("Failed to save notes: " + errorText)
        return
      }
      alert("Notes saved!")
      reloadJob()
    } catch (err) {
      console.error("Error saving notes:", err)
      alert("Error saving notes: " + err.message)
    }
  }

  const handleSaveDefect = async (updatedDefect) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/defects/${updatedDefect.defect_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          defective_quantity: updatedDefect.defective_quantity,
          defect_reason: updatedDefect.defect_reason
        })
      });
      if (res.ok) {
        alert("Defect updated successfully!");
        setEditingDefect(null);
        reloadJob();
      } else {
        const msg = await parseServerError(res);
        alert(`Error updating defect: ${msg}`);
      }
    } catch (err) {
      alert(`An error occurred: ${err.message}`);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleDeleteDefect = async (defect) => {
    if (submittingRef.current) return;
    if (!window.confirm(`Are you sure you want to delete this defect: "${defect.defect_reason}"?`)) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/defects/${defect.defect_id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const msg = await parseServerError(res);
        alert(msg);
        return;
      }
      alert('Defect deleted successfully.');
      reloadJob();
    } catch (err) {
      alert(`An error occurred: ${err.message}`);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleSaveStagedDefect = async (updatedDefect) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/pending_defects/${updatedDefect.pending_defect_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          defective_quantity: updatedDefect.defective_quantity,
          defect_reason: updatedDefect.defect_reason,
        }),
      });
      if (res.ok) {
        setEditingStagedDefect(null);
        setStagedDefectsReloadKey(k => k + 1);
      } else {
        const msg = await parseServerError(res);
        alert(msg);
      }
    } catch (err) {
      alert(`An error occurred: ${err.message}`);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleDeleteStagedDefect = async (defect) => {
    if (submittingRef.current) return;
    if (!window.confirm(`Delete this staged defect: "${defect.defect_reason}" (qty ${defect.defective_quantity})? It has not yet been committed — no downstream tasks will be affected.`)) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/pending_defects/${defect.pending_defect_id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const msg = await parseServerError(res);
        alert(msg);
        return;
      }
      setStagedDefectsReloadKey(k => k + 1);
    } catch (err) {
      alert(`An error occurred: ${err.message}`);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ position: "relative", padding: "1rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <h3 style={{ marginTop: 0 }}>
            Job #{jobDetail.master_job_num}: {jobDetail.job_description}            
            {isProtected && (
              <span style={{ 
                  marginLeft: '10px', 
                  padding: '4px 8px',
                  backgroundColor: '#6610f2', // Indigo
                  color: 'white',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  verticalAlign: 'middle'
              }}>
                  <i className="bi bi-person-lock me-1"></i> Protected Task
              </span>
            )}
          </h3>
          <strong>Master Job Details</strong>
          <br />
          <p style={{ margin: 0 }}>
            <strong>Priority:</strong> {jobDetail.priority_level} &nbsp;|&nbsp;
            <strong>Due Date:</strong>{" "}
            {jobDetail.due_date ? formatDueDate(jobDetail.due_date) : "—"}
            &nbsp;|&nbsp;
            <strong>Status:</strong> {jobDetail.status}
            {jobDetail.pre_shipped_qty > 0 && (
              <>
                &nbsp;|&nbsp;
                <strong>Pre-shipped:</strong>{" "}
                <span style={{ color: "#198754", fontWeight: "bold" }}>
                  <i className="bi bi-box-seam me-1"></i>
                  {jobDetail.pre_shipped_qty} pc{jobDetail.pre_shipped_qty === 1 ? "" : "s"}
                </span>
              </>
            )}
            {jobDetail.sales_order_number && (
              <>
                <br />
                <strong>Sales Order:</strong> {jobDetail.sales_order_number}
              </>
            )}
            <br />
            {/* Conditional display for Laser Station */}
            {workstation === "Laser" && (
              <span>
                {/* CORRECTED: Use material_description for consistency */}
                <strong>Material:</strong> {wsTask.material_description || "N/A"} &nbsp;|&nbsp;
                <strong>Sheets Needed:</strong> {wsTask.sheet_required || 0}
              </span>
            )}
            {/* Conditional display for a cut-list-level Fabrication task
                (Cutting saw list, or Lathe/Milling machined from the stick) */}
            {workstation === "Fabrication" && !wsTask.part_id && wsTask.cut_list_id && (
              <span>
                <strong>Material:</strong> {wsTask.material_description || "N/A"} &nbsp;|&nbsp;
                {/* CORRECTED: Added " sticks" for clarity */}
                <strong>Stock Pieces:</strong> {wsTask.stock_pieces_needed ? `${wsTask.stock_pieces_needed} sticks` : "N/A"}
              </span>
            )}
          </p>

        </div>
        <div style={{ textAlign: "right", minWidth: "130px" }}>
          {jobDetail.sales_order_number && (
            <div style={{ marginBottom: "0.25rem" }}>
              <span
                title={`Sales Order ${jobDetail.sales_order_number}`}
                style={{
                  padding: '3px 8px',
                  backgroundColor: '#0d6efd',
                  color: 'white',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  display: 'inline-block'
                }}
              >
                <i className="bi bi-receipt me-1"></i>{jobDetail.sales_order_number}
              </span>
            </div>
          )}
          <div style={{ fontWeight: "bold", marginBottom: "0.25rem" }}>
            Estimated: {wsTask.estimated_time ? formatInterval(wsTask.estimated_time) : "--:--"}
          </div>
          <div
            style={{
              fontSize: "1.3rem",
              fontWeight: "bold",
              marginBottom: "0.5rem",
              color: "#333",
            }}
          >
            <PunchElapsedTimer dbElapsedSeconds={dbElapsedSeconds} status={wsTask.status} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
            
              {/* --- LOCKED OVERRIDE MESSAGE --- */}
              {controlsDisabled && (
                  <div className="alert alert-danger py-1 px-2 mb-2 small text-center fw-bold">
                      <i className="bi bi-lock-fill"></i> LOCKED
                  </div>
              )}
              {/* --- MAINTENANCE NUDGE BANNER (Before 2 PM) --- */}
              {showMaintenanceNudge && (
                <div className="alert alert-warning py-2 px-3 mb-2 small" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-1"></i>
                  <strong>Maintenance Due Today:</strong> {maintenanceTitles}.
                  {' '}Please complete before 2:00 PM to avoid production lockout.
                </div>
              )}
              {/* --- FAI PASSED BANNER (Laser Pending tasks where FAI samples cleared) --- */}
              {isFaiPassed && (
                <div className="alert alert-success py-2 px-3 mb-2 small" role="alert">
                  <div className="fw-bold mb-1">
                    <i className="bi bi-check-circle-fill me-1"></i>
                    First Article Inspection — Passed · Ready to Start
                  </div>
                  {faiStatus.override_applied ? (
                    <div className="ms-3">
                      Admin override by <strong>{faiStatus.override_by}</strong>
                      {faiStatus.override_reason ? ` — "${faiStatus.override_reason}"` : ''}
                    </div>
                  ) : (
                    <ul className="mb-0 ps-3">
                      {(faiStatus.parts || []).map(p => (
                        <li key={p.part_id}>
                          <strong>{p.product_id}</strong>: {p.latest_attempt_count}/{p.required_count} samples passed
                          {p.attempts_used > 1 ? ` (attempt ${p.attempts_used})` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {/* --- FAI BANNER (Laser Pending tasks with FAI required) --- */}
              {isFaiGateClosed && (
                <div
                  className={`alert ${isFaiLocked ? 'alert-danger' : (faiPendingParts.some(p => p.attempts_used > 0) ? 'alert-warning' : 'alert-info')} py-2 px-3 mb-2 small`}
                  role="alert"
                >
                  <div className="fw-bold mb-1">
                    <i className={`bi ${isFaiLocked ? 'bi-lock-fill' : 'bi-clipboard-check'} me-1`}></i>
                    {isFaiLocked
                      ? 'FAI LOCKED — Admin Override Required'
                      : (faiPendingParts.some(p => p.attempts_used > 0)
                          ? 'First Article Inspection Failed — Adjust and Retry'
                          : 'First Article Inspection Required Before Start')}
                  </div>
                  <ul className="mb-1 ps-3">
                    {faiPendingParts.map(p => (
                      <li key={p.part_id}>
                        <strong>{p.product_id}</strong>: cut and measure {p.required_count} part(s)
                        {p.attempts_used > 0 && !p.locked && ` — attempt ${p.attempts_used + 1} of 2`}
                        {p.locked && ' — locked after 2 failed attempts'}
                      </li>
                    ))}
                  </ul>
                  <div className="d-flex gap-2 mt-2 flex-wrap">
                    {!isFaiLocked && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          const first = faiPendingParts.find(p => !p.locked);
                          if (first) handleFaiOpenForPart(first.part_id, wsTask.task_id);
                        }}
                      >
                        <i className="bi bi-rulers me-1"></i> Record First Article
                      </button>
                    )}
                    {isFaiLocked && (
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => setShowFaiOverrideModal(true)}
                      >
                        <i className="bi bi-shield-lock me-1"></i> Admin Override
                      </button>
                    )}
                  </div>
                </div>
              )}
            {/* The task's steps (queueRules.taskButtons), in order; a kind appears once per task. */}
            {actionSteps.map((step) => (
              <React.Fragment key={step.kind}>{renderActionStep(step)}</React.Fragment>
            ))}

            {/* --- LASER CONSUMABLE BUTTONS ---
                 Not a step of the task above, so they stay
                 available while a Laser task is paused into Maintenance —
                 swapping a worn nozzle IS a maintenance activity, and the
                 gas/nozzle swap handlers post inventory transactions that
                 don't depend on task status. */}
            {workstation === "Laser" && ['In Progress', 'Paused', 'Pending', 'Maintenance'].includes(wsTask.status) && (
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <button
                        onClick={() => handleGasSwap('N2')}
                        disabled={isSubmitting}
                        style={{
                            backgroundColor: '#0dcaf0',
                            color: '#000',
                            border: '1px solid #0dcaf0',
                            borderRadius: '4px',
                            padding: '0.3rem 0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: 'bold'
                        }}
                    >
                        Swap Nitrogen
                    </button>
                    <button
                        onClick={() => handleGasSwap('O2')}
                        disabled={isSubmitting}
                        style={{
                            backgroundColor: '#0d6efd',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '0.3rem 0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: 'bold'
                        }}
                    >
                        Swap Oxygen
                    </button>
                    <button
                        onClick={handleNozzleSwap}
                        disabled={isSubmitting}
                        style={{
                            backgroundColor: '#6610f2',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '0.3rem 0.5rem',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: 'bold'
                        }}
                    >
                        Swap Nozzle
                    </button>
                </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Flagged Parts Card — surfaces the flag reason persistently so the
          operator can see WHY a part was flagged without dismissing the
          one-shot popup alert. Each flagged part gets a PIN-gated Unflag
          button (PIN validates via /auth/pin for audit identity). */}
      {jobParts && jobParts.some(p => p.flagged) && (
        <div className="alert alert-warning" style={{ marginBottom: "1rem" }}>
          <h5 className="mb-2">
            <i className="bi bi-flag-fill me-2"></i>
            Flagged Part{jobParts.filter(p => p.flagged).length > 1 ? "s" : ""} on this Job
          </h5>
          {jobParts.filter(p => p.flagged).map((part, idx) => (
            <div
              key={part.part_id}
              className="d-flex justify-content-between align-items-start"
              style={{
                paddingTop: idx === 0 ? 0 : "0.5rem",
                marginTop: idx === 0 ? 0 : "0.5rem",
                borderTop: idx === 0 ? "none" : "1px solid rgba(0,0,0,0.1)",
              }}
            >
              <div style={{ flexGrow: 1, paddingRight: "0.75rem" }}>
                <div><strong>{part.product_id}</strong>{part.description ? ` — ${part.description}` : ""}</div>
                <div style={{ marginTop: "0.25rem" }}>
                  <strong>Reason:</strong> {part.flag_reason || <em>(no reason recorded)</em>}
                </div>
                {(part.flagged_by_name || part.flagged_at) && (
                  <div className="text-muted small" style={{ marginTop: "0.15rem" }}>
                    {part.flagged_by_name && <>Flagged by {part.flagged_by_name}</>}
                    {part.flagged_at && (
                      <> on {new Date(part.flagged_at).toLocaleString()}</>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-dark"
                onClick={() => handleUnflagFromCard(part)}
                disabled={isSubmitting}
                style={{ flexShrink: 0 }}
              >
                Unflag
              </button>
            </div>
          ))}
        </div>
      )}

      {/* 1. Maintenance / Shop Health View (Cleanest, highest priority check) */}
      {/* UPDATED: Use the robust isMaintenanceTask check here too for consistency */}
      {isMaintenanceTask ? (
        <MaintenanceDetailSection
          job={jobDetail}
          task={wsTask}
          isSubmitting={isSubmitting}
        />
      ) : isSheetStockLaserTask ? (
        /* 2. Sheet Stock Laser View */
        <LaserDetailSection
          task={wsTask}
          cutSheetDetail={cutSheetDetail}
          materialInfo={materialInfo}
          dropMaterialInfo={dropMaterialInfo}
          customers={customers}
          jobParts={jobParts}
          reloadJob={reloadJob}
          onEnlarge={onEnlarge}
          isSubmitting={isSubmitting}
          setShowPpapModal={setShowPpapModal}
          setPpapPartData={setPpapPartData}
          setPpapTaskId={setPpapTaskId}
          setPpapPhase={setPpapPhase}
          pendingLabelConfirmations={pendingLabelConfs}
          onLabelConfirmationNeeded={openLabelConfirmation}
          isTmJob={isTmJob}
        />
      ) : (
        /* 3. Standard / Shape Stock / Fabrication View */
        <>
          <NonLaserDetailSection
            job={jobDetail}
            task={wsTask}
            jobParts={jobParts}
            reloadJob={reloadJob}
            onEnlarge={onEnlarge}
            isSubmitting={isSubmitting}
            isTmJob={isTmJob}
          />
          {/* the pallets holding this task's parts, with their beacons: Find + map (stations after the laser) */}
          {wsTask && <TaskPalletsPanel taskId={wsTask.task_id} workstation={wsTask.workstation} />}
        </>
      )}

      {/* Editable Job Notes */}
      <div
        style={{
          marginTop: "1rem",
          border: "1px solid var(--jq-border-medium, #ccc)",
          borderRadius: "6px",
          padding: "1rem",
          backgroundColor: "var(--jq-bg-surface, #f9f9f9)",
        }}
      >
        <h5 style={{ marginTop: 0 }}>Job Notes</h5>
        <textarea
          style={{
            width: "100%",
            height: "80px",
            borderRadius: "4px",
            border: localNotes ? "3px solid red" : "1px solid var(--jq-border-medium, #ccc)",
            backgroundColor: localNotes ? "var(--jq-alert-danger-bg, #ffe6e6)" : "var(--jq-bg-card, #fff)",
            padding: "0.5rem",
          }}
          value={localNotes}
          onChange={(e) => setLocalNotes(e.target.value)}
        />
        <div style={{ marginTop: "0.5rem" }}>
          <button
            onClick={handleSaveNotes}
            disabled={isSubmitting}
            style={{
              backgroundColor: "#5bc0de",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "0.5rem 1rem",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Save Notes
          </button>
        </div>
      </div>

      {/* Staged (pending) laser defects — commits on laser task complete */}
      <StagedDefectsList
        jobId={jobDetail.job_id}
        onEdit={setEditingStagedDefect}
        onDelete={handleDeleteStagedDefect}
        isSubmitting={isSubmitting}
        reloadKey={stagedDefectsReloadKey}
      />

      {/* Defects List (Kept as requested) */}
      <DefectsList jobId={jobDetail.job_id} onEdit={setEditingDefect} onDelete={handleDeleteDefect} isSubmitting={isSubmitting} />

      {/* Edit Defect Modal (Kept as requested) */}
      {editingDefect && (
        <EditDefectModal
          defect={editingDefect}
          onSave={handleSaveDefect}
          onClose={() => setEditingDefect(null)}
          isSubmitting={isSubmitting}
        />
      )}

      {editingStagedDefect && (
        <EditDefectModal
          title="Edit Staged Defect"
          defect={editingStagedDefect}
          onSave={handleSaveStagedDefect}
          onClose={() => setEditingStagedDefect(null)}
          isSubmitting={isSubmitting}
        />
      )}

      <div style={{ textAlign: "right", marginTop: "1rem" }}>
        <button
          onClick={handleDownloadPdf}
          disabled={isSubmitting}
          style={{
            backgroundColor: "#343a40",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            padding: "0.5rem 1rem",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          Print Report
        </button>
      </div>

      {/* Activity Log */}
      {activityLog && activityLog.length > 0 && (
        <div style={{ marginTop: "2rem", fontSize: "14px" }}>
          <h5>Activity Log</h5>
          <table
            className="table table-sm"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid var(--jq-border-medium, #ccc)",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "var(--jq-bg-surface, #f9f9f9)" }}>
                <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Timestamp</th>
                <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Action</th>
                <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Old → New</th>
                <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>User</th>
                <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Task ID</th>
                <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Notes</th>
                <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {activityLog.map((entry) => (
                <tr key={entry.job_activity_id}>
                  <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                    {formatTimestampToCST(entry.action_timestamp)}
                  </td>
                  <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                    {entry.action_type}
                  </td>
                  <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                    {entry.old_status} → {entry.new_status}
                  </td>
                  <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                    {entry.user_name || "N/A"}
                  </td>
                  <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                    {entry.task_id || ""}
                  </td>
                  <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                    {formatNoteTimestamps(entry.notes)}
                  </td>
                  <td
                    style={{
                      border: "1px solid var(--jq-border-medium, #ccc)",
                      padding: "0.5rem",
                      textAlign: "center",
                    }}
                  >
                    <button
                      onClick={() => handleEditActivityTimestamp(entry)}
                      disabled={isSubmitting}
                      style={{
                        backgroundColor: "#5bc0de",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        padding: "0.25rem 0.5rem",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                      }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeLabelConf && (
        <RemnantLabelConfirmModal
          key={activeLabelConf.print_job_id}
          confirmation={activeLabelConf}
          intro={labelConfIntro}
          onClose={handleLabelConfClosed}
        />
      )}
    </div>
  );
}

/**
 * LaserDetailSection
 */
function LaserDetailSection({
  task,
  cutSheetDetail,
  materialInfo,
  dropMaterialInfo,
  customers,
  jobParts,
  reloadJob,
  onEnlarge,
  isSubmitting,
  // NEW PPAP PROPS: Pass down the state setters
  setShowPpapModal,
  setPpapPartData,
  setPpapTaskId,
  setPpapPhase,
  // Remnant / drop label questions (DetailView owns the popup; see RemnantLabelConfirmModal)
  pendingLabelConfirmations = [],
  onLabelConfirmationNeeded = null,
  // T&M job (DetailView's isTmJob: the queue row's so_pricing_mode, else jobDetail's): a part or
  // sheet defect asks the neutral cause.
  isTmJob = false,
  // How often the laser camera is read (tests shorten it).
  cameraPollMs = CAMERA_POLL_MS,
}) {
  const [flagged, setFlagged] = useState(false)
  const [flagReason, setFlagReason] = useState("")
  const [flaggedByName, setFlaggedByName] = useState("")
  const [showFlagModal, setShowFlagModal] = useState(false)
  const [showSheetModal, setShowSheetModal] = useState(false);

  // The laser camera records sheets (jobq-api "LASER CAMERA SHEETS", 2026-11-03): GET /laser/{id}/camera every
  // 5 s while this task is open and not finished. Mode off = nothing shown (the screen as before). When the
  // camera counts more sheets than the screen shows, it recorded one: reload the task as a successful Add Sheet
  // does (DetailView then re-reads the remnant / drop label questions, which a camera sheet can create). Once per
  // new count, so a reload that is slow to show it never loops. Never blocks Add Sheet. Above the early return.
  // ⚠ A reload clears the job detail (handleSelectJob), which unmounts this section and the Add Sheet dialog with
  // it: while the dialog is open the reload waits until it closes (its camera line still shows the new count).
  // A successful Add Sheet reloads anyway and records the count it saw (`cameraReloadedForRef`), so no second one.
  const cameraTaskOpen = !!(task && task.task_id) && !["Completed", "Cancelled", "Voided"].includes(task && task.status);
  const camera = useLaserCamera(task ? task.task_id : null, cameraTaskOpen, { pollMs: cameraPollMs });
  const reloadJobRef = useRef(reloadJob);
  reloadJobRef.current = reloadJob;
  const cameraReloadedForRef = useRef(null);     // {taskId, count}: a reload already asked for this count
  const shownSheetCount = task ? task.sheet_count : null;
  const shownTaskId = task ? task.task_id : null;
  useEffect(() => {
    if (!camera || shownTaskId === null || showSheetModal) return;
    if (!cameraRecordedSheet(camera, shownSheetCount, shownTaskId)) return;
    const done = cameraReloadedForRef.current;
    if (done && done.taskId === shownTaskId && Number(camera.sheet_count) <= done.count) return;
    cameraReloadedForRef.current = { taskId: shownTaskId, count: Number(camera.sheet_count) };
    if (reloadJobRef.current) reloadJobRef.current();
  }, [camera, shownSheetCount, shownTaskId, showSheetModal]);

  // PO/Shipment defaults for the Add Sheet modal. Re-read from
  // sessionStorage whenever the task_id changes — `useState` lazy
  // initializers only fire on first mount, so without this effect a
  // long-lived LaserDetailSection (which it is — the component stays
  // mounted across job switches) would keep showing the FIRST task's
  // PO/Shipment defaults forever, even after the operator has saved
  // new values for subsequent tasks.
  const [lastUsedPo, setLastUsedPo] = useState("");
  const [lastUsedShipment, setLastUsedShipment] = useState("");

  useEffect(() => {
    setLastUsedPo(sessionStorage.getItem(`ppap_po_${task?.task_id}`) || "");
    setLastUsedShipment(sessionStorage.getItem(`ppap_ship_${task?.task_id}`) || "");
  }, [task?.task_id]);

  // --- FIXED: Moved hooks up BEFORE the early return check ---
  const [activeTraceInfo, setActiveTraceInfo] = useState(null);

  useEffect(() => {
      async function fetchTrace() {
          if (!task || !task.task_id) return;
          try {
              const res = await fetch(`${API_BASE}/tasks/${task.task_id}/material_trace`);
              if (res.ok) {
                  const data = await res.json();
                  setActiveTraceInfo(data);
              }
          } catch (e) {
              console.error("Error fetching trace info", e);
          }
      }
      fetchTrace();
  }, [task]);
  // ----------------------------------------------------------

  // --- Remote Operation state ---
  const [remoteOpEnabled, setRemoteOpEnabled] = useState(false);
  const [remoteOpBy, setRemoteOpBy] = useState(null);
  const [remoteOpLoading, setRemoteOpLoading] = useState(false);

  useEffect(() => {
    async function fetchRemoteOp() {
      try {
        const res = await fetch(`${API_BASE}/tasks/${task.task_id}/remote_operation`);
        if (res.ok) {
          const data = await res.json();
          setRemoteOpEnabled(!!data.remote_operation);
          setRemoteOpBy(data.enabled_by || null);
        }
      } catch (e) {
        console.error("Error fetching remote operation state", e);
      }
    }
    fetchRemoteOp();
  }, [task?.task_id]);

  async function handleToggleRemoteOp() {
    const newState = !remoteOpEnabled;
    const action = newState ? "ENABLE" : "DISABLE";
    if (!window.confirm(`${action} Remote Operation mode?\n\nWhen enabled, the laser will auto-pause on yellow LED warnings and send SMS alerts.`)) return;

    // Admin-only on the API, and a face never approves (face_signin.py): typed PIN.
    const pin = await pinPrompt("Enter your Admin PIN:", { face: false });
    if (!pin) return;

    setRemoteOpLoading(true);
    try {
      const res = await fetch(`${API_BASE}/tasks/${task.task_id}/remote_operation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: newState, pin })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Unknown error" }));
        alert(detailToString(err.detail) || "Failed to toggle Remote Operation.");
        return;
      }
      const data = await res.json();
      setRemoteOpEnabled(!!data.remote_operation);
      setRemoteOpBy(data.enabled_by || null);
    } catch (e) {
      alert("Error toggling Remote Operation: " + e.message);
    } finally {
      setRemoteOpLoading(false);
    }
  }
  // --- End Remote Operation ---

  // Track which cut_sheet_id we've already alerted about so the flag
  // popup only fires when the operator first opens a flagged cut sheet,
  // not on every auto-refresh that re-fetches the same cut_sheet_id.
  const flaggedCutSheetAlertedRef = useRef(null)

  useEffect(() => {
    if (cutSheetDetail) {
      setFlagged(!!cutSheetDetail.flagged)
      setFlagReason(cutSheetDetail.flag_reason || "")
      setFlaggedByName(cutSheetDetail.flagged_by_name || "")
      const csId = cutSheetDetail.cut_sheet_id || cutSheetDetail.id
      if (cutSheetDetail.flagged && flaggedCutSheetAlertedRef.current !== csId) {
        flaggedCutSheetAlertedRef.current = csId
        alert(
          `WARNING: This cut sheet is flagged!\nReason: ${cutSheetDetail.flag_reason || "No reason provided"
          }`
        )
      }
    }
  }, [cutSheetDetail])


  async function handleFlagToggle(e) {
    const newValue = e.target.checked
    if (!cutSheetDetail) {
      e.preventDefault()
      return
    }
    // If turning ON
    if (!flagged && newValue) {
      setShowFlagModal(true)
    } else if (flagged && !newValue) {
      // If turning OFF
      const confirmClear = window.confirm("Clear the flag and reset reason?")
      if (!confirmClear) {
        e.preventDefault()
        return
      }
      unflagCutSheet()
    }
  }

  async function unflagCutSheet() {
    if (!cutSheetDetail) return
    try {
      const url = `${API_BASE}/cut_sheets/${cutSheetDetail.cut_sheet_id}`
      const payload = {
        flagged: false,
        flag_reason: null,
        flagged_by_name: null,
        flagged_at: null,
      }
      const res = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      alert("Cut sheet unflagged.")
      setFlagged(false)
      setFlagReason("")
      setFlaggedByName("")
      reloadJob()
    } catch (err) {
      alert("Error unflagging cut sheet: " + err.message)
    }
  }

  async function handleSaveFlag({ reason, userName }) {
    if (!cutSheetDetail) return
    try {
      const url = `${API_BASE}/cut_sheets/${cutSheetDetail.cut_sheet_id}`
      const nowIsoUtc = new Date().toISOString()
      const payload = {
        flagged: true,
        flag_reason: reason,
        flagged_by_name: userName,
        flagged_at: nowIsoUtc,
      }
      const res = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      alert("Cut sheet flagged.")
      setFlagged(true)
      setFlagReason(reason)
      setFlaggedByName(userName)
      setShowFlagModal(false)
      reloadJob()
    } catch (err) {
      alert("Error flagging cut sheet: " + err.message)
    }
  }

  if (!task) return <div>No Laser task found.</div>

  function onAddSheetClick() {
    setShowSheetModal(true);
  }

  async function handleSheetModalSave({ poNumber, shipmentId, pin, mtnId, managerPin, managerReason, dockOverridePin, dockOverrideReason,
                                        cameraOverridePin, cameraOverrideReason, cameraOverrideWhat }) {
    try {
      const payload = {
        po_number: poNumber || "",
        shipment_id: parseInt(shipmentId, 10) || 0,
        pin: pin
      };
      if (mtnId) {
        payload.mtn_id = mtnId;
      }
      if (managerPin) {
        // "No Tracking" approved by a manager (the API refuses it otherwise when every sheet is on a lot)
        payload.no_tracking_manager_pin = managerPin;
        payload.no_tracking_reason = managerReason;
      }
      if (dockOverridePin) {
        // The laser dock rule overridden by a manager (dead dock / dead beacon): the typed lot is used
        payload.dock_override_pin = dockOverridePin;
        payload.dock_override_reason = dockOverrideReason;
      }
      if (cameraOverridePin) {
        // The laser camera records sheet 2+ (jobq-api "LASER CAMERA SHEETS"): a manager records a sheet it missed.
        // Independent of the dock override: a request can carry both.
        payload.camera_override_pin = cameraOverridePin;
        payload.camera_override_reason = cameraOverrideReason;
      }

      const postSheet = () =>
        fetch(`${API_BASE}/laser/${task.task_id}/increment_sheet`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });

      // The 409s the dialog answers itself (no alert):
      //  - the laser dock rule (detail {error: 'dock_required', message, ...}): the pallet in the dock changed
      //    between the dialog's check and this save. The dialog shows the message and checks again;
      //  - the laser camera (detail {error: 'camera_records_sheets', message, override?}): the camera records
      //    this sheet. The dialog shows the message as information, and the manager override when offered.
      const refusedBy = async (r) => {
        if (r.status !== 409) return null;
        const text = await r.text();
        const camera = cameraRefusal(text);
        if (camera) return { cameraRefused: camera };
        const refusal = dockRefusal(text);
        if (refusal) return { dockRefused: refusal };
        alert(errorTextToMessage(text));
        return { other: true };
      };
      // A save that carried the camera override and was refused for it (401 / 403, or the reason's 400):
      // the API's sentence goes in the override box. Reads the body only when it may be that.
      const cameraOverrideRefusedBy = async (r) => {
        if (!cameraOverridePin || ![400, 401, 403].includes(r.status)) return { text: null };
        const text = await r.text();
        const failure = cameraOverrideFailure(r.status, text, cameraOverrideWhat);
        return failure ? { failure } : { text };
      };

      let res = await postSheet();
      let answer = await refusedBy(res);
      if (answer) return answer.other ? undefined : answer;
      let overrideAnswer = await cameraOverrideRefusedBy(res);
      if (overrideAnswer.failure) return { cameraOverrideError: overrideAnswer.failure };

      // Depleted-lot guard: the server refuses a tracking number whose lot is
      // already fully used and names the lots that do still have sheets. Show
      // the operator that list and let them either fix the tag or confirm they
      // really are cutting from this stack.
      if (!res.ok && res.status === 400) {
        let errText = overrideAnswer.text !== null ? overrideAnswer.text : await res.text();
        let refusal = noTrackingRefusal(errText);
        if (!refusal) {
          const msg = errorTextToMessage(errText);
          if (!isConfirmableSheetRefusal(msg)) {
            alert(msg);
            return;
          }
          if (!window.confirm(`${msg}\n\nOK = record it against this tracking number anyway.\nCancel = go back and enter a different one.`)) {
            return;
          }
          payload.allow_depleted_lot = true;
          res = await postSheet();
          answer = await refusedBy(res);
          if (answer) return answer.other ? undefined : answer;
          overrideAnswer = await cameraOverrideRefusedBy(res);
          if (overrideAnswer.failure) return { cameraOverrideError: overrideAnswer.failure };
          if (!res.ok && res.status === 400) {
            errText = overrideAnswer.text !== null ? overrideAnswer.text : await res.text();
            refusal = noTrackingRefusal(errText);
            if (!refusal) {
              alert(errorTextToMessage(errText));
              return;
            }
          }
        }
        if (refusal) {
          // Every sheet / piece of this material in stock is on a lot: the dialog asks for a manager's PIN + reason.
          alert(`${refusal.message}\n\nA manager can enter their PIN and a reason in this window to record it anyway.`);
          return { needsManager: refusal.message };
        }
      }

      if (!res.ok) {
        const msg = await parseServerError(res);
        alert(msg);
        return;
      }

      // Persist values for pre-fill
      sessionStorage.setItem(`ppap_po_${task.task_id}`, poNumber || "");
      sessionStorage.setItem(`ppap_ship_${task.task_id}`, shipmentId || "");

      setLastUsedPo(poNumber || "");
      setLastUsedShipment(shipmentId || "");

      // Phase 2: per-sheet drop / remnant labels are auto-enqueued by
      // increment_sheet on the backend (kind='drop_labels_per_sheet',
      // sheet_index = post-increment count). The agent fetches the PDF
      // for THAT specific sheet (with its actual MTN, heat#, PO,
      // shipment) and lpr's it to the ZP 505. Read back the job id from
      // the response if available so it shows in dev console; no alert
      // (Add Sheet is a frequent operation, alert per sheet would be
      // noise).
      let sheetBody = null;
      try { sheetBody = await res.json(); } catch (_) { /* non-fatal */ }
      if (sheetBody && sheetBody.drop_label_print_job_id) {
        console.log(
          `Drop labels for sheet ${sheetBody.sheet_count} queued (#${sheetBody.drop_label_print_job_id}) — agent will print to ZP 505 within ~10s.`
        );
      }
      // Brake-bound jobs also get a per-sheet ONE-SHEET-PER-PALLET
      // pallet label (kind='brake_sheet_label'). Same quiet console
      // read-back as drop labels — the printed label itself is the
      // operator-facing artifact.
      if (sheetBody && sheetBody.brake_label_print_job_id) {
        console.log(
          `Brake sheet-pallet label for sheet ${sheetBody.sheet_count} queued (#${sheetBody.brake_label_print_job_id}) — stick it on this sheet's pallet.`
        );
      }

      // Close modal and reload (the camera's poll then asks for no second reload for this count)
      const savedCount = sheetBody ? Number(sheetBody.sheet_count) : NaN;
      if (Number.isFinite(savedCount)) cameraReloadedForRef.current = { taskId: task.task_id, count: savedCount };
      setShowSheetModal(false);
      reloadJob();

      // This sheet prints a remnant / stock-drop label: the operator must say whether it
      // printed before moving on (a "didn't print" goes to the office with the label).
      if (sheetBody && sheetBody.label_confirmation && onLabelConfirmationNeeded) {
        onLabelConfirmationNeeded(sheetBody.label_confirmation);
      }
    } catch (err) {
      alert("Error adding sheet: " + err.message);
    }
  }

  async function handleDecrementSheet() {
    if (!window.confirm("Subtract 1 sheet from this task's count? (Use this if you accidentally added a sheet that wasn't actually cut.)")) {
      return;
    }
    const pin = await promptForUserPin();
    if (!pin) return;
    try {
      const res = await fetch(`${API_BASE}/laser/${task.task_id}/decrement_sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Backend currently ignores the body; sending PIN forward-compatibly
        // so it gets recorded once the endpoint logs the operator.
        body: JSON.stringify({ pin })
      })
      if (!res.ok) {
        const msg = await parseServerError(res)
        alert(msg)
        return
      }
      reloadJob()
    } catch (err) {
      alert(err.message)
    }
  }

  // Updated handleDefectSheet to use the global promptForUserPin
  async function handleDefectSheet() {
    // 1) Prompt how many sheets
    const qtyStr = prompt("How many sheets are defective?", "1")
    if (!qtyStr) return
    const qty = parseInt(qtyStr, 10) || 0
    if (qty < 1) return

    // 2) Prompt reason (a T&M job: the neutral cause picker instead, required — decision 15 covers
    //    every defect row on a T&M job, and a ruined sheet is priced on the order)
    const tmCause = isTmJob ? await causePrompt({ subject: `${qty} sheet(s)` }) : null
    if (isTmJob && !tmCause) return
    const reason = tmCause ? tmCause.reason : prompt("Reason for defect?", "Sheet Ruined")
    if (!reason) return

    // 2b) Which sheet (2026-10-20): one already recorded on this task, or an extra one? (jobq-api "Defects name
    //     their lot": a recorded sheet of a running task is Subtracted for you; of a completed task it already
    //     came off stock; an extra sheet comes off stock against the stack being cut)
    const sheets = qty > 1 ? "sheets" : "sheet"
    const ended = ["Completed", "Cancelled", "Voided"].includes(task.status)
    const alreadyRecorded = ended
      ? window.confirm(`Was the ruined ${sheets} one of the sheets this task already cut?\n\n`
          + "OK = yes, one it cut: it comes off the task's count (don't Subtract it).\n"
          + `Cancel = no, an extra ${sheets} ${qty > 1 ? "were" : "was"} ruined: it comes off stock.\n\n`
          + "(You can still back out at the PIN.)")
      : window.confirm(`Was the ruined ${sheets} already added with Add Sheet (the last ${qty > 1 ? `${qty} recorded` : "one recorded"})?\n\n`
          + "OK = yes, it was added: it comes off the task's count and off stock (add the replacement as usual; don't Subtract it).\n"
          + "Cancel = no, it was never added: it comes off stock as an extra sheet.\n\n"
          + "(You can still back out at the PIN.)")

    // 3) Prompt user for PIN (using globally defined promptForUserPin)
    const userPin = await promptForUserPin()
    if (!userPin) return

    // 4) Build request body
    const payload = {
      job_id: task.job_id,
      task_id: task.task_id,
      workstation: "Laser",
      defect_reason: reason,
      defective_quantity: qty,
      defect_type: "sheet",
      cascade: true,
      pin: userPin,
      ...(alreadyRecorded ? { consume_scan: true } : {}),
    }

    try {
      // 5) POST to /defects
      const res = await fetch(`${API_BASE}/defects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withCause(payload, tmCause)),
      })
      if (!res.ok) {
        const msg = await parseServerError(res)
        alert(msg || "Error reporting defect.")
        return
      }
      const json = await res.json()
      // the API says which lot the ruined sheet came off and that it left the count (2026-10-20)
      alert(json.message || "Defected entire sheet(s).")

      // 6) Retrieve & print the defect label from /defects/{defect_id}/sheet_label
      if (json.defect_id) {
        try {
          const labelUrl = `${API_BASE}/defects/${json.defect_id}/sheet_label`
          const labelRes = await fetch(labelUrl)
          if (!labelRes.ok) {
            const errTxt = await labelRes.text()
            console.error(errTxt)
            alert("Could not fetch defect label PDF.")
          } else {
            const pdfBlob = await labelRes.blob()
            openPdfInPrintDialog(pdfBlob)
          }
        } catch (errLabel) {
          console.error("Error printing defect label:", errLabel)
          alert("Error printing defect label.")
        }
      }

      // 7) Finally, reload the task/job detail
      reloadJob()
    } catch (err) {
      alert(err.message)
    }
  }

  async function handleDefectPart(part) {
    // 0) Duplicate guard: someone (office or another operator) may have
    // already recorded this damage — decoupled assembly defects don't show
    // in this page's task rows, which is exactly how SO-1102 got the same 7
    // parts entered twice on 2026-08-05. Never blocks — confirm() only.
    try {
      const dRes = await fetch(`${API_BASE}/defects?job_id=${task.job_id}`)
      if (dRes.ok) {
        const existing = (await dRes.json())
          .filter(d => d.part_id === part.part_id && (d.defect_type || 'part') === 'part')
          .filter(d => (Date.now() - new Date(d.created_at).getTime()) < 14 * 86400 * 1000)
        if (existing.length > 0) {
          const lines = existing.slice(0, 4).map(d => {
            const hrs = Math.round((Date.now() - new Date(d.created_at).getTime()) / 3600000)
            const age = hrs < 48 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`
            return `• ${d.defective_quantity} pc(s) by ${d.recorded_by_name || '?'} ${age} — "${(d.defect_reason || '').slice(0, 60)}"`
          }).join('\n')
          const goOn = window.confirm(
            `⚠ DEFECTS ALREADY RECORDED for ${part.product_id} on this job:\n\n${lines}\n\n` +
            `If you're reporting the SAME damage, press Cancel — it's already in the system.\n` +
            `Press OK only to record NEW, additional damage.`)
          if (!goOn) return
        }
      }
    } catch (e) { /* guard is best-effort — never block defect recording */ }

    // 1) Prompt for qty:
    const qtyStr = prompt(`How many defective for product: ${part.product_id}?`, "1")
    if (!qtyStr) return
    const qty = parseInt(qtyStr, 10) || 0
    if (qty < 1) return

    // 2) Prompt for reason (a T&M job: the neutral cause picker instead, required):
    const tmCause = isTmJob ? await causePrompt({ subject: `${part.product_id}: ${qty} pc` }) : null
    if (isTmJob && !tmCause) return
    const reason = tmCause ? tmCause.reason : prompt("Reason for defect?", "Damaged part")
    if (!reason) return

    // 3) Prompt for user PIN:
    const userPin = await promptForUserPin()
    if (!userPin) return

    // 4) Build the payload:
    const payload = {
      job_id: task.job_id,
      task_id: task.task_id,
      workstation: task.workstation,
      defect_reason: reason,
      defective_quantity: qty,
      defect_type: "part",
      pin: userPin,
      part_id: part.part_id,
    }

    try {
      const res = await fetch(`${API_BASE}/defects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withCause(payload, tmCause)),
      })
      if (!res.ok) {
        const errText = await res.text()
        alert(`Error reporting defect: ${errText}`)
        return
      }
      // Heads-up (jobq-api remake_hold_preview, 2026-09-26): what this loss holds back at the join.
      const defectJson = await res.json().catch(() => null)
      if (defectJson && defectJson.remake_hold_preview) {
        await remakeHeadsUp(defectJson.remake_hold_preview, { lead: `Defected ${qty} from product ${part.product_id}.` })
      } else {
        alert(`Defected ${qty} from product ${part.product_id}`)
      }
      reloadJob()
    } catch (err) {
      alert(err.message)
    }
  }

  // NEW: Function to open the PPAP modal
  // phase = 'ongoing' (default) for in-process sampling that gates Complete,
  //         'initial' for First Article Inspection that gates Start.
  const handlePpapMeasureClick = async (partId, taskId, phase = 'ongoing') => {
    try {
        const res = await fetch(`${API_BASE}/parts/${partId}`);
        if (!res.ok) throw new Error("Failed to fetch part details for PPAP.");
        const fullPartData = await res.json();

        if (!fullPartData.ppap_required) {
             alert("PPAP is not required for this part.");
             return;
        }

        // Find if this part is on the cutSheetDetail array (which includes PPAP critical characteristics)
        const csPartData = cutSheetDetail.parts.find(p => p.part_id === partId);
        if(csPartData) {
            fullPartData.ppap_critical_characteristics = csPartData.ppap_critical_characteristics;
            fullPartData.ppap_image_path = csPartData.ppap_image_path;
            fullPartData.ppap_sample_size_ongoing = csPartData.ppap_sample_size_ongoing;
            fullPartData.ppap_sample_size_initial = csPartData.ppap_sample_size_initial;
        }

        setPpapPartData(fullPartData);
        setPpapTaskId(taskId);
        setPpapPhase(phase);
        setShowPpapModal(true);

    } catch (err) {
        alert(err.message);
    }
  };

  function getQtyOnOrder(partId) {
    const match = jobParts.find((jp) => jp.part_id === partId)
    return match ? match.quantity : 0
  }

  const totalSheets = task.sheet_required || 0

  return (
    <div
      style={{
        position: "relative",
        marginTop: "0.5rem",
        border: "1px solid var(--jq-border-medium, #ccc)",
        borderRadius: "6px",
        padding: "1rem",
        backgroundColor: "var(--jq-bg-subtle, #fafafa)",
      }}
    >
      <h5 style={{ marginTop: 0 }}>Laser Task (Task #{task.task_id})</h5>

      {/* --- Remote Operation Toggle --- */}
      <div style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.4rem 0.75rem",
        marginBottom: "0.5rem",
        borderRadius: "6px",
        border: remoteOpEnabled ? "2px solid #28a745" : "1px solid #dee2e6",
        backgroundColor: remoteOpEnabled ? "rgba(40, 167, 69, 0.08)" : "transparent",
        fontSize: "0.85rem",
      }}>
        <span>📡</span>
        <strong>Remote Operation</strong>
        {remoteOpEnabled && remoteOpBy && (
          <span style={{ fontSize: "0.75rem", color: "#6c757d" }}>({remoteOpBy})</span>
        )}
        <label style={{
          position: "relative",
          display: "inline-block",
          width: "44px",
          height: "24px",
          cursor: remoteOpLoading ? "wait" : "pointer",
          opacity: remoteOpLoading ? 0.5 : 1,
          flexShrink: 0,
        }}>
          <input
            type="checkbox"
            checked={remoteOpEnabled}
            onChange={handleToggleRemoteOp}
            disabled={remoteOpLoading}
            style={{ opacity: 0, width: 0, height: 0 }}
          />
          <span style={{
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: remoteOpEnabled ? "#28a745" : "#ccc",
            borderRadius: "24px",
            transition: "background-color 0.3s",
          }}>
            <span style={{
              position: "absolute",
              height: "18px",
              width: "18px",
              left: remoteOpEnabled ? "23px" : "3px",
              bottom: "3px",
              backgroundColor: "white",
              borderRadius: "50%",
              transition: "left 0.3s",
            }} />
          </span>
        </label>
      </div>
      {remoteOpEnabled && (
        <div style={{
          display: "inline-block",
          padding: "0.3rem 0.6rem",
          marginLeft: "0.5rem",
          marginBottom: "0.5rem",
          borderRadius: "4px",
          backgroundColor: "rgba(40, 167, 69, 0.1)",
          border: "1px solid #28a745",
          fontSize: "0.75rem",
          color: "#155724",
          verticalAlign: "middle",
        }}>
          SMS alerts active on yellow + red LED
        </div>
      )}
      {/* --- End Remote Operation Toggle --- */}

      {cutSheetDetail && (
        <div style={{ marginTop: "0.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <strong>Cut Sheet:</strong>
            <span>{cutSheetDetail.cut_sheet_name}</span>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={flagged}
                onChange={handleFlagToggle}
                style={{ marginRight: "4px" }}
              />
              Flag?
            </label>
          </div>
          {flagged && flagReason && (
            <div
              style={{
                marginTop: "0.5rem",
                display: "inline-block",
                width: "fit-content",
                backgroundColor: "var(--jq-alert-danger-bg, #ffcccc)",
                padding: "0.5rem",
                borderRadius: "4px",
                border: "1px solid red",
              }}
            >
              <strong>Flagged!</strong> Reason: {flagReason}{" "}
              {flaggedByName ? `(By: ${flaggedByName})` : ""}
            </div>
          )}
        </div>
      )}

      {pendingLabelConfirmations.length > 0 && (
        <div
          role="alert"
          style={{
            // Left column only: the cut sheet image panel below is absolutely positioned top-right
            // (up to 745px wide) and would otherwise cover the text and the "Answer now" button.
            // 48% like the Material box, but always stopping 1rem short of the panel.
            width: "48%", maxWidth: "calc(100% - 745px - 1rem)", minWidth: "16rem",
            marginTop: "1rem", padding: "0.6rem 0.75rem", borderRadius: "6px",
            backgroundColor: "#f8d7da", border: "1px solid #f1aeb5", color: "#842029",
            display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
          }}
        >
          <span>
            🏷️ <strong>
              {pendingLabelConfirmations.length === 1
                ? "A label is waiting for your answer"
                : `${pendingLabelConfirmations.length} labels are waiting for your answer`}
            </strong>{" "}
            ({pendingLabelConfirmations.map((c) => confirmationTitle(c)).join("; ")}). Say whether it printed
            before completing this task.
          </span>
          {onLabelConfirmationNeeded && (
            <button
              type="button"
              onClick={() => onLabelConfirmationNeeded(pendingLabelConfirmations[0])}
              style={{
                backgroundColor: "#6f42c1", color: "#fff", border: "none", borderRadius: "4px",
                padding: "0.4rem 0.9rem", fontWeight: "bold", cursor: "pointer",
              }}
            >
              Answer now
            </button>
          )}
        </div>
      )}

      <p style={{ marginTop: "1rem" }}>
        <strong>Sheets Required:</strong> {totalSheets}
        <br />
        <strong>Sheets Cut:</strong> {task.sheet_count}
      </p>

      {/* The laser camera: what it is doing for this task (nothing when its switch is off) */}
      <LaserCameraLine
        camera={camera}
        style={{ width: "48%", maxWidth: "calc(100% - 745px - 1rem)", minWidth: "16rem", marginTop: "0.5rem" }}
      />

      <div style={{ display: "flex", flexDirection: "row", gap: "0.5rem", marginTop: "0.75rem" }}>
        <button
          onClick={onAddSheetClick} // Changed handler
          disabled={isSubmitting}
          style={{
            backgroundColor: "#28a745",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            padding: "0.5rem 1rem",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          Add Sheet Count
        </button>
        <button
          onClick={handleDecrementSheet}
          disabled={isSubmitting}
          style={{
            backgroundColor: "#f0ad4e",
            color: "#000",
            border: "none",
            borderRadius: "4px",
            padding: "0.5rem 1rem",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          Subtract Sheet Count
        </button>
        <button
          onClick={handleDefectSheet}
          disabled={isSubmitting}
          style={{
            backgroundColor: "#d9534f",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            padding: "0.5rem 1rem",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          Defect Entire Sheet
        </button>
      </div>

      <div style={{ marginTop: "1rem", padding: "10px", backgroundColor: "var(--jq-bg-subtle, #e9ecef)", borderRadius: "4px", width: "48%" }}>
          <strong>Material:</strong> {task.material_description || "(No material info)"}
          <span className="ms-3">
            <strong>Owner:</strong>{" "}
            {materialInfo && materialInfo.owner_customer_id
              ? (customers.find(c => c.customer_id === materialInfo.owner_customer_id)?.name || "Unknown")
              : "(No owner)"}
          </span>
          
          {/* NEW: Heat Number Display */}
          <div style={{ marginTop: "5px", fontSize: "0.9rem" }}>
              {activeTraceInfo && activeTraceInfo.found ? (
                  <>
                      <span className="me-3"><strong>PO:</strong> {activeTraceInfo.po_number}</span>
                      <span className="me-3"><strong>Shipment:</strong> {activeTraceInfo.shipment_id}</span>
                      <span>
                          <strong>Heat #: </strong> 
                          {activeTraceInfo.heat_number ? (
                              <span className="text-success fw-bold">{activeTraceInfo.heat_number}</span>
                          ) : (
                              <span className="text-danger fw-bold">
                                  ⚠️ No Material Cert Linked
                              </span>
                          )}
                      </span>
                  </>
              ) : (
                  <span className="text-muted font-italic">No sheet usage recorded yet.</span>
              )}
          </div>

          {/* Pallet beacons: flash the ONE pallet to use next */}
          <FindMaterialButton taskId={task.task_id} />
          {/* the Start popup's pallets + beacons, brought back (Find each, map, Try again) */}
          <TaskPalletsButton taskId={task.task_id} status={task.status} />
      </div>

      {cutSheetDetail && (
        <div style={{ marginTop: "1rem" }}>
          {dropMaterialInfo && cutSheetDetail.drop_quantity > 0 && (
            <div style={{ marginBottom: "1rem", width: "48%", marginRight: "1rem" }}>
              <div
                style={{
                  border: "1px solid var(--jq-border-medium, #ddd)",
                  borderRadius: "6px",
                  padding: "0.5rem",
                  background: "var(--jq-bg-card, #fff)",
                }}
              >
                <strong>Drop Material:</strong> {dropMaterialInfo.material_code} -{" "}
                {dropMaterialInfo.description}
                <br />
                <strong>Total Drop Pieces:</strong> {cutSheetDetail.drop_quantity * totalSheets}
                <DropMaterialDefectButton
                  cutSheet={cutSheetDetail}
                  totalSheets={totalSheets}
                  dropMaterialInfo={dropMaterialInfo}
                  reloadJob={reloadJob}
                  isSubmitting={isSubmitting}
                  task={task}
                  onLabelConfirmationNeeded={onLabelConfirmationNeeded}
                />
              </div>
            </div>
          )}
          <RemnantPrintPanel
            cutSheetDetail={cutSheetDetail}
            totalSheets={totalSheets}
            task={task}
            onLabelConfirmationNeeded={onLabelConfirmationNeeded}
          />

          {cutSheetDetail.parts && cutSheetDetail.parts.length > 0 && (
            <div style={{ marginTop: "1rem" }}>
              <h6>Products on this Cut Sheet</h6>
              <div style={{ width: "48%", marginRight: "1rem" }}>
                <table
                  className="table table-sm table-bordered"
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    border: "1px solid var(--jq-border-medium, #ccc)",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "var(--jq-bg-surface, #f9f9f9)" }}>
                      <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Product ID</th>
                      <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Description</th>
                      <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Qty/Sheet</th>
                      <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                        Qty Ordered
                      </th>
                      <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Image</th>
                      {/* NEW COLUMN: PPAP */}
                      <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>PPAP</th> 
                      <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Defect</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cutSheetDetail.parts.map((p) => {
                      const ordered = getQtyOnOrder(p.part_id)
                      // Check if PPAP is required and if the task is in progress/paused
                      const showPpapButton = p.ppap_required && ['In Progress', 'Paused'].includes(task.status);
                      
                      return (
                        <tr key={p.part_id}>
                          <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                            {p.product_id}
                          </td>
                          <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                            {p.part_description}
                          </td>
                          <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                            {p.quantity_per_sheet}
                          </td>
                          <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                            {ordered}
                          </td>
                          <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                            {p.image_path ? (
                              <img
                                src={p.image_path}
                                alt="Part"
                                style={{ maxWidth: "80px", cursor: "pointer" }}
                                onClick={() => onEnlarge(p.image_path)}
                              />
                            ) : (
                              "No image"
                            )}
                          </td>
                          {/* PPAP cell — Record button (status-gated) + View Report button (always visible when ppap_required) */}
                          <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem", textAlign: "center" }}>
                            {p.ppap_required ? (
                                <div className="d-flex flex-column align-items-center gap-1">
                                    {showPpapButton && (
                                        <button
                                            onClick={() => handlePpapMeasureClick(p.part_id, task.task_id)}
                                            disabled={isSubmitting}
                                            style={{
                                                backgroundColor: "#007bff",
                                                color: "#fff",
                                                border: "none",
                                                borderRadius: "4px",
                                                padding: "0.25rem 0.5rem",
                                                cursor: "pointer",
                                                fontSize: "0.75rem",
                                            }}
                                            title={`Record ${p.ppap_sample_size_ongoing} PPAP Samples`}
                                        >
                                            <i className="bi bi-rulers"></i> Record
                                        </button>
                                    )}
                                    <button
                                        onClick={async () => {
                                            try {
                                                const res = await fetch(`${API_BASE}/tasks/${task.task_id}/ppap_measurement_report`);
                                                if (!res.ok) {
                                                    throw new Error(await apiErrorMessage(res));
                                                }
                                                const blob = await res.blob();
                                                const url = URL.createObjectURL(blob);
                                                window.open(url, '_blank');
                                                setTimeout(() => URL.revokeObjectURL(url), 60000);
                                            } catch (err) {
                                                alert(`Could not open PPAP report: ${err.message}`);
                                            }
                                        }}
                                        className="btn btn-sm btn-outline-secondary"
                                        style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem' }}
                                        title="Open the PPAP measurement report PDF (balloon print, characteristics, all recorded samples)"
                                    >
                                        <i className="bi bi-file-earmark-text"></i> Report
                                    </button>
                                </div>
                            ) : (
                                <span className="text-muted" style={{fontSize: '0.75rem'}}>N/A</span>
                            )}
                          </td>
                          {/* END PPAP CELL */}
                          <td
                            style={{
                              border: "1px solid var(--jq-border-medium, #ccc)",
                              padding: "0.5rem",
                              textAlign: "center",
                            }}
                          >
                            <button
                              onClick={() => handleDefectPart(p)}
                              disabled={isSubmitting}
                              style={{
                                backgroundColor: "#d9534f",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                padding: "0.25rem 0.5rem",
                                cursor: "pointer",
                                fontSize: "0.75rem",
                              }}
                            >
                              Defect
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Display cut sheet image, if any */}
      <div
        style={{
          position: "absolute",
          top: "1rem",
          right: "1rem",
          maxWidth: "745px",
          maxHeight: "600px",
          border: "1px solid var(--jq-border-medium, #ddd)",
          borderRadius: "6px",
          padding: "0.5rem",
          boxShadow: "0 2px 3px rgba(0,0,0,0.1)",
          overflow: "auto",
        }}
      >
        {cutSheetDetail && cutSheetDetail.image_path ? (
          <img
            src={cutSheetDetail.image_path}
            alt="Cut Sheet"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "1000px",
              height: "260px",
              display: "block",
              margin: "0 auto",
              objectFit: "contain",
              cursor: "pointer",
              onClick: () => onEnlarge(cutSheetDetail.image_path)
            }}
            onClick={() => onEnlarge(cutSheetDetail.image_path)}
          />
        ) : (
          <div>No Cut Sheet Image</div>
        )}
        {cutSheetDetail && (
          <div
            style={{
              marginTop: "0.5rem",
              border: cutSheetDetail?.notes ? "3px solid red" : "1px solid var(--jq-border-medium, #ccc)",
              backgroundColor: cutSheetDetail?.notes ? "var(--jq-alert-danger-bg, #ffe6e6)" : "var(--jq-bg-surface, #fafafa)",
              borderRadius: "6px",
              padding: "0.5rem",
            }}
          >
            <strong>Cut Sheet Notes:</strong>
            <div style={{ marginTop: "0.5rem" }}>
              {cutSheetDetail?.notes || "(No cut sheet notes)"}
            </div>
          </div>
        )}
      </div>

      {showSheetModal && (
        <SheetTrackingModal 
          isOpen={showSheetModal}
          onClose={() => setShowSheetModal(false)}
          onSave={handleSheetModalSave}
          initialPo={lastUsedPo}
          initialShipment={lastUsedShipment}
          isSubmitting={isSubmitting}
          taskId={task.task_id}
          camera={camera}
        />
      )}
      {showFlagModal && (
        <FlagModal isOpen={showFlagModal} onClose={() => setShowFlagModal(false)} onSave={handleSaveFlag} />
      )}
    </div>
  )
}

// Shared helper for the per-kind print buttons in the laser detail view.
// Enqueues a print job on the backend `print_jobs` queue. The Pi-hosted
// jobq-print-agent picks it up and lpr's it to the ZP 505 thermal
// printer. We do NOT fetch the PDF here — that would route through the
// browser print dialog to the system default (Brother letter), which is
// the wrong destination for 4x6 thermal labels.
// A reprint is a label like any other: when jobq-api says it needs confirming, the operator
// answers the same "did it print?" popup instead of the old "check the Pi" alert.
async function printDropOrRemnantLabels(taskId, kind, onLabelConfirmationNeeded = null) {
  const labelText = kind === "remnant" ? "remnant" : "stock drop";
  const body = {
    station: "laser",
    kind: kind === "remnant" ? "drop_labels_remnant" : "drop_labels_drop",
    pdf_url: `/tasks/${taskId}/drop_labels?kind=${kind}`,
    task_id: taskId,
    params: { trigger: "manual_button", button_kind: kind },
  };
  try {
    const res = await fetch(`${API_BASE}/print/queue`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      if (data.label_confirmation && onLabelConfirmationNeeded) {
        onLabelConfirmationNeeded(data.label_confirmation);
        return;
      }
      const jobId = data.print_job_id ? `#${data.print_job_id}` : "";
      alert(`${labelText[0].toUpperCase()}${labelText.slice(1)} labels queued ${jobId} → ZP 505.\n\nIf nothing prints in ~10s, check the print agent on the Pi:\n  journalctl -u jobq-print-agent -f`);
      return;
    }
    const txt = await res.text().catch(() => "");
    alert(`Could not queue ${labelText} labels (HTTP ${res.status}). ${txt.slice(0, 200)}`);
  } catch (e) {
    console.error(`Network error queuing ${labelText} labels:`, e);
    alert(`Network error queuing ${labelText} labels. Check connectivity and retry.`);
  }
}

function RemnantPrintPanel({ cutSheetDetail, totalSheets, task, onLabelConfirmationNeeded = null }) {
  if (!cutSheetDetail) return null;
  const w = cutSheetDetail.remnant_width;
  const l = cutSheetDetail.remnant_length;
  const q = cutSheetDetail.remnant_quantity;
  if (!w || !l || !q || q <= 0) return null;

  const totalRemnants = q * (totalSheets || 0);
  return (
    <div style={{ marginBottom: "1rem", width: "48%", marginRight: "1rem" }}>
      <div
        style={{
          border: "1px solid var(--jq-border-medium, #ddd)",
          borderRadius: "6px",
          padding: "0.5rem",
          background: "var(--jq-bg-card, #fff)",
        }}
      >
        <strong>Non-Inventory Remnant:</strong> {`${w}" x ${l}" × ${q} per sheet`}
        <br />
        <strong>Total Remnant Pieces:</strong> {totalRemnants}
        <div style={{ marginTop: "0.5rem", display: "flex", flexWrap: "wrap", gap: "5px" }}>
          <button
            onClick={async () => {
              if (!task || !task.task_id) {
                alert("Task ID missing. Please reload.");
                return;
              }
              await printDropOrRemnantLabels(task.task_id, "remnant", onLabelConfirmationNeeded);
            }}
            style={{
              backgroundColor: "#6f42c1",
              color: "white",
              border: "none",
              borderRadius: "4px",
              padding: "0.25rem 0.5rem",
              cursor: "pointer",
              fontSize: "0.75rem",
              fontWeight: "bold",
            }}
          >
            Print Remnant
          </button>
        </div>
      </div>
    </div>
  );
}

function DropMaterialDefectButton({ cutSheet, totalSheets, dropMaterialInfo, reloadJob, isSubmitting, task, onLabelConfirmationNeeded = null }) {

  async function handleDefectDrop() {
    const qtyStr = prompt("How many leftover drop pieces are defective?", "1")
    if (!qtyStr) return
    const qty = parseInt(qtyStr, 10) || 0
    if (qty < 1) return
    const reason = prompt("Reason for defect?", "Drop Ruined")
    if (!reason) return

    const payload = {
      drop_material_id: cutSheet.drop_material_id,
      drop_quantity_defected: qty,
      reason,
      task_id: task ? task.task_id : undefined,     // its sheet lot names the drop's lot (2026-10-20)
    }
    try {
      const res = await fetch(`${API_BASE}/defects/drop_material`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const msg = await parseServerError(res)
        alert(msg || "Error defecting drop material.")
        return
      }
      let dropBody = null
      try { dropBody = await res.json() } catch (_) { /* older API: no body to read */ }
      alert("Defected leftover drop material."
            + (dropBody && dropBody.mtn_id ? ` (TRK ${String(dropBody.mtn_id).padStart(6, "0")})` : ""))
      reloadJob()
    } catch (err) {
      alert(err.message)
    }
  }

  return (
    <div style={{ marginTop: "0.5rem", display: "flex", flexWrap: "wrap", gap: "5px" }}>
      <button
        onClick={handleDefectDrop}
        disabled={isSubmitting}
        style={{
          backgroundColor: "#d9534f",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          padding: "0.25rem 0.5rem",
          cursor: "pointer",
          fontSize: "0.75rem",
        }}
      >
        Defect Drop Material
      </button>

      <button
        onClick={async () => {
          if (!task || !task.task_id) {
            alert("Task ID missing. Please reload.");
            return;
          }
          await printDropOrRemnantLabels(task.task_id, "drop", onLabelConfirmationNeeded);
        }}
        style={{
          backgroundColor: "#17a2b8",
          color: "white",
          border: "none",
          borderRadius: "4px",
          padding: "0.25rem 0.5rem",
          cursor: "pointer",
          fontSize: "0.75rem",
          fontWeight: "bold"
        }}
      >
        Print Stock Drop
      </button>
    </div>
  )
}

/**
 * MaintenanceDetailSection - Simplified view for Shop Health tasks.
 * Hides production data (materials, parts, cut sheets).
 */
/**
 * MaintenanceDetailSection - Simplified view for Shop Health tasks.
 * Hides production data (materials, parts, cut sheets).
 * UPDATED: Includes interactive checklist support.
 */
function MaintenanceDetailSection({ job, task, isSubmitting }) {
  // Deep-copy step objects on every reseed so optimistic updates don't
  // mutate the parent's task.checklist_status array (shallow-spread alone
  // shares inner refs with the prop, leaking changes back into parent state).
  const [checklist, setChecklist] = useState(() =>
    (task.checklist_status || []).map(s => ({...s}))
  );

  const [documents, setDocuments] = useState([]);

  // Sync state if the selected task changes (e.g. switching between maintenance tasks)
  useEffect(() => {
    setChecklist((task.checklist_status || []).map(s => ({...s})));

    // Fetch documents if this task is linked to a maintenance definition
    if (task.maintenance_definition_id) {
        fetch(`${config.API_BASE_URL}/maintenance/definitions/${task.maintenance_definition_id}/documents`)
            .then(res => res.ok ? res.json() : [])
            .then(data => setDocuments(data))
            .catch(err => console.error("Error loading maintenance docs:", err));
    } else {
        setDocuments([]);
    }
  }, [task]);

  const handleToggleStep = async (index, currentVal) => {
    if (isSubmitting) return; // Prevent interaction during submissions

    const newVal = !currentVal;

    // Optimistic UI update — replace the toggled step with a new object
    // to keep state immutable. (Old code did `updatedList[index].completed = newVal`
    // which mutated the inner step object that was potentially shared
    // with parent state.)
    const updatedList = checklist.map((s, i) =>
      i === index ? {...s, completed: newVal} : s
    );
    setChecklist(updatedList);

    try {
        // Using config.API_BASE_URL directly as API_BASE is scoped inside the parent component
        const res = await fetch(`${config.API_BASE_URL}/tasks/${task.task_id}/toggle_step`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ 
                step_index: index, 
                completed: newVal, 
                pin: "" // Optional: passed if user enforcement is needed later
            })
        });
        
        if(!res.ok) {
            throw new Error(await apiErrorMessage(res, "Failed to save step status"));
        }
        
    } catch(err) {
        alert("Error updating checklist: " + err.message);
        // Revert on error — also immutable so parent state isn't poisoned
        const revertedList = checklist.map((s, i) =>
          i === index ? {...s, completed: currentVal} : s
        );
        setChecklist(revertedList);
    }
  };
  
  // Calculate completion stats for the progress bar
  const totalSteps = checklist.length;
  const completedSteps = checklist.filter(s => s.completed).length;
  const percent = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 100;
  
  // Determine if all steps are done (used for visual cues)
  const isComplete = totalSteps === 0 || completedSteps === totalSteps;

  return (
    <div
      style={{
        marginTop: "1rem",
        border: "1px solid #ffc107", // Yellow border to distinguish maintenance
        borderRadius: "6px",
        padding: "1.5rem",
        backgroundColor: "var(--jq-maint-bg, #fffdf5)", // Very light yellow background
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: "1rem" }}>
        <span style={{ fontSize: "1.5rem", marginRight: "0.5rem" }}>🔧</span>
        <h4 style={{ margin: 0, color: "#856404" }}>
          Maintenance: {job.job_description.replace("MAINT: ", "")}
        </h4>
      </div>

      <div className="row">
        <div className="col-md-12">
            
          {/* CHECKLIST CARD (Only renders if steps exist) */}
          {totalSteps > 0 && (
              <div className="card mb-3 border-secondary shadow-sm">
                <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--jq-bg-card, #fff)' }}>
                    <span className="fw-bold text-dark">
                        <i className="bi bi-list-check me-2"></i>Required Procedures
                    </span>
                    <span className={`badge ${isComplete ? 'bg-success' : 'bg-secondary'}`}>
                        {completedSteps}/{totalSteps} Completed
                    </span>
                </div>
                {/* Progress Bar */}
                <div className="progress" style={{height: "6px", borderRadius: 0}}>
                    <div 
                        className={`progress-bar ${isComplete ? 'bg-success' : 'bg-warning'}`} 
                        role="progressbar" 
                        style={{width: `${percent}%`, transition: "width 0.3s ease"}}
                    ></div>
                </div>
                <ul className="list-group list-group-flush">
                    {checklist.map((step, idx) => (
                        <li 
                            key={idx} 
                            className="list-group-item d-flex align-items-center" 
                            style={{ 
                                cursor: "pointer", 
                                backgroundColor: step.completed ? "var(--jq-step-done-bg, #f8fff9)" : "var(--jq-bg-card, #fff)",
                                transition: "background-color 0.2s"
                            }}
                            onClick={() => handleToggleStep(idx, step.completed)}
                        >
                            <div className="form-check">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    checked={!!step.completed} 
                                    onChange={() => {}} // Handled by LI click for better hit area
                                    style={{cursor: "pointer", transform: "scale(1.2)", marginRight: "10px"}}
                                />
                            </div>
                            <span 
                                className={step.completed ? "text-decoration-line-through text-muted" : "fw-bold text-dark"}
                                style={{fontSize: "1rem"}}
                            >
                                {step.text}
                            </span>
                        </li>
                    ))}
                </ul>
              </div>
          )}

          {/* DOCUMENTS SECTION */}
          {documents.length > 0 && (
            <div className="card mb-3 border-info shadow-sm">
                <div className="card-header bg-info text-dark fw-bold">
                    <i className="bi bi-folder2-open me-2"></i>Attached Manuals & Guides
                </div>
                <ul className="list-group list-group-flush">
                    {documents.map(doc => (
                        <li key={doc.document_id} className="list-group-item">
                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center text-decoration-none">
                                <i className="bi bi-file-earmark-pdf fs-4 text-danger me-3"></i>
                                <div>
                                    <div className="fw-bold text-dark">{doc.display_name || doc.original_filename}</div>
                                    <small className="text-muted">Click to view instructions</small>
                                </div>
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
          )}

          <div className="card mb-3">
            <div className="card-header bg-warning text-dark fw-bold">
              Instructions / Description
            </div>
            <div className="card-body">
              <p style={{ whiteSpace: "pre-wrap", fontSize: "1.1rem" }}>
                {job.notes || job.job_description || "No detailed instructions provided."}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className={`alert ${!isComplete && totalSteps > 0 ? 'alert-danger' : 'alert-light'} border`} role="alert">
        {!isComplete && totalSteps > 0 ? (
            <div>
                <strong><i className="bi bi-exclamation-triangle-fill me-2"></i>Action Required:</strong> 
                You must complete all checklist items above before this task can be marked Complete.
            </div>
        ) : (
            <div>
                <strong>Note:</strong> This is a non-production task. Completing this task will reset the maintenance timer (if applicable) and log the activity.
            </div>
        )}
      </div>
    </div>
  );
}

/**
 * NonLaserDetailSection
 */

// Per-operation breakdown for a multi-op machining task. Renders nothing at all
// for an ordinary task, so every existing card is untouched.
//
// The op split is recorded on the punches (time_punches.op_seq) and summed by
// GET /tasks/{id}/ops; without this panel the recorded times are invisible
// outside the API, which makes "did Op 1 actually get timed?" unanswerable on
// the floor.
function TaskOpsPanel({ task }) {
  const [ops, setOps] = React.useState(null)
  const taskId = task?.task_id
  const opCount = task?.op_count
  // Re-fetch when the cursor or status moves, so finishing an op updates the panel.
  const cursor = `${task?.current_op || ''}-${task?.status || ''}`

  React.useEffect(() => {
    if (!taskId || !opCount) { setOps(null); return }
    let cancelled = false
    fetch(`${API_BASE}/tasks/${taskId}/ops`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setOps(d) })
      .catch(() => { if (!cancelled) setOps(null) })
    return () => { cancelled = true }
  }, [taskId, opCount, cursor])

  if (!opCount) return null

  const fmt = (secs) => {
    const s = Math.max(0, Math.round(secs || 0))
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60)
    return h ? `${h}h ${m}m` : `${m}m`
  }
  const STATE = {
    complete: { label: '✔ Done', bg: '#5cb85c' },
    running:  { label: '● Running', bg: '#0275d8' },
    open:     { label: 'Ready', bg: '#f0ad4e' },
    pending:  { label: 'Not started', bg: '#9e9e9e' },
  }
  const rows = ops?.ops || Array.from({ length: opCount }, (_, i) => ({
    op_seq: i + 1, label: `Op ${i + 1}`,
    state: i + 1 < (task.current_op || 1) ? 'complete'
         : i + 1 > (task.current_op || 1) ? 'pending'
         : (task.status === 'In Progress' ? 'running' : 'open'),
    seconds: 0, operators: [],
  }))
  const total = rows.reduce((a, r) => a + (r.seconds || 0), 0)

  return (
    <div style={{ margin: '0.75rem 0 1rem', border: '1px solid #5b2d90', borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ backgroundColor: '#5b2d90', color: '#fff', padding: '0.35rem 0.6rem', fontWeight: 'bold', fontSize: '0.85rem' }}>
        Operations — this task needs {opCount} machine setups and is not finished until the last one is done
      </div>
      <table className="table table-sm mb-0" style={{ fontSize: '0.85rem' }}>
        <thead>
          <tr><th>Operation</th><th>Status</th><th>Time recorded</th><th>Operator(s)</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const st = STATE[r.state] || STATE.pending
            return (
              <tr key={r.op_seq}>
                <td><strong>{r.label}</strong></td>
                <td><span style={{ backgroundColor: st.bg, color: '#fff', borderRadius: 4, padding: '0.1rem 0.4rem', fontSize: '0.75rem', fontWeight: 'bold' }}>{st.label}</span></td>
                <td>{r.seconds ? fmt(r.seconds) : <span className="text-muted">—</span>}</td>
                <td>{r.operators?.length ? r.operators.join(', ') : <span className="text-muted">—</span>}</td>
              </tr>
            )
          })}
          <tr>
            <td colSpan={2}><strong>Total</strong></td>
            <td><strong>{total ? fmt(total) : '—'}</strong></td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  )
}


function NonLaserDetailSection({ job, task, jobParts, reloadJob, onEnlarge, isSubmitting, isTmJob }) {
  const [showPartFlagModal, setShowPartFlagModal] = useState(false)
  const [selectedPartToFlag, setSelectedPartToFlag] = useState(null)
  const [siblingFabTasks, setSiblingFabTasks] = useState([]);
  const [machineCount, setMachineCount] = useState(task.machine_multiplier || 1);
  // Part inspector for the cut-list task table — same modal the SO page uses.
  const [inspectedPartId, setInspectedPartId] = useState(null);

  useEffect(() => {
    setMachineCount(task.machine_multiplier || 1);
  }, [task]);

  // Helper boolean to check if we should show blast times
  const isBlastStation = task.workstation === "Blast";

  // Attach / detach a replacement job by hand (jobq-api 2026-09-26): for a replacement the
  // matcher can't see — a stock job, a saw-cut cut list, a job loaded after the sets closed.
  // Admin/Manager PIN (never auto-filled) + a reason, like every remake-hold action.
  async function remakeManagerPinAndReason(pinLabel, reasonLabel) {
    const pin = await pinPrompt(pinLabel, { autofill: false })
    if (!pin) return null
    const reason = window.prompt(`${reasonLabel} (at least 5 characters)`, "")
    const trimmed = (reason || "").trim()
    if (trimmed.length < 5) {
      if (reason !== null) alert("A reason of at least 5 characters is needed. Nothing was changed.")
      return null
    }
    return { pin, reason: trimmed }
  }

  async function handleAttachReplacement(r) {
    let data = null
    try {
      const res = await fetch(`${API_BASE}/remake_demands/${r.demand_id}/link_candidates`)
      if (!res.ok) {
        alert(errorTextToMessage(await res.text()))
        return
      }
      data = await res.json()
    } catch (err) {
      alert(err.message)
      return
    }
    const pick = await remakeAttachPicker(data.demand, data.candidates || [])
    if (!pick) return
    const auth = await remakeManagerPinAndReason(
      "Admin or Manager PIN to attach this replacement job:",
      `Why is job ${pick.label} the replacement for ${data.demand.product_id}?`
    )
    if (!auth) return
    try {
      const res = await fetch(`${API_BASE}/remake_demands/${r.demand_id}/links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pin: auth.pin,
          reason: auth.reason,
          qty: pick.qty,
          makeup_job_id: pick.makeup_job_id ?? null,
          makeup_job_num: pick.makeup_job_num ?? null,
        }),
      })
      const text = await res.text()
      if (!res.ok) {
        alert(`The replacement job was not attached: ${errorTextToMessage(text)}`)
        return
      }
      let out = null
      try { out = JSON.parse(text) } catch { /* keep null */ }
      alert((out && out.message) || "Replacement job attached.")
      reloadJob()
    } catch (err) {
      alert(err.message)
    }
  }

  async function handleDetachReplacement(r) {
    if (!window.confirm(
      `Take job #${r.makeup_job_num} off as the replacement for ${r.product_id}?\n\n` +
      "Only if those parts are NOT coming to this job. If they're already at the weld table, keep the link."
    )) return
    const auth = await remakeManagerPinAndReason(
      "Admin or Manager PIN to remove this replacement job:",
      `Why is job #${r.makeup_job_num} no longer the replacement?`
    )
    if (!auth) return
    try {
      const res = await fetch(`${API_BASE}/remake_links/${r.link_id}/detach`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: auth.pin, reason: auth.reason }),
      })
      const text = await res.text()
      if (!res.ok) {
        alert(`The replacement job was not removed: ${errorTextToMessage(text)}`)
        return
      }
      let out = null
      try { out = JSON.parse(text) } catch { /* keep null */ }
      alert((out && out.message) || "Replacement job removed.")
      reloadJob()
    } catch (err) {
      alert(err.message)
    }
  }

  // Incoming remakes — this job is the SOURCE of remake demands. Shared
  // across all three render paths (standard / assembly not-joined /
  // assembly joined) so the join-station operator always sees what
  // replacement parts are coming and which makeup job cuts them.
  const incomingRemakesCard = (
    <>
      {task.workstation === 'Fabrication' && Array.isArray(job.incoming_remakes) && job.incoming_remakes.length > 0 && (
        <div className="alert" style={{ marginTop: '1rem', border: '2px solid #5a5adb', backgroundColor: 'rgba(90,90,219,0.08)', borderRadius: '6px', padding: '0.75rem' }}>
          <h6 style={{ marginTop: 0, color: '#4343b8' }}>
            <i className="bi bi-arrow-repeat me-1"></i>
            Incoming Replacement Parts (Remakes)
          </h6>
          {/* This set's own hold (jobq-api _remake_hold_gate): the same answer Complete gets. */}
          {task.remake_hold && (
            <div
              className={`alert py-2 mb-2 ${
                task.remake_hold.level === 'block' ? 'alert-danger'
                  : task.remake_hold.level === 'confirm' ? 'alert-warning' : 'alert-secondary'}`}
              style={{ fontSize: '0.9rem' }}
            >
              <strong>
                {task.remake_hold.level === 'block' ? '⛔ On hold: '
                  : task.remake_hold.level === 'confirm' ? '⏳ Replacements on the way: ' : 'Heads up: '}
              </strong>
              {task.remake_hold.message}
            </div>
          )}
          <table className="table table-sm mb-1" style={{ backgroundColor: 'transparent' }}>
            <thead>
              <tr><th>Part</th><th>Needed</th><th>Cut</th><th>Makeup Job</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {job.incoming_remakes.map((r, i) => (
                <tr key={i}>
                  <td><strong>{r.product_id}</strong></td>
                  <td>{r.qty_needed}</td>
                  <td>{r.qty_covered}</td>
                  <td>{r.makeup_job_num ? `#${r.makeup_job_num}` : <span className="text-danger fw-bold">not cut yet</span>}</td>
                  <td>
                    {r.status === 'Covered'
                      ? <span className="badge bg-success">{r.makeup_job_status === 'Completed' ? 'Cut — arriving' : 'Being cut'}</span>
                      : <span className="badge bg-warning text-dark">Waiting on makeup sheet</span>}
                  </td>
                  <td className="text-nowrap">
                    {r.gap > 0 && job.incoming_remakes.findIndex((x) => x.demand_id === r.demand_id) === i && (
                      <button type="button" className="btn btn-sm btn-outline-primary"
                        title="Attach the job that made these replacements (manager)"
                        onClick={() => handleAttachReplacement(r)}>
                        Attach job…
                      </button>
                    )}
                    {r.link_id && r.makeup_job_num && (
                      <button type="button" className="btn btn-sm btn-outline-danger ms-1"
                        title={`Take job #${r.makeup_job_num} off as the replacement (manager)`}
                        onClick={() => handleDetachReplacement(r)}>
                        Detach
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <small className="text-muted">Replacements for defected parts on this job — they arrive with a violet REMAKE slip. Weld them into the set; only a manager closes a set short (when the customer takes fewer).</small>
        </div>
      )}
    </>
  );


  // Same pattern for the requested coating color: only the Coating station needs
  // it, and that's where the operator hands parts to / receives them from the coater.
  const isCoatingStation = task.workstation === "Coating";

  // For assembly data
  const [assemblyData, setAssemblyData] = useState(null)
  // Job-scoped BOM from GET /assemblies/{id}/bom — the SAME payload the printed report
  // is built from, so the screen and the paper cannot disagree about a rack's contents
  // or about which items are ready.
  const [assemblyBom, setAssemblyBom] = useState(null)
  const [assemblyConsumables, setAssemblyConsumables] = useState([]);

  // ** FIX STARTS HERE **
  // Define the set of fabrication sub-processes to correctly determine the join rank.
  const fabricationSubProcesses = new Set(['Cutting', 'Drilling', 'Milling', 'Tapping', 'Lathe', 'Welding', 'Assembly']);
  // ** FIX ENDS HERE **
  // Cut-list-level task: one task covering every part on the list, so there is
  // no single linked part. sub_process is the list's fab_subprocess — 'Cutting'
  // for a saw cut-to-length list, 'Lathe'/'Milling' when the parts are machined
  // straight off the stick. Gating on 'Cutting' sent Lathe lists down the
  // !relevantPart path and rendered "No specific part linked to this sub-job."
  const isCutListSummaryTask = task.workstation === 'Fabrication' && !task.part_id && task.parts && task.parts.length > 0;
  const cutListOpLabel = task.sub_process || 'Cutting';


  useEffect(() => {
    if (task && task.workstation === 'Fabrication' && task.part_id) {
      async function fetchSiblingTasks() {
        try {
          const res = await fetch(`${API_BASE}/jobs/${task.job_id}/parts/${task.part_id}/fabrication_tasks`);
          if (res.ok) {
            const data = await res.json();
            setSiblingFabTasks(data);
          }
        } catch (err) {
          console.error("Failed to fetch sibling fabrication tasks:", err);
        }
      }
      fetchSiblingTasks();
    } else {
      setSiblingFabTasks([]); // Clear when not a fab task
    }
  }, [task]); // Rerun when the selected task changes

  // Toggle part flag
  async function handleTogglePartFlag(part) {
    if (part.flagged) {
      // Already flagged => unflag
      const confirmClear = window.confirm("Clear the flag and reset reason?")
      if (!confirmClear) return
      unflagPart(part)
    } else {
      // Not flagged => open modal
      setSelectedPartToFlag(part)
      setShowPartFlagModal(true)
    }
  }

  async function unflagPart(part) {
    try {
      const payload = {
        flagged: false,
        flag_reason: null,
        flagged_by_name: null,
        flagged_at: null,
      }
      const res = await fetch(`${API_BASE}/parts/${part.part_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      alert("Part unflagged.")
      part.flagged = false
      part.flag_reason = ""
      part.flagged_by_name = ""
      part.flagged_at = ""
      reloadJob()
    } catch (err) {
      alert("Error unflagging part: " + err.message)
    }
  }

  async function saveFlag(part, reason, userName) {
    try {
      const payload = {
        flagged: true,
        flag_reason: reason,
        flagged_by_name: userName,
        flagged_at: new Date().toISOString(),
      }
      const res = await fetch(`${API_BASE}/parts/${part.part_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      alert("Part flagged.")
      part.flagged = true
      part.flag_reason = reason
      part.flagged_by_name = userName
      part.flagged_at = new Date().toISOString()
      reloadJob()
    } catch (err) {
      alert("Error flagging part: " + err.message)
    }
  }

  async function handleToggleMachines() {
    const newCount = machineCount === 1 ? 2 : 1;
    const pin = await promptForUserPin(); // Reuse your existing helper
    if (!pin) return;

    try {
        const res = await fetch(`${API_BASE}/tasks/${task.task_id}/set_multiplier`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ multiplier: newCount, pin: pin })
        });

        if (!res.ok) throw new Error(await apiErrorMessage(res));

        setMachineCount(newCount);
        alert(`✅ Switched to ${newCount} Machine(s)`);
        reloadJob(); // Refresh to ensure backend time sync
    } catch (e) {
        alert(e.message);
    }
}

  // Attempt to load assembly info if relevantPart is found
  const relevantPart = jobParts.find((p) => p.part_id === task.part_id) || null

  useEffect(() => {
    // **MODIFICATION START**
    // Reset state when the relevant part changes to avoid showing stale data.
    setAssemblyData(null);
    setAssemblyConsumables([]);
    setAssemblyBom(null);
  
    if (!relevantPart || !relevantPart.assemblies || relevantPart.assemblies.length === 0) {
      return;
    }
  
    async function loadAssemblyDetails() {
      // Use the assembly ID from the part data, which now comes from the API.
      const assemblyId = relevantPart.assemblies[0].assembly_id;
  
      try {
        // Fetch all assembly-related data in parallel for better performance.
        const bomQs = new URLSearchParams();
        if (task?.job_id) bomQs.set('job_id', task.job_id);
        if (relevantPart?.part_id) bomQs.set('part_id', relevantPart.part_id);
        const [headerRes, componentsRes, consumablesRes, bomRes] = await Promise.all([
          fetch(`${API_BASE}/assemblies/${assemblyId}`),
          fetch(`${API_BASE}/assemblies/${assemblyId}/components`),
          fetch(`${API_BASE}/assemblies/${assemblyId}/consumable-components`),
          fetch(`${API_BASE}/assemblies/${assemblyId}/bom?${bomQs.toString()}`)
        ]);

        // Non-fatal: the panel still renders its catalog view without readiness.
        if (bomRes.ok) {
          try { setAssemblyBom(await bomRes.json()); } catch { setAssemblyBom(null); }
        }
  
        if (!headerRes.ok) {
          console.error("Failed to fetch assembly header");
          return;
        }
        const assemblyHeader = await headerRes.json();
  
        // Handle components (fabricated parts)
        if (componentsRes.ok) {
          const subComponents = await componentsRes.json();
          // -- CHANGE START --
          // The primary part details, now using primary_part_quantity from the fetched header.
          const primaryPartAsComponent = {
            ...relevantPart,
            assembly_component_id: `primary-${relevantPart.part_id}`,
            quantity_per_assembly: assemblyHeader.primary_part_quantity || 1, // Use the new quantity field here
            is_primary: true,
          };
          // -- CHANGE END --
          assemblyHeader.components = [primaryPartAsComponent, ...subComponents];
        } else {
            assemblyHeader.components = [];
        }
  
        // Handle consumables (purchased parts)
        if (consumablesRes.ok) {
          const consumableData = await consumablesRes.json();
          setAssemblyConsumables(consumableData);
        }
  
        // Set the complete assembly data in state.
        setAssemblyData(assemblyHeader);
  
      } catch (err) {
        console.error("Assembly fetch error:", err);
        setAssemblyData(null); // Clear data on error
      }
    }
  
    loadAssemblyDetails();
    // **MODIFICATION END**
  }, [relevantPart]);


  // A part defect at a non-Laser station: features/queue/taskActions.js recordPartDefect (shared
  // with the phone queue). isTmJob is DetailView's decision (queue row first, then jobDetail);
  // undefined lets the action decide from `job`.
  async function handleDefectPart(part) {
    const result = await recordPartDefect({ job, task, part, isTmJob, ui: desktopUi })
    if (result.reload) reloadJob()
  }

  // A purchased component (consumables row — rivnuts, inserts, hardware)
  // destroyed while joining the assembly. POST /defects with consumable_id
  // takes the API's consumable branch: quality row + the pieces come out of
  // consumable stock in one call (the join-station decrement only ever books
  // qty/assembly × assemblies, so a stripped rivnut was invisible to stock
  // until 2026-09-04 — 2× 3E-4304 on job 9477).
  async function handleDefectConsumable(comp) {
    if (!comp || comp.consumable_id == null) return
    const label = comp.product_id || `Component #${comp.consumable_id}`

    // Duplicate guard — decoupled rows never show on task rows, so ask first.
    try {
      const dRes = await fetch(`${API_BASE}/defects?job_id=${job.job_id}`)
      if (dRes.ok) {
        const existing = recentConsumableDefects(await dRes.json(), comp.consumable_id)
        if (existing.length > 0) {
          const goOn = window.confirm(
            `⚠ COMPONENT DEFECTS ALREADY RECORDED for ${label} on this job:\n\n${describeRecentDefects(existing)}\n\n` +
            `If you're reporting the SAME loss, press Cancel — it's already in the system.\n` +
            `Press OK only to record NEW, additional destroyed pieces.`)
          if (!goOn) return
        }
      }
    } catch (e) { /* guard is best-effort — never block defect recording */ }

    const qty = parseDefectQty(prompt(
      `How many ${label} were destroyed / scrapped on this task?\n\n` +
      `Each one is taken out of consumable stock on top of the ${comp.quantity_per_assembly} per assembly the build uses.`, "1"))
    if (!qty) return

    const reason = prompt("Reason for the loss?", "Stripped / damaged during install")
    if (!reason) return

    const userPin = await promptForUserPin()
    if (!userPin) return

    const payload = buildConsumableDefectPayload({
      jobId: job.job_id,
      taskId: task.task_id,
      workstation: task.workstation,
      consumableId: comp.consumable_id,
      qty,
      reason,
      pin: userPin,
    })

    try {
      const res = await fetch(`${API_BASE}/defects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const msg = await parseServerError(res)
        alert(`Error recording component defect: ${msg}`)
        return
      }
      const json = await res.json().catch(() => null)
      alert(describeConsumableDefectResult(json, label))
      reloadJob()
    } catch (err) {
      alert(err.message)
    }
  }

  if (isCutListSummaryTask) {
    return (
      <div style={{ marginTop: "1rem", border: "1px solid var(--jq-border-medium, #ccc)", borderRadius: "6px", padding: "1rem", backgroundColor: "var(--jq-bg-subtle, #fafafa)" }}>
        <h5 style={{ marginTop: 0 }}>{cutListOpLabel} Task (Task #{task.task_id})</h5>
        <TaskOpsPanel task={task} />
        <p>
          {cutListOpLabel === 'Cutting'
            ? 'This task involves cutting the following parts from the specified stock material.'
            : `This task produces the following parts on the ${cutListOpLabel.toLowerCase()} directly from the specified stock material.`}
        </p>
        <table className="table table-sm table-bordered" style={{ marginTop: '1rem' }}>
          <thead>
            <tr>
              <th>Product ID</th>
              <th>Description</th>
              <th>Part Cut Length (in)</th>
              <th>Qty Per Stock Length</th>
              <th>Pieces This Task</th>
              <th>Defect</th>
            </tr>
          </thead>
          <tbody>
            {task.parts.map(part => (
              <tr key={part.part_id}>
                <td>
                  <ProductIdLink
                    productId={part.product_id}
                    partId={part.part_id}
                    onInspect={setInspectedPartId}
                  />
                </td>
                <td>{part.description}</td>
                <td>{part.size_length ? part.size_length.toFixed(4) : 'N/A'}</td>
                <td>{part.quantity_per_length}</td>
                {/* task.quantity on a cut-list task counts STOCK LENGTHS, not
                    pieces (API: "quantity is already sticks, whatever the op").
                    Defects are entered in PIECES, so spell the piece count out
                    here — it is the number the operator defects against. */}
                <td>
                  {(part.quantity_per_length && task.quantity)
                    ? part.quantity_per_length * task.quantity
                    : '--'}
                </td>
                <td style={{ textAlign: "center" }}>
                  <button
                    onClick={() => handleDefectPart(part)}
                    disabled={isSubmitting}
                    style={{
                      backgroundColor: "#d9534f",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      padding: "0.25rem 0.5rem",
                      cursor: "pointer",
                      fontSize: "0.75rem",
                    }}
                  >
                    Defect
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Part inspector — opens when a product id in the table is clicked. */}
        {inspectedPartId && (
          <PartDetailModal
            apiBase={API_BASE}
            partId={inspectedPartId}
            onClose={() => setInspectedPartId(null)}
          />
        )}
      </div>
    );
  }

  if (!relevantPart) {
    return (
      <div
        style={{
          marginTop: "1rem",
          border: "1px solid var(--jq-border-medium, #ccc)",
          borderRadius: "6px",
          padding: "1rem",
          backgroundColor: "var(--jq-bg-subtle, #fafafa)",
        }}
      >
        <h5 style={{ marginTop: 0 }}>{task.workstation} Task (Task #{task.task_id})</h5>
        <p>No specific part linked to this sub-job.</p>
      </div>
    )
  }

  // GET /jobs/{id}/parts returns job_parts.quantity aliased as `quantity_ordered`.
  // Reading `.quantity` alone made this undefined, which floored to 0 and printed
  // "Assemblies Ordered: 0" on every assembly card (job 9537) and a blank Qty cell.
  const originalQty = relevantPart.quantity ?? relevantPart.quantity_ordered
  const remaining = task.remaining;


  // Look a table row up in the job-scoped BOM by product_id. The catalog rows
  // (assemblyData.components / assemblyConsumables) and the BOM rows are built by
  // different queries, so match on the one field both carry.
  const bomRowFor = (row) => {
    if (!assemblyBom || !row) return null;
    const pid = row.product_id || row.consumable_product_id;
    if (!pid) return null;
    return (assemblyBom.rows || []).find((r) => r.product_id === pid) || null;
  };

  // The per-task quantity column names itself after what the task actually is, so the
  // operator never has to work out whether the number in front of them covers their
  // set or the whole order. A split build says "This Set"; an un-split one says
  // "This Task" (and then it equals This Job, which is correct, not redundant).
  const thisTaskColLabel = isAssemblySetRow(task) ? "This Set" : "This Task";
  const thisTaskColTitle = isAssemblySetRow(task)
    ? `Pieces THIS SET needs — set ${task.set_identifier} builds ${assembliesOnTask(task, assemblyData?.primary_part_quantity)} assembly(s). This is the count to collect.`
    : "Pieces THIS TASK needs. This is the count to collect.";

  if (assemblyData) {
    // 1. Get the dynamic process order from the part definition
    // Default to a standard flow if process_steps is missing
    const rawSteps = relevantPart?.process_steps || "Laser,Cutting,Drilling,Milling,Tapping,Lathe,Welding,Assembly,Shipping";
    
    // Create an ordered list of steps from the DB string
    const dynamicStepOrder = rawSteps.split(',').map(s => s.trim());

    // 2. Identify Current vs. Join Stations
    // If we are in Fabrication, we look at the sub_process (e.g., "Welding"). 
    // If it's a main station (e.g., "Brake"), we use that.
    const currentStepName = (task.workstation === 'Fabrication' && task.sub_process) 
        ? task.sub_process 
        : task.workstation;
        
    const joinStepName = assemblyData.join_workstation;

    // 3. Determine Ranks dynamically
    // We find the index in the array. -1 means "not found".
    const currentRank = dynamicStepOrder.indexOf(currentStepName);
    const joinRank = dynamicStepOrder.indexOf(joinStepName);

    let isJoinedNow;
    if (currentRank !== -1 && joinRank !== -1) {
        // Both stations are in the part's own process flow — its ordering is
        // authoritative (correctly sequences Fabrication sub-processes like
        // Drilling before Welding, which a global rank table can't).
        isJoinedNow = currentRank >= joinRank;
    } else if (joinStepName) {
        // Station missing from the part's process_steps (e.g. a Brake task
        // added to a job whose part lists "Laser,Drilling,Welding,…" — prod
        // task #33673). The old default-to-Joined showed full assembly info
        // at pre-join stations; rank by the canonical station table instead,
        // mirroring the backend PDF's is_post_join.
        isJoinedNow = isAtOrAfterJoin(currentStepName, joinStepName);
    } else {
        // No join station on the assembly — keep the legacy joined view.
        isJoinedNow = true;
    }


    // If not joined yet, just show the single part
    if (!isJoinedNow) {
      return (
        <div
          style={{
            marginTop: "1rem",
            border: "1px solid var(--jq-border-medium, #ccc)",
            borderRadius: "6px",
            padding: "1rem",
            backgroundColor: "var(--jq-bg-subtle, #fafafa)",
          }}
        >
          <h5 style={{ marginTop: 0 }}>{task.workstation} Task (Task #{task.task_id})</h5>
          <TaskOpsPanel task={task} />
          {incomingRemakesCard}
          <p style={{ margin: 0 }}>
            <strong>Assembly is NOT joined yet.</strong>
          </p>
          <div style={{ marginTop: "1rem" }}>
            <table
              className="table table-sm table-bordered"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                border: "1px solid var(--jq-border-medium, #ccc)",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "var(--jq-bg-surface, #f9f9f9)" }}>
                  <th style={{ padding: "0.5rem" }}>Product ID</th>
                  <th style={{ padding: "0.5rem" }}>Description</th>
                  
                  {/* --- NEW: Conditionally render Blast Headers --- */}
                  {isBlastStation && (
                    <>
                      <th style={{ padding: "0.5rem", color: "#d63384" }}>Blast Top</th>
                      <th style={{ padding: "0.5rem", color: "#d63384" }}>Blast Bot</th>
                    </>
                  )}
                  {/* ----------------------------------------------- */}
                  {isCoatingStation && (
                    <th style={{ padding: "0.5rem" }}>Coat Color</th>
                  )}

                  <th style={{ padding: "0.5rem" }}>Original Order</th>
                  <th style={{ padding: "0.5rem" }}>Task Remaining</th>
                  <th style={{ padding: "0.5rem" }}>Image</th>
                  <th style={{ padding: "0.5rem" }}>Flag</th>
                  <th style={{ padding: "0.5rem" }}>Defect</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: "0.5rem" }}>
                    {relevantPart.flagged && <span style={{ color: "red", marginRight: "4px" }}>🚩</span>}
                    {relevantPart.product_id}
                  </td>
                  <td style={{ padding: "0.5rem" }}>{relevantPart.description}</td>

                  {/* --- NEW: Conditionally render Blast Data --- */}
                  {isBlastStation && (
                    <>
                      <td style={{ padding: "0.5rem", fontWeight: "bold", color: "#d63384" }}>
                        {relevantPart.blast_time_top || "--:--"}
                      </td>
                      <td style={{ padding: "0.5rem", fontWeight: "bold", color: "#d63384" }}>
                        {relevantPart.blast_time_bottom || "--:--"}
                      </td>
                    </>
                  )}
                  {/* --------------------------------------------- */}
                  {isCoatingStation && (
                    <td style={{ padding: "0.5rem", fontWeight: "bold" }}>
                      {relevantPart.coating_color_code ? (
                        <CoatingSwatch
                          code={relevantPart.coating_color_code}
                          name={relevantPart.coating_color_name}
                          hex={relevantPart.coating_color_hex}
                        />
                      ) : (
                        <span style={{ color: "#d63384" }}>not set</span>
                      )}
                    </td>
                  )}

                  <td style={{ padding: "0.5rem" }}>{originalQty}</td>
                  <td style={{ padding: "0.5rem" }}>{task.remaining !== undefined ? task.remaining : "--"}</td>
                  <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                    {relevantPart.image_path ? (
                      <img
                        src={relevantPart.image_path}
                        alt="Part"
                        style={{ maxWidth: "120px", cursor: "pointer" }}
                        onClick={() => onEnlarge(relevantPart.image_path)}
                      />
                    ) : (
                      "No image"
                    )}
                  </td>
                  <td
                    style={{
                      border: "1px solid var(--jq-border-medium, #ccc)",
                      padding: "0.5rem",
                      textAlign: "center",
                    }}
                  >
                    <button
                      onClick={() => handleTogglePartFlag(relevantPart)}
                      style={{
                        backgroundColor: relevantPart.flagged ? "#dc3545" : "#007bff",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        padding: "0.25rem 0.5rem",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                      }}
                    >
                      {relevantPart.flagged ? "Unflag" : "Flag"}
                    </button>
                  </td>
                  <td
                    style={{
                      border: "1px solid var(--jq-border-medium, #ccc)",
                      padding: "0.5rem",
                      textAlign: "center",
                    }}
                  >
                    <button
                      onClick={() => handleDefectPart(relevantPart)}
                      disabled={isSubmitting}
                      style={{
                        backgroundColor: "#d9534f",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        padding: "0.25rem 0.5rem",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                      }}
                    >
                      Defect
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {showPartFlagModal && selectedPartToFlag && (
            <PartFlagModal
              isOpen={showPartFlagModal}
              onClose={() => setShowPartFlagModal(false)}
              onSave={({ reason, userName }) => {
                setShowPartFlagModal(false)
                saveFlag(selectedPartToFlag, reason, userName)
              }}
            />
          )}
        </div>
      )
    } else {
      // Assembly is joined => show sub-components
      return (
        <div
          style={{
            marginTop: "1rem",
            border: "1px solid var(--jq-border-medium, #ccc)",
            borderRadius: "6px",
            padding: "1rem",
            backgroundColor: "var(--jq-bg-subtle, #fafafa)",
          }}
        >
          <h5 style={{ marginTop: 0 }}>
            {task.workstation} Task (Task #{task.task_id}) - Assembly Joined
          </h5>
          <TaskOpsPanel task={task} />
          {incomingRemakesCard}
          <div style={{ marginTop: "0.5rem" }}>
            <strong>Assembly:</strong> {assemblyData.assembly_name} (Part ID #
            {assemblyData.primary_part_id})
            <br />
            {isAssemblySetRow(task) ? (
              // A SET task's quantity is ALREADY an assembly count — the scheduler
              // inserts `assemblies_for_this_set`. Dividing by ppq a second time is what
              // printed "Assemblies Ordered: 0 | Remaining: 0" on job 9403's weld sets.
              <>
                <strong>This set:</strong> SET {task.set_identifier}
                {/* set_total counts the SETS, which is not the job's assembly count:
                    job 9420 is 42 assemblies cut into 9 sets, and reading assembly_count
                    here said "SET 1 of 42". Omitted entirely when the API did not send
                    it — a wrong total is worse than none. */}
                {task.set_total ? ` of ${task.set_total}` : ""}
                {" "}&nbsp;|&nbsp; <strong>Assemblies in this set:</strong> {task.quantity}
                {" "}&nbsp;|&nbsp; <strong>Remaining:</strong> {task.remaining}
              </>
            ) : (
              <>
                <strong>Assemblies Ordered:</strong>{" "}
                {assemblyCountFromPrimarySpace(originalQty, assemblyData.primary_part_quantity)} &nbsp;|&nbsp;
                <strong>Remaining:</strong>{" "}
                {assemblyCountFromPrimarySpace(task.remaining, assemblyData.primary_part_quantity)}
              </>
            )}
            {(assemblyBom?.finished_weight_lbs || assemblyBom?.document_name) && (
              <div style={{ marginTop: "0.35rem", fontSize: "0.9rem" }}>
                {assemblyBom?.finished_weight_lbs ? (
                  <span><strong>Finished weight:</strong> {assemblyBom.finished_weight_lbs} lbs each</span>
                ) : null}
                {assemblyBom?.finished_weight_lbs && assemblyBom?.document_name ? " · " : ""}
                {assemblyBom?.document_name ? (
                  <span><strong>Drawing:</strong> {assemblyBom.document_name}</span>
                ) : null}
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: "1rem",
              border: "1px solid var(--jq-border-medium, #ccc)",
              borderRadius: "6px",
              padding: "0.5rem",
              backgroundColor: "var(--jq-bg-card, #fff)",
            }}
          >
            <h6 style={{ margin: 0 }}>Sub‐Components</h6>
            {assemblyData.components && assemblyData.components.length > 0 ? (
              <table
                className="table table-sm table-bordered"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  border: "1px solid var(--jq-border-medium, #ccc)",
                  marginTop: "0.5rem",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "var(--jq-bg-surface, #f9f9f9)" }}>
                    <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Product ID</th>
                    <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Description</th>
                    <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Qty/Assembly</th>
                    <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }} title={thisTaskColTitle}>{thisTaskColLabel}</th>
                    <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }} title="Pieces the WHOLE job needs — every set together. Use it for staging, not for the build in front of you.">This Job</th>
                    <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Status</th>
                    <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Image</th>
                    <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Flag</th>
                    <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Defect</th>
                  </tr>
                </thead>
                <tbody>
                  {assemblyData.components.map((comp) => (
                    <tr key={comp.assembly_component_id}>
                      <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                        {comp.flagged && (
                          <span style={{ color: "red", marginRight: "4px" }}>🚩</span>
                        )}
                        {comp.product_id || comp.part_id}
                      </td>
                      <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                        {comp.description || ""}
                      </td>
                      <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                        {comp.quantity_per_assembly}
                      </td>
                      {/* The operator's own number: what THIS task consumes. On a split
                          build that is one set, not the job. Bold because it is the count
                          they collect; the job column beside it is context. */}
                      <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                        <strong>{taskPiecesForComponent(task, assemblyData.primary_part_quantity, comp.quantity_per_assembly)}</strong>
                      </td>
                      {/* Job totals + readiness come from the SAME BOM payload the printed
                          report uses, so screen and paper cannot disagree. Blank when the
                          BOM call did not return (the catalog view still renders). */}
                      <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }} className="text-muted">
                        {bomRowFor(comp)?.job_qty ?? ""}
                      </td>
                      <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem", whiteSpace: "nowrap" }}>
                        {bomRowFor(comp)?.status ? (
                          <span
                            className={`badge ${bomRowFor(comp).ready ? "bg-success" : "bg-danger"}`}
                            title={bomRowFor(comp).ready
                              ? "Upstream production complete"
                              : "Not ready — this is what holds the build up"}
                          >
                            {bomRowFor(comp).status}
                          </span>
                        ) : ""}
                      </td>
                      <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                        {comp.image_path ? (
                          <img
                            src={comp.image_path}
                            alt="Component"
                            style={{ maxWidth: "120px", cursor: "pointer" }}
                            onClick={() => onEnlarge(comp.image_path)}
                          />
                        ) : (
                          "No image"
                        )}
                      </td>
                      <td
                        style={{
                          border: "1px solid var(--jq-border-medium, #ccc)",
                          padding: "0.5rem",
                          textAlign: "center",
                        }}
                      >
                        <button
                          onClick={() => handleTogglePartFlag(comp)}
                          disabled={isSubmitting}
                          style={{
                            backgroundColor: comp.flagged ? "#dc3545" : "#007bff",
                            color: "#fff",
                            border: "none",
                            borderRadius: "4px",
                            padding: "0.25rem 0.5rem",
                            cursor: "pointer",
                            fontSize: "0.75rem",
                          }}
                        >
                          {comp.flagged ? "Unflag" : "Flag"}
                        </button>
                      </td>
                      <td
                        style={{
                          border: "1px solid var(--jq-border-medium, #ccc)",
                          padding: "0.5rem",
                          textAlign: "center",
                        }}
                      >
                        <button
                          onClick={() =>
                            handleDefectPart({
                              product_id: comp.product_id || `SubComp#${comp.part_id}`,
                              part_id: comp.part_id,
                            })
                          }
                          disabled={isSubmitting}
                          style={{
                            backgroundColor: "#d9534f",
                            color: "#fff",
                            border: "none",
                            borderRadius: "4px",
                            padding: "0.25rem 0.5rem",
                            cursor: "pointer",
                            fontSize: "0.75rem",
                          }}
                        >
                          Defect
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ marginTop: "0.5rem" }}>
                (No sub‐components found for this assembly.)
              </div>
            )}
          </div>

          <div style={{ marginTop: '1rem', borderTop: '2px solid #007bff', paddingTop: '1rem' }}>
            <h6 style={{ margin: 0 }}>Additional Components (Consumables)</h6>
            {assemblyConsumables && assemblyConsumables.length > 0 ? (
              <table className="table table-sm table-bordered" style={{ marginTop: '0.5rem' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--jq-bg-subtle, #f0f0f0)' }}>
                    <th>Component ID</th>
                    <th>Description</th>
                    <th>Qty/Assembly</th>
                    <th title={thisTaskColTitle}>{thisTaskColLabel}</th>
                    <th title="Pieces the WHOLE job needs — every set together.">This Job</th>
                    <th>Defect</th>
                  </tr>
                </thead>
                <tbody>
                  {assemblyConsumables.map((comp) => {
                    {/* Job total from the same job-scoped BOM the printed report
                        uses; falls back to qty/assembly × assemblies on the task
                        (task qty ÷ primary_part_quantity) when the BOM call
                        did not return. */}
                    const jobQty = consumableJobQty({
                      bomRow: bomRowFor(comp),
                      quantityPerAssembly: comp.quantity_per_assembly,
                      taskQuantity: task.quantity,
                      primaryPartQuantity: assemblyData?.primary_part_quantity,
                    })
                    return (
                      <tr key={comp.assembly_consumable_id}>
                        <td>{comp.product_id || 'N/A'}</td>
                        <td>{comp.description || 'N/A'}</td>
                        <td>{comp.quantity_per_assembly}</td>
                        <td><strong>{taskPiecesForComponent(task, assemblyData?.primary_part_quantity, comp.quantity_per_assembly)}</strong></td>
                        <td className="text-muted">{jobQty ?? ''}</td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            onClick={() => handleDefectConsumable(comp)}
                            disabled={isSubmitting}
                            title="Record destroyed / scrapped pieces of this component — they are taken out of consumable stock"
                            style={{
                              backgroundColor: "#d9534f",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              padding: "0.25rem 0.5rem",
                              cursor: "pointer",
                              fontSize: "0.75rem",
                            }}
                          >
                            Defect
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{ marginTop: '0.5rem' }}>(No additional components required.)</div>
            )}
          </div>

          {/* Assembly Image */}
          {assemblyData && assemblyData.image_path && (
            <div style={{ marginTop: '1rem', borderTop: '2px solid #28a745', paddingTop: '1rem' }}>
              <h6 style={{ margin: 0 }}>Assembly Image</h6>
              <img src={assemblyData.image_path} alt="Assembly" className="dark-invertible" style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain', marginTop: '0.5rem', cursor: 'pointer', border: '1px solid var(--jq-border-medium, #dee2e6)' }} onClick={() => onEnlarge(assemblyData.image_path)} />
            </div>
          )}

          {/* **MODIFICATION START**: Add Assembly Documents section */}
          {assemblyData && assemblyData.documents && assemblyData.documents.length > 0 && (
            <div style={{ marginTop: '1rem', borderTop: '2px solid #28a745', paddingTop: '1rem' }}>
              <h6 style={{ margin: 0 }}>Assembly Documents</h6>
              <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                {assemblyData.documents.map(doc => (
                  <li key={doc.assembly_document_id} style={{ marginBottom: '0.5rem' }}>
                    <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                      {doc.original_filename || 'View Document'}
                    </a>
                    {doc.notes && <em style={{ marginLeft: '10px', color: '#6c757d' }}> - {doc.notes}</em>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* **MODIFICATION END** */}


          {showPartFlagModal && selectedPartToFlag && (
            <PartFlagModal
              isOpen={showPartFlagModal}
              onClose={() => setShowPartFlagModal(false)}
              onSave={({ reason, userName }) => {
                setShowPartFlagModal(false)
                saveFlag(selectedPartToFlag, reason, userName)
              }}
            />
          )}
        </div>
      )
    }
  }

  // Normal single-part detail (not an assembly)
  return (
    <div
      style={{
        marginTop: "1rem",
        border: "1px solid var(--jq-border-medium, #ccc)",
        borderRadius: "6px",
        padding: "1rem",
        backgroundColor: "var(--jq-bg-subtle, #fafafa)",
      }}
    >
      <h5 style={{ marginTop: 0 }}>
        {task.workstation} Task (Task #{task.task_id})
        {task.workstation === 'Fabrication' && task.sub_process && ` - ${task.sub_process}`}
        
        {/* Set Indicator in Detail Header */}
        {task.set_identifier && (
            <span style={{ 
                marginLeft: '10px', 
                color: '#fd7e14', 
                border: '1px solid #fd7e14', 
                borderRadius: '4px', 
                padding: '1px 6px',
                fontSize: '0.8em'
            }}>
                SET #{task.set_identifier}
            </span>
        )}
      </h5>
        <TaskOpsPanel task={task} />

      {task.workstation === 'Blast' && (
        <div style={{ marginBottom: "1rem" }}>
            <button
                onClick={handleToggleMachines}
                disabled={isSubmitting}
                className={`btn btn-sm ${machineCount === 2 ? 'btn-warning' : 'btn-outline-secondary'}`}
                style={{ fontWeight: "bold" }}
            >
                {machineCount === 2 ? "⚡ 2 Machines Active (2x Speed)" : "⚙️ 1 Machine Active"}
            </button>
        </div>
      )}

      {incomingRemakesCard}

      {/* 2. <<<<< YOUR NEW SIBLING TASK TABLE GOES HERE >>>>> */}
      {task.workstation === 'Fabrication' && siblingFabTasks.length > 0 && (
        <div style={{ marginTop: '1rem', border: '1px solid var(--jq-border-medium, #ddd)', borderRadius: '4px', padding: '0.5rem', backgroundColor: 'var(--jq-bg-card, #fff)' }}>
          <h6>Fabrication Sub-Processes Status</h6>
          <table className="table table-sm table-bordered">
            <thead>
              <tr>
                <th>Sub-Process</th>
                <th>Status</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {siblingFabTasks.map(sibling => (
                <tr key={sibling.task_id} style={{ backgroundColor: sibling.task_id === task.task_id ? 'var(--jq-job-selected, #e9f5ff)' : 'transparent' }}>
                  <td>{sibling.sub_process}</td>
                  <td>{statusBadge(sibling.status)}</td>
                  <td>{formatInterval(sibling.elapsed_time)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginTop: "1rem" }}>
        <table
          className="table table-sm table-bordered"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid var(--jq-border-medium, #ccc)",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "var(--jq-bg-surface, #f9f9f9)" }}>
              <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Product ID</th>
              <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Description</th>
              
              {/* --- Blast Headers --- */}
              {isBlastStation && (
                <>
                  <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem", color: "#d63384" }}>Blast Top</th>
                  <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem", color: "#d63384" }}>Blast Bot</th>
                </>
              )}
              {/* --------------------- */}
              {isCoatingStation && (
                <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Coat Color</th>
              )}

              <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Original Order</th>
              <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                Task Remaining (After Defects)
              </th>
              <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Image</th>
              <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Flag</th>
              <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Defect</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                {relevantPart.flagged && (
                  <span style={{ color: "red", marginRight: "4px" }}>🚩</span>
                )}
                {relevantPart.product_id}
              </td>
              <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                {relevantPart.description}
              </td>

              {/* --- Blast Data --- */}
              {isBlastStation && (
                <>
                  <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem", fontWeight: "bold", color: "#d63384" }}>
                    {relevantPart.blast_time_top || "--:--"}
                  </td>
                  <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem", fontWeight: "bold", color: "#d63384" }}>
                    {relevantPart.blast_time_bottom || "--:--"}
                  </td>
                </>
              )}
              {/* ------------------ */}
              {isCoatingStation && (
                <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem", fontWeight: "bold" }}>
                  {relevantPart.coating_color_code ? (
                    <CoatingSwatch
                      code={relevantPart.coating_color_code}
                      name={relevantPart.coating_color_name}
                      hex={relevantPart.coating_color_hex}
                    />
                  ) : (
                    <span style={{ color: "#d63384" }}>not set</span>
                  )}
                </td>
              )}

              <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>{originalQty}</td>
              <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                {task.remaining !== undefined ? task.remaining : "--"}
              </td>
              <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                {relevantPart.image_path ? (
                  <img
                    src={relevantPart.image_path}
                    alt="Part"
                    style={{ maxWidth: "120px", cursor: "pointer" }}
                    onClick={() => onEnlarge(relevantPart.image_path)}
                  />
                ) : (
                  "No image"
                )}
              </td>
              <td
                style={{
                  border: "1px solid var(--jq-border-medium, #ccc)",
                  padding: "0.5rem",
                  textAlign: "center",
                }}
              >
                <button
                  onClick={() => handleTogglePartFlag(relevantPart)}
                  style={{
                    backgroundColor: relevantPart.flagged ? "#dc3545" : "#007bff",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    padding: "0.25rem 0.5rem",
                    cursor: "pointer",
                    fontSize: "0.75rem",
                  }}
                >
                  {relevantPart.flagged ? "Unflag" : "Flag"}
                </button>
              </td>
              <td
                style={{
                  border: "1px solid var(--jq-border-medium, #ccc)",
                  padding: "0.5rem",
                  textAlign: "center",
                }}
              >
                <button
                  onClick={() => handleDefectPart(relevantPart)}
                  disabled={isSubmitting}
                  style={{
                    backgroundColor: "#d9534f",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    padding: "0.25rem 0.5rem",
                    cursor: "pointer",
                    fontSize: "0.75rem",
                  }}
                >
                  Defect
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {showPartFlagModal && selectedPartToFlag && (
        <PartFlagModal
          isOpen={showPartFlagModal}
          onClose={() => setShowPartFlagModal(false)}
          onSave={({ reason, userName }) => {
            setShowPartFlagModal(false)
            saveFlag(selectedPartToFlag, reason, userName)
          }}
        />
      )}
    </div>
  )
}

/**
 * Modal for enlarging images
 */
function EnlargeModal({ url, onClose }) {
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--jq-bg-card, #fff)",
          padding: "1rem",
          maxWidth: "90vw",
          maxHeight: "90vh",
          position: "relative",
          borderRadius: "6px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "0.5rem",
            right: "0.5rem",
            backgroundColor: "#d9534f",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            padding: "0.25rem 0.5rem",
            cursor: "pointer",
            fontSize: "0.85rem",
          }}
        >
          X
        </button>
        <img src={url} alt="Enlarged" style={{ maxWidth: "100%", maxHeight: "80vh" }} />
      </div>
    </div>
  )
}

/**
 * PartFlagModal - for entering reason & name to flag a part.
 */
function PartFlagModal({ isOpen, onClose, onSave, isSubmitting }) {
  const [reason, setReason] = useState("")
  const [userName, setUserName] = useState("")

  if (!isOpen) return null

  function handleSave() {
    if (!reason.trim() || !userName.trim()) {
      alert("Please enter both a reason and your name.")
      return
    }
    onSave({ reason, userName })
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "var(--jq-bg-card, #fff)",
          padding: "1rem",
          borderRadius: "6px",
          width: "400px",
          maxWidth: "90%",
        }}
      >
        <h4>Flag This Part</h4>
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", marginBottom: "0.25rem" }}>Reason:</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ width: "100%", height: "60px" }}
          />
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", marginBottom: "0.25rem" }}>Your Name:</label>
          <input
            type="text"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>
        <div style={{ textAlign: "right" }}>
          <button onClick={onClose} style={{ marginRight: "0.5rem" }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={isSubmitting} style={{ background: "#28a745", color: "#fff" }}>
            Save Flag
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * FlagModal - used specifically for Cut Sheet in LaserDetailSection.
 */
function FlagModal({ isOpen, onClose, onSave, isSubmitting }) {
  const [reason, setReason] = useState("")
  const [userName, setUserName] = useState("")

  if (!isOpen) return null

  function handleSave() {
    if (!reason.trim() || !userName.trim()) {
      alert("Please enter both a reason and your name.")
      return
    }
    onSave({ reason, userName })
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "var(--jq-bg-card, #fff)",
          padding: "1rem",
          borderRadius: "6px",
          width: "400px",
          maxWidth: "90%",
        }}
      >
        <h4>Flag This Cut Sheet</h4>
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", marginBottom: "0.25rem" }}>Reason:</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ width: "100%", height: "60px" }}
          />
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", marginBottom: "0.25rem" }}>Your Name:</label>
          <input
            type="text"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>
        <div style={{ textAlign: "right" }}>
          <button onClick={onClose} style={{ marginRight: "0.5rem" }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={isSubmitting} style={{ background: "#28a745", color: "#fff" }}>
            Save Flag
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * LaserConsumablesPanel: Displays live life stats for Gas, Lens, and Nozzle.
 * Updated to match MobileJobQueue styling and fit StationStatusPanel width.
 */
function LaserConsumablesPanel() {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);

  // Declared as a `const` arrow function, so it must appear BEFORE the
  // useEffect that calls it — otherwise we rely on the implicit ordering
  // of effect callbacks running after the function body, which is correct
  // today but fragile (a fast-refresh edit or a future ESLint rule could
  // turn it into a TDZ ReferenceError).
  const fetchData = async () => {
    const data = await loadLaserConsumables();
    if (data) setMetrics(data);
    setLoading(false);
  };

  // Fetch on mount and every 60 seconds
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div style={{fontSize: '0.8rem', color: '#888', textAlign: 'center'}}>Loading Consumables...</div>;

  return (
    <div style={{
      display: 'grid',
      // Creates a 2-column grid to match the mobile "col-6" layout
      gridTemplateColumns: 'repeat(2, 1fr)', 
      gap: '0.5rem',
      width: '100%' // Stretches to fill the parent container width
    }}>
      {metrics.map((item, idx) => {
        // Normalize field names (API may return "current"/"avg" instead of "current_life"/"avg_life" for unknown consumables)
        const currentLife = item.current_life ?? item.current ?? 0;
        const avgLife = item.avg_life ?? item.avg ?? 0;
        // Warning logic matching mobile
        const isWarning = item.status === "Warning" || (avgLife > 0 && currentLife >= avgLife * 0.9);
        
        // Mobile Styles Translation:
        // bg-warning (#ffc107) vs bg-light (#f8f9fa)
        const bgColor = isWarning ? '#ffc107' : 'var(--jq-bg-card, #f8f9fa)';
        // border-warning vs border-secondary (approx #dee2e6)
        const borderColor = isWarning ? '#ffc107' : 'var(--jq-border-medium, #dee2e6)';
        const textColor = isWarning ? '#212529' : 'var(--jq-text-primary, #212529)';

        return (
          <div key={idx} style={{
            backgroundColor: bgColor,
            color: textColor,
            border: `1px solid ${borderColor}`,
            borderRadius: '6px', // Rounded corners
            padding: '0.5rem',   // p-2
            textAlign: 'center', // text-center
            boxShadow: '0 .125rem .25rem rgba(0,0,0,.075)', // shadow-sm
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minWidth: '0' // Prevents flex child overflow
          }}>
            <small style={{ 
              display: 'block', 
              fontWeight: 'bold', 
              textTransform: 'uppercase', 
              fontSize: '0.7rem', 
              opacity: 0.85,
              marginBottom: '2px'
            }}>
              {item.label}
            </small>
            <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
              {currentLife.toFixed(1)}h <span style={{opacity: 0.6, fontWeight: 'normal'}}>/ {avgLife > 0 ? avgLife.toFixed(1) : '-'}h</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StationStatusPanel({ userTime, stationDailyState }) {
  const totalUserSeconds = userTime?.reduce((sum, u) => sum + u.total_seconds, 0) || 0;
  const totalFaultSeconds = userTime?.reduce((sum, u) => sum + (u.fault_seconds || 0), 0) || 0;

  return (
    <div
      style={{
        display: "flex",
        gap: "1rem",
        backgroundColor: "var(--jq-bg-subtle, #f7f7f7)",
        border: "1px solid var(--jq-border-medium, #ddd)",
        borderRadius: "6px",
        padding: "0.5rem 1rem",
      }}
    >
      {/* Left box: user times */}
      <div>
        <strong>On Task Today</strong>
        {(!userTime || userTime.length === 0) && <div>No users yet.</div>}
        {userTime &&
          userTime.map((u) => (
            <div key={u.user_id}>
              {u.user_name}: {formatSecondsToHhMm(u.total_seconds)}
              {u.fault_seconds > 0 && (
                <span style={{ color: "#FF6B6B", marginLeft: "0.4rem" }}>
                  (+{formatSecondsToHhMm(u.fault_seconds)} fault)
                </span>
              )}
              {u.active ? " (active)" : ""}
            </div>
          ))}
        {userTime && userTime.length > 0 && (
          <div style={{ marginTop: "0.5rem", fontWeight: "bold" }}>
            Total: {formatSecondsToHhMm(totalUserSeconds)}
            {totalFaultSeconds > 0 && (
              <span style={{ color: "#FF6B6B", fontWeight: "normal", marginLeft: "0.4rem" }}>
                (+{formatSecondsToHhMm(totalFaultSeconds)} fault)
              </span>
            )}
          </div>
        )}
      </div>

      {/* Right box: station states */}
      <div>
        <strong>Station States (Shift)</strong>
        <table style={{ marginTop: "0.25rem", borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td style={{ padding: "0.2rem 0.5rem" }}>In Work:</td>
              <td style={{ padding: "0.2rem 0.5rem", fontWeight: "bold" }}>
                {formatSecondsToHhMm(stationDailyState.in_work_seconds)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "0.2rem 0.5rem" }}>Waiting:</td>
              <td style={{ padding: "0.2rem 0.5rem", fontWeight: "bold" }}>
                {formatSecondsToHhMm(stationDailyState.waiting_seconds)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "0.2rem 0.5rem" }}>Maintenance:</td>
              <td style={{ padding: "0.2rem 0.5rem", fontWeight: "bold" }}>
                {formatSecondsToHhMm(stationDailyState.maintenance_seconds)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "0.2rem 0.5rem" }}>Idle:</td>
              <td style={{ padding: "0.2rem 0.5rem", fontWeight: "bold" }}>
                {formatSecondsToHhMm(stationDailyState.idle_seconds)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}


/**
 * ConsumablesDrawer
 */
function ConsumablesDrawer({ workstation }) {
  const [consumables, setConsumables] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadConsumables()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workstation])

  async function loadConsumables() {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${API_BASE}/consumables?category=${encodeURIComponent(workstation)}`)
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      let data = await res.json()

      // --- FILTER: HIDE AUTOMATED ITEMS FOR LASER ---
      // We hide Nozzles, Nitrogen, and Oxygen because the new "Swap" buttons 
      // handle the logic/deduction automatically.
      if (workstation === "Laser") {
        const termsToExclude = ["nozzle", "nitrogen", "oxygen"];
        
        data = data.filter(c => {
          const desc = (c.description || "").toLowerCase();
          const pid = (c.product_id || "").toLowerCase();
          
          // Check if any excluded term is present in description or product ID
          const hasExcludedTerm = termsToExclude.some(term => 
            desc.includes(term) || pid.includes(term)
          );
          
          return !hasExcludedTerm;
        });
      }

      setConsumables(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }


  async function promptForPin() {
    return await pinPrompt("Enter your 4-digit PIN:");
  }

  async function handleUse(consumable) {
    const usageStr = prompt(`How many of '${consumable.product_id}' do you want to use?`, "1")
    if (!usageStr) return
    const usage = parseInt(usageStr, 10) || 0
    if (usage < 1) return

    const reason = prompt("Notes / reason for usage?", "")
    const pin = await promptForPin()
    if (!pin) return

    const payload = {
      delta: -usage,
      pin,
      reason,
    }
    try {
      const res = await fetch(
        `${API_BASE}/consumables/${consumable.consumable_id}/transactions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      )
      if (!res.ok) {
        const msg = await parseServerError(res)
        alert(msg)
        return
      }
      alert(`Used ${usage} of '${consumable.product_id}'.`)
      loadConsumables()
    } catch (err) {
      alert(err.message)
    }
  }

  return (
    <div
      style={{
        border: "1px solid var(--jq-border-medium, #ccc)",
        borderRadius: "6px",
        padding: "1rem",
        backgroundColor: "var(--jq-bg-card, #fefefe)",
      }}
    >
      <h5 style={{ marginTop: 0 }}>Consumables for {workstation}</h5>
      {error && (
        <div
          style={{
            backgroundColor: "var(--jq-alert-danger-bg, #f8d7da)",
            border: "1px solid var(--jq-alert-danger-border, #f5c6cb)",
            padding: "0.5rem",
            borderRadius: "4px",
            color: "#721c24",
          }}
        >
          {error}
        </div>
      )}
      {loading && <div style={{ color: "var(--jq-text-muted, #666)" }}>Loading consumables...</div>}

      {!loading && consumables.length === 0 && (
        <div style={{ color: "var(--jq-text-muted, #666)" }}>(No consumables found for {workstation}.)</div>
      )}

      {consumables.length > 0 && (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginTop: "0.5rem",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "var(--jq-bg-surface, #f9f9f9)" }}>
              <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Product</th>
              <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Description</th>
              <th
                style={{
                  border: "1px solid var(--jq-border-medium, #ccc)",
                  padding: "0.5rem",
                  textAlign: "right",
                }}
              >
                On Hand
              </th>
              <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>Reorder Point</th>
              <th style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}></th>
            </tr>
          </thead>
          <tbody>
            {consumables.map((c) => (
              <tr key={c.consumable_id}>
                <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>{c.product_id}</td>
                <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>{c.description}</td>
                <td
                  style={{
                    border: "1px solid var(--jq-border-medium, #ccc)",
                    padding: "0.5rem",
                    textAlign: "right",
                  }}
                >
                  {c.stock_quantity}
                </td>
                <td style={{ border: "1px solid var(--jq-border-medium, #ccc)", padding: "0.5rem" }}>
                  {c.reorder_point}
                </td>
                <td
                  style={{
                    border: "1px solid var(--jq-border-medium, #ccc)",
                    padding: "0.5rem",
                    textAlign: "center",
                  }}
                >
                  <button
                    onClick={() => handleUse(c)}
                    style={{
                      backgroundColor: "#ffc107",
                      color: "#000",
                      border: "none",
                      borderRadius: "4px",
                      padding: "0.25rem 0.5rem",
                      cursor: "pointer",
                      fontSize: "0.75rem",
                    }}
                  >
                    Use
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}